import * as Logger from './logger.js';
import { Auth } from './auth.js';
import { HSComponent } from './component.js';
import { UserSession } from './session.js';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { escapeHtmlAttribute } from './utils.js';
import './toolbar-button.js';
import './checkbox-toggle.js';
import './ml-models.js';
import './ml-labeling.js';
import { HelpRegistry } from './help-registry.js';
import './help-icon.js';
import './concept-panel.js';
import { ExplainClient } from './explain-client.js';
import './explain-panel.js';
import { getTheme, toggleTheme } from './theme.js';
import styles from './pipeline-sandbox.css';
import template from './pipeline-sandbox.html';
import { HSListPicker } from './list-picker.js';

const LOG_LIMIT_DEFAULT = 100;
const LOG_LIMIT_MIN = 10;
const LOG_LIMIT_MAX = 1000;
// Cap on how many pipelines keep a retained buffer at once (LRU-evicted).
const MAX_RETAINED_LOG_PIPELINES = 10;

const PIPELINE_NAME_RE = /^[A-Za-z0-9](?:[A-Za-z0-9._-]*[A-Za-z0-9])?$/;
const NAMESPACE_RE = /^[A-Za-z](?:[A-Za-z0-9._-]*[A-Za-z0-9])?$/;

// Curated raw external Kafka feeds — mixed-event streams that require recv+decrypt+unzip.
const RAW_FEED_TOPICS = ['homesight-events', 'homesight-metrics', 'user-events'];

// UI trigger category → ELM trigger.type emitted on save.
const UI_TO_ELM_TRIGGER = {
    events: 'events',
    state: 'state',
    pipeline: 'pipeline',
    schedule: 'schedule',
    api: 'api',
    'kafka-events': 'kafka',
    'kafka-raw': 'kafka',
};

// Plugin refs auto-prepended to the pipeline steps when a UI trigger category is selected.
// Only the raw Kafka categories need them: events/state records are parsed, filtered and
// normalized by the framework before step one.
const TRIGGER_AUTO_PLUGINS = {
    'kafka-events': ['infrastructure:kafka.recv', 'core:crypto.decrypt', 'core:compression.unzip'],
    'kafka-raw': ['infrastructure:kafka.recv'],
};

// Human-readable label shown in the pipeline list for each ELM trigger.type.
const ELM_TRIGGER_DISPLAY_LABEL = {
    events: 'events',
    state: 'state',
    pipeline: 'pipeline',
    telemetry: 'pipeline',   // deprecated alias still accepted by ELM
    kafka: 'kafka',
    schedule: 'schedule',
    api: 'api',
};

export class HSPipelineSandbox extends HSComponent {
    #pipelines = [];
    #pipelineError = null;
    #catalog = {};
    #currentView = 'list';
    #selectedPipeline = null;
    // Retained per-pipeline ring buffers, so leaving and re-entering the Logs tab still shows
    // recent activity. Insertion order doubles as the LRU order for eviction.
    #logBuffers = new Map();
    #logLimit = LOG_LIMIT_DEFAULT;
    #logPaused = false;
    #logAutoScroll = true;
    #logPretty = true;
    #logLevelFilters = { error: true, warn: true, info: true, debug: true, verbose: true };
    #sseAbortController = null;
    #editingPipeline = null;
    #formSnapshot = null;          // JSON snapshot of the builder form at open time, used for dirty detection
    #isAdmin = false;
    #readOnly = false;
    #baseUrl = '/elm/api';
    #namespace = null;           // active workspace namespace (persisted)
    #knownNamespaces = new Set(); // union across pipelines / _sandbox / telemetry
    #schemaExplorer = {
        common: null,
        domains: [],
        selectedDomain: null,
        selectedType: null,
        types: [],           // types for the selected domain
        detail: null,        // per-type record for the selected type
        filter: '',
    };
    #schemaEditor = null;    // active editor state: { isNew, domain, messageType, mode, sourceDetail, error }
    #schemaCache = { domainsLoaded: false, typesByDomain: new Map(), typeDetails: new Map() };  // shared with builder
    #telemetry = {
        open: false,
        mode: 'state',           // 'state' | 'journal'
        providerid: '',
        providerLabel: '',       // rendered label for the provider picker button
        entityId: '',
        entityLabel: '',         // rendered label for the entity picker button
        entityType: '',          // 'device' | 'user' | ''
        domain: '',
        messageType: '',
        rangeMs: 15 * 60 * 1000,   // 15m default (journal only)
        rangeLabel: '15m',
        autoRefresh: false,
        rows: [],
        loading: false,
        error: null,
        expanded: new Set(),
        pendingRefresh: null,
    };
    #entityCache = { devices: null, users: null };   // lazy-fetched for the entity picker
    #mlModels = null;    // mounted <hs-ml-models>, created on demand
    #mlLabeling = null;  // mounted <hs-ml-labeling>, created on demand
    #conceptPanel = null;   // mounted <hs-concept-panel>, created on demand (tier-2 help)
    #explainPanel = null;   // mounted <hs-explain-panel>, created on demand (tier-3 explain)
    #concurrencyExplainSubject = null;   // cached GET /explain/subjects entry, or null if unregistered
    #pipelineExplainSubject = null;   // cached GET /explain/subjects entry for the "pipeline" walkthrough, or null if unregistered
    #trackedExplainSubject = null;   // which subject #concurrencyObserver's live recompute should build context for
    #concurrencyObserver = null;   // MutationObserver driving live recompute while the explain panel tracks the builder form
    #topology = {
        graph: null,             // last static graph result
        live: null,              // last live overlay result
        livePoll: null,          // setInterval handle
        selectedEdge: null,      // { domain, message_type }
        channelSSE: null,        // AbortController for live channel stream
        channelBuffer: [],       // buffered events for the inspector
    };
    #bundleTab = 'import';
    #bundleParsed = null;            // decoded bundle object backing the current validate plan
    #bundleExportItems = [];         // [{ type, name, feature }] candidate sandbox artifacts for export
    #bundleExportFeatures = [];      // [{ feature, count }] merged from GET /sandbox/features + local items
    #bundleExportSelected = new Set(); // `${type}:${name}` keys currently checked for export
    #evidenceRuns = null;            // last POST /sandbox/bundles/:bundle_id/run result for the selected pipeline
    #isTuning = false;                // editing an imported (bundle_id) pipeline vs. the scratch builder

    get readOnly() { return this.#readOnly; }
    set readOnly(v) { this.#readOnly = !!v; }

    get baseUrl() { return this.#baseUrl; }
    set baseUrl(v) { this.#baseUrl = v || '/elm/api'; }

    onLoad() {
        this.loadTemplate(template);
        this.loadStyles(styles);
        const usertype = UserSession.get('userinfo')?.usertype;
        this.#isAdmin = !this.#readOnly && ['devops', 'developer', 'super'].includes(usertype);
        this.shadowRoot.getElementById('ps-subscriptions-section')?.classList.toggle('hidden', !this.#isAdmin);
        this.#initNamespace();
        this.#initLogLimit();
        this.#bindEvents();
        HelpRegistry.configure(this.#baseUrl);
        HelpRegistry.ensureLoaded()   // prefetch; help icons also await this themselves
            .then(() => this.#openDeepLinkedConcept());
        ExplainClient.configure(this.#baseUrl);
        this.#refreshExplainAvailability();
        if (this.#readOnly) {
            const sd = this.shadowRoot;
            sd.getElementById('ps-create-btn')?.classList.add('hidden');
            sd.getElementById('ps-ml-models-btn')?.classList.add('hidden');
            sd.getElementById('ps-ml-labeling-btn')?.classList.add('hidden');
            sd.getElementById('ps-plugins-btn')?.classList.add('hidden');
            sd.getElementById('ps-schema-explorer-btn')?.classList.add('hidden');
            sd.getElementById('ps-topology-btn')?.classList.add('hidden');
            sd.getElementById('ps-prepare-pr-btn')?.classList.add('hidden');
            sd.getElementById('ps-bundle-btn')?.classList.add('hidden');
            sd.getElementById('ps-namespace-selector')?.classList.add('hidden');
            sd.querySelector('.ps-tab[data-tab="test"]')?.classList.add('hidden');
        }
        this.#renderNamespaceLabel();
        this.#applyTheme(getTheme());
        this.#showView('list');
        this.#fetchPipelines();
    }

    onUnload() {
        super.onUnload();
        this.#disconnectSSE();
        this.#removeMlModels();
        this.#removeMlLabeling();
        this.#logBuffers.clear();
        this.#concurrencyObserver?.disconnect();
    }

    // ---- Navigation ----

    #showView(view) {
        this.#currentView = view;
        const sd = this.shadowRoot;
        for (const el of sd.querySelectorAll('.ps-view')) {
            el.classList.add('hidden');
        }
        const target = sd.getElementById(`ps-${view}-view`);
        if (target) target.classList.remove('hidden');
    }

    #showMlModelsView() {
        const mount = this.shadowRoot.getElementById('ps-ml-models-view');
        if (!mount) return;
        this.#removeMlModels();
        this.#mlModels = document.createElement('hs-ml-models');
        this.#mlModels.baseUrl = `${this.#baseUrl}/ml`;
        this.onEvent('hs-close-ml-models', () => this.#showView('list'), this.#mlModels);
        mount.appendChild(this.#mlModels);
        this.#showView('ml-models');
    }

    #removeMlModels() {
        if (this.#mlModels) {
            this.#mlModels.remove();
            this.#mlModels = null;
        }
    }

    #showMlLabelingView() {
        const mount = this.shadowRoot.getElementById('ps-ml-labeling-view');
        if (!mount) return;
        this.#removeMlLabeling();
        this.#mlLabeling = document.createElement('hs-ml-labeling');
        this.#mlLabeling.baseUrl = `${this.#baseUrl}/ml`;
        this.onEvent('hs-close-ml-labeling', () => this.#showView('list'), this.#mlLabeling);
        mount.appendChild(this.#mlLabeling);
        this.#showView('ml-labeling');
    }

    #removeMlLabeling() {
        if (this.#mlLabeling) {
            this.#mlLabeling.remove();
            this.#mlLabeling = null;
        }
    }

    async #withLoading(fn) {
        const overlay = this.shadowRoot.getElementById('ps-loading-overlay');
        overlay?.classList.remove('hidden');
        try {
            return await fn();
        } finally {
            overlay?.classList.add('hidden');
        }
    }

    // ---- Event Binding ----

    #bindEvents() {
        const sd = this.shadowRoot;

        this.onEvent('click', () => this.#close(), sd.querySelector('.ps-backdrop'));
        this.onEvent('click', () => this.#close(), sd.getElementById('ps-close-btn'));
        this.onEvent('click', () => this.#applyTheme(toggleTheme()), sd.getElementById('ps-theme-toggle-btn'));
        this.onEvent('click', () => this.#toggleConceptPanel(), sd.getElementById('ps-concepts-btn'));
        this.onEvent('click', () => this.#openPipelineWalkthrough(), sd.getElementById('ps-explain-pipeline-btn'));
        this.onEvent('click', () => this.#openConcurrencyExplain(), sd.getElementById('ps-explain-concurrency-btn'));
        this.onEvent('click', () => this.#openDetailPipelineWalkthrough(), sd.getElementById('ps-detail-explain-pipeline-btn'));
        this.onEvent('click', () => this.#openDetailConcurrencyExplain(), sd.getElementById('ps-detail-explain-concurrency-btn'));

        // List view
        this.onEvent('click', () => this.#showFormView(), sd.getElementById('ps-create-btn'));
        this.onEvent('click', () => this.#showMlModelsView(), sd.getElementById('ps-ml-models-btn'));
        this.onEvent('click', () => this.#showMlLabelingView(), sd.getElementById('ps-ml-labeling-btn'));
        this.onEvent('click', () => this.#showPluginView(), sd.getElementById('ps-plugins-btn'));
        this.onEvent('click', () => this.#showTopologyView(), sd.getElementById('ps-topology-btn'));
        this.onEvent('click', () => this.#fetchPipelines(), sd.getElementById('ps-refresh-btn'));

        // Detail view
        this.onEvent('click', () => this.#backToList(), sd.getElementById('ps-back-btn'));

        // Detail tabs
        for (const tab of sd.querySelectorAll('.ps-tab[data-tab]')) {
            this.onEvent('click', () => this.#switchTab(tab.dataset.tab), tab);
        }

        // Log controls
        this.onEvent('click', () => this.#toggleLogPause(), sd.getElementById('ps-log-pause-btn'));
        this.onEvent('click', () => this.#clearLogs(), sd.getElementById('ps-log-clear-btn'));
        this.onEvent('click', () => this.#toggleLogPretty(), sd.getElementById('ps-log-pretty-btn'));
        this.onEvent('change', (_, ev) => this.#setLogLimit(ev.target.value), sd.getElementById('ps-log-limit'));

        // Telemetry drawer (in Logs tab)
        this.onEvent('click', () => this.#toggleTelemetryDrawer(), sd.getElementById('ps-telemetry-drawer-toggle'));
        this.onEvent('click', () => this.#toggleTelemetryDrawer(false), sd.getElementById('ps-telemetry-drawer-close'));
        for (const btn of sd.querySelectorAll('.ps-tel-mode-btn')) {
            this.onEvent('click', () => this.#setTelemetryMode(btn.dataset.mode), btn);
        }
        this.onEvent('click', () => this.#fetchTelemetryRows(), sd.getElementById('ps-tel-refresh'));
        this.onEvent('change', (_, ev) => { this.#telemetry.autoRefresh = ev.target.checked; }, sd.getElementById('ps-tel-auto'));

        // Log-tab inline test runner
        this.onEvent('click', () => this.#runLogTest(), sd.getElementById('ps-log-test-run'));

        for (const cb of sd.querySelectorAll('hs-checkbox-toggle[data-level]')) {
            this.onEvent('change', (detail) => {
                this.#logLevelFilters[cb.dataset.level] = !!detail.checked;
                this.#rerenderLogs();
            }, cb);
        }

        const logOutput = sd.getElementById('ps-log-output');
        this.onEvent('scroll', () => {
            const atBottom = logOutput.scrollHeight - logOutput.scrollTop - logOutput.clientHeight < 40;
            this.#logAutoScroll = atBottom;
        }, logOutput);

        // Test panel
        this.onEvent('click', () => this.#runTest(), sd.getElementById('ps-test-run-btn'));
        this.onEvent('click', () => this.#saveTestCase(), sd.getElementById('ps-test-save-btn'));
        this.onEvent('click', () => this.#deleteTestCase(), sd.getElementById('ps-test-delete-btn'));
        this.onEvent('click', () => this.#openTestCasePicker({ includeCustom: true, target: 'test' }), sd.getElementById('ps-test-case-select'));
        this.onEvent('click', () => this.#openTestCasePicker({ includeCustom: false, target: 'log' }), sd.getElementById('ps-log-test-select'));
        this.#bindTestDropZone();

        // Builder form
        this.onEvent('click', async () => {
            if (!await this.#confirmDiscardIfDirty()) return;
            this.#formSnapshot = null;
            this.#backToList();
        }, sd.getElementById('ps-form-back-btn'));
        this.onEvent('click', () => this.#savePipeline(), sd.getElementById('ps-form-save-btn'));
        this.onEvent('click', () => {
            const adv = sd.getElementById('ps-form-advanced');
            const btn = sd.getElementById('ps-form-advanced-toggle');
            const showing = adv.classList.toggle('hidden');
            btn.textContent = showing ? 'Show advanced options' : 'Hide advanced options';
        }, sd.getElementById('ps-form-advanced-toggle'));

        for (const radio of sd.querySelectorAll('input[name="ps-trigger-type"]')) {
            this.onEvent('change', () => {
                this.#renderTriggerFields(radio.value);
                this.#ensureTriggerAutoPlugins(radio.value);
            }, radio);
        }

        this.onEvent('click', () => {
            const ref = sd.getElementById('ps-add-step-select')?.value;
            if (ref) this.#addPipelineStep(ref);
        }, sd.getElementById('ps-add-step-btn'));

        // Plugin view
        this.onEvent('click', () => this.#showView('list'), sd.getElementById('ps-plugin-back-btn'));
        this.onEvent('click', () => this.#fetchCatalogForPluginView(), sd.getElementById('ps-plugin-refresh-btn'));
        this.#bindPluginDropZone();

        // Topic subscriptions (developer-admin only, see #isAdmin gate in onLoad)
        this.onEvent('click', () => {
            const detail = sd.getElementById('ps-subscriptions-detail');
            const toggle = sd.getElementById('ps-subscriptions-toggle');
            detail?.classList.toggle('hidden');
            toggle?.classList.toggle('expanded');
        }, sd.getElementById('ps-subscriptions-toggle'));
        this.onEvent('click', () => this.#pruneSubscriptions(), sd.getElementById('ps-subscriptions-prune-btn'));

        // Namespace selector (header)
        this.onEvent('click', (_, ev) => this.#openNamespacePicker(ev.currentTarget), sd.getElementById('ps-namespace-btn'));

        // Schema Explorer (v2)
        this.onEvent('click', () => this.#showSchemaExplorer(), sd.getElementById('ps-schema-explorer-btn'));
        this.onEvent('click', () => this.#showView('list'), sd.getElementById('ps-schema-explorer-back-btn'));
        this.onEvent('click', () => this.#refreshSchemaExplorer(), sd.getElementById('ps-schema-explorer-refresh-btn'));
        this.onEvent('click', () => this.#openExplorerEditor('new'), sd.getElementById('ps-schema-explorer-new-btn'));
        this.onEvent('click', () => this.#openOrphansModal(), sd.getElementById('ps-schema-explorer-orphans-btn'));
        this.onEvent('input', (_, ev) => {
            this.#schemaExplorer.filter = String(ev.target.value || '').toLowerCase();
            this.#renderExplorerTypes();
        }, sd.getElementById('ps-schema-explorer-filter'));

        // Topology view
        this.onEvent('click', () => this.#leaveTopologyView(), sd.getElementById('ps-topology-back-btn'));
        this.onEvent('click', () => this.#loadTopology(), sd.getElementById('ps-topology-refresh-btn'));
        this.onEvent('change', (_, ev) => this.#toggleTopologyLivePoll(ev.target.checked), sd.getElementById('ps-topology-live-toggle'));

        // Prepare PR view
        this.onEvent('click', () => this.#showPreparePrView(), sd.getElementById('ps-prepare-pr-btn'));
        this.onEvent('click', () => this.#showView('list'), sd.getElementById('ps-prepare-pr-back-btn'));
        this.onEvent('click', () => this.#loadPreparePr(), sd.getElementById('ps-prepare-pr-refresh-btn'));

        // Feature Bundle view
        this.onEvent('click', () => this.#showBundleView(), sd.getElementById('ps-bundle-btn'));
        this.onEvent('click', () => this.#showView('list'), sd.getElementById('ps-bundle-back-btn'));
        this.onEvent('click', () => this.#copyCapabilityIndex(), sd.getElementById('ps-bundle-capabilities-btn'));
        for (const t of sd.querySelectorAll('.ps-bundle-tab[data-bundle-tab]')) {
            this.onEvent('click', () => this.#switchBundleTab(t.dataset.bundleTab), t);
        }
        this.#bindBundleDropZone();
        this.onEvent('input', () => {
            this.#bundleParsed = null;
            sd.getElementById('ps-bundle-apply-btn').disabled = true;
            sd.getElementById('ps-bundle-plan-result').innerHTML = '';
        }, sd.getElementById('ps-bundle-import-text'));
        this.onEvent('click', () => this.#validateBundle(), sd.getElementById('ps-bundle-validate-btn'));
        this.onEvent('click', () => this.#importBundle(), sd.getElementById('ps-bundle-apply-btn'));
        this.onEvent('click', () => this.#loadExportItems(), sd.getElementById('ps-bundle-export-refresh-btn'));
        this.onEvent('change', () => this.#renderExportItems(), sd.getElementById('ps-bundle-export-feature'));
        this.onEvent('click', () => this.#assignFeatureLabel(), sd.getElementById('ps-bundle-assign-feature-btn'));
        this.onEvent('click', () => this.#buildExport(), sd.getElementById('ps-bundle-export-btn'));
    }

    // ---- Pipeline List ----

    async #fetchPipelines() {
        await this.#withLoading(async () => {
            try {
                const res = await this.fetch(`${this.#baseUrl}/pipelines`);
                this.#pipelines = res?.pipelines || [];
                this.#pipelineError = null;
                this.#renderPipelineTable();
            } catch (err) {
                Logger.error('Failed to fetch pipelines: ', err);
                this.#pipelines = [];
                this.#pipelineError = err;
                this.#renderPipelineTable();
            }
        });
    }

    #renderPipelineTable() {
        const container = this.shadowRoot.getElementById('ps-pipeline-table');
        if (this.#pipelineError) {
            const status = this.#pipelineError.message?.match(/\d{3}/)?.[0] || '';
            const hint = status === '502' ? 'Service may not support the pipeline API.' : '';
            container.innerHTML = `<div class="ps-empty-state" style="color:var(--error-color,#c00)">Failed to connect to pipeline service${status ? ` (${status})` : ''}.${hint ? ' ' + hint : ''}</div>`;
            return;
        }
        if (!this.#pipelines.length) {
            container.innerHTML = '<div class="ps-empty-state">No pipelines configured</div>';
            return;
        }

        const rows = this.#pipelines.map(p => {
            const stateClass = `state-${this.#esc(p.state || 'stopped')}`;
            const actions = this.#getActionsForState(p.state, p.name, p);
            const triggerLabel = ELM_TRIGGER_DISPLAY_LABEL[p.trigger?.type] || p.trigger?.type || '—';
            const pluginCount = Array.isArray(p.plugins) ? p.plugins.length : '—';
            const created = p.created_at ? new Date(p.created_at).toLocaleString() : '—';
            const namespace = p.namespace || '—';
            const superseded = this.#isSupersededPipeline(p);
            const detailOnly = superseded || this.#isIncompletePipeline(p);
            const stateTitle = this.#problemSummary(p);
            const sandboxBadge = superseded ? `<span class="ps-sandbox-badge ${this.#esc(p.sandbox_state)}" title="Created from an older ELM version — ready for cleanup rather than editing">${this.#esc(p.sandbox_state)}</span>` : '';
            return `<tr>
                <td><a href="#" class="ps-pipeline-link" data-name="${this.#esc(p.name)}" data-detail-only="${detailOnly ? '1' : ''}">${this.#esc(p.name)}</a>${sandboxBadge}</td>
                <td>${this.#esc(namespace)}</td>
                <td><span class="ps-state-badge ${stateClass}"${stateTitle ? ` title="${this.#esc(stateTitle)}"` : ''}>${this.#esc(p.state || 'unknown')}</span></td>
                <td>${this.#esc(triggerLabel)}</td>
                <td>${pluginCount}</td>
                <td>${created}</td>
                ${this.#readOnly ? '' : `<td class="ps-row-actions">${actions}</td>`}
            </tr>`;
        }).join('');

        container.innerHTML = `<table class="ps-table">
            <thead><tr><th>Name</th><th>Namespace</th><th>State</th><th>Trigger</th><th>Plugins</th><th>Created</th>${this.#readOnly ? '' : '<th>Actions</th>'}</tr></thead>
            <tbody>${rows}</tbody>
        </table>`;

        for (const link of container.querySelectorAll('.ps-pipeline-link')) {
            this.onEvent('click', (_, ev) => {
                ev.preventDefault();
                // Superseded/archived sandboxes are ready for cleanup, not editing. Incomplete
                // ones must not reach the form either: update() persists the mutated config
                // before restart() fails, so Save would overwrite the only surviving record.
                if (link.dataset.detailOnly) this.#openPipelineDetail(link.dataset.name);
                else this.#openPipelineForEdit(link.dataset.name);
            }, link);
        }

        for (const btn of container.querySelectorAll('[data-action]')) {
            this.onEvent('click', () => {
                this.#handlePipelineAction(btn.dataset.action, btn.dataset.pipeline);
            }, btn);
        }
    }

    #getActionsForState(state, name, pipeline = null) {
        if (this.#readOnly) return '';
        const esc = this.#esc(name);
        const btn = (action, label) =>
            `<hs-toolbar-button data-action="${action}" data-pipeline="${esc}" title="${label}"></hs-toolbar-button>`;
        const canClone = !pipeline?.is_sandbox;

        switch (state) {
            case 'running':
                return btn('stop', 'Stop') + btn('restart', 'Restart') + btn('logs', 'Logs') + (canClone ? btn('clone', 'Clone') : '') + btn('delete', 'Delete');
            case 'stopped':
                return btn('start', 'Start') + btn('restart', 'Restart') + (canClone ? btn('clone', 'Clone') : '') + btn('delete', 'Delete');
            case 'error':
                return btn('restart', 'Restart') + (canClone ? btn('clone', 'Clone') : '') + btn('delete', 'Delete');
            case 'creating':
                return '<span style="font-size:11px;color:#888">creating...</span>';
            case 'incomplete':
            default:
                // Incomplete pipelines never started and cannot be repaired from here — cloning
                // one only propagates the missing artifact, so delete is the sole remedy. Unknown
                // states get the same non-actionable treatment so a new state value cannot offer
                // an action the backend will refuse.
                return btn('delete', 'Delete');
        }
    }

    async #handlePipelineAction(action, name) {
        if (action === 'logs') {
            this.#openPipelineDetail(name, 'logs');
            return;
        }
        if (action === 'clone') {
            const namespace = this.#getEffectiveNamespace();
            if (!namespace) {
                this.#showAlert('Cannot clone pipeline: no active namespace and no authenticated developer username.');
                return;
            }
            await this.#openCloneModal(name, namespace);
            return;
        }
        if (action === 'delete') {
            if (!await this.#confirm(`Delete pipeline "${name}"? This cannot be undone.`)) return;
            await this.#withLoading(async () => {
                let res = null;
                try {
                    res = await this.fetch(`${this.#baseUrl}/pipelines/${encodeURIComponent(name)}`, { method: 'DELETE' });
                } catch (err) {
                    Logger.error('Delete failed: ', err);
                }
                await this.#fetchPipelines();
                if (res && res.orphaned_sandbox) {
                    this.#showOrphanCleanupModal(res.orphaned_sandbox);
                }
            });
            return;
        }

        await this.#withLoading(async () => {
            try {
                await this.fetch(`${this.#baseUrl}/pipelines/${encodeURIComponent(name)}/${action}`, { method: 'POST' });
            } catch (err) {
                Logger.error(`Pipeline ${action} failed: `, err);
            }
            await this.#fetchPipelines();
        });
    }

    // ---- Pipeline Detail ----

    // Fetches the full pipeline object and stores it on #selectedPipeline. Returns
    // true on success, false on failure (already logged) — shared by the detail
    // view and the "click name → edit" shortcut so both use the same fetch.
    async #fetchPipelineDetail(name) {
        try {
            const res = await this.fetch(`${this.#baseUrl}/pipelines/${encodeURIComponent(name)}`);
            if (!res.test_data && res.config?.test_data) res.test_data = res.config.test_data;
            this.#selectedPipeline = res;
            return true;
        } catch (err) {
            Logger.error('Failed to fetch pipeline detail: ', err);
            return false;
        }
    }

    async #openPipelineDetail(name, initialTab = 'logs') {
        await this.#withLoading(async () => {
            if (!await this.#fetchPipelineDetail(name)) return;

            const sd = this.shadowRoot;
            sd.getElementById('ps-detail-name').textContent = this.#selectedPipeline.name;
            this.#evidenceRuns = null;
            this.#renderDetailState();
            this.#renderDetailProblems();
            this.#renderDetailActions();
            this.#renderConfig();
            this.#populateTestCases();
            this.#showView('detail');
            this.#switchTab(initialTab);
        });
    }

    // Clicking a pipeline name in the list opens it directly for editing.
    async #openPipelineForEdit(name) {
        await this.#withLoading(async () => {
            if (!await this.#fetchPipelineDetail(name)) return;
            await this.#showFormView(this.#selectedPipeline);
        });
    }

    #renderDetailState() {
        const badge = this.shadowRoot.getElementById('ps-detail-state');
        const state = this.#selectedPipeline?.state || 'unknown';
        badge.textContent = state;
        badge.className = `ps-state-badge state-${state}`;
        const summary = this.#problemSummary(this.#selectedPipeline);
        if (summary) badge.title = summary;
        else badge.removeAttribute('title');

        const sandboxBadge = this.shadowRoot.getElementById('ps-detail-sandbox-badge');
        const superseded = this.#isSupersededPipeline(this.#selectedPipeline);
        if (sandboxBadge) {
            sandboxBadge.classList.toggle('hidden', !superseded);
            if (superseded) {
                sandboxBadge.textContent = this.#selectedPipeline.sandbox_state;
                sandboxBadge.className = `ps-sandbox-badge ${this.#esc(this.#selectedPipeline.sandbox_state)}`;
                sandboxBadge.title = 'Created from an older ELM version — ready for cleanup rather than editing';
            }
        }

        // Feature-bundle provenance (design doc §5) — a developer must never mistake an
        // imported artifact for a scratch one, or vice versa.
        const featureBadge = this.shadowRoot.getElementById('ps-detail-feature-badge');
        if (featureBadge) {
            const feature = this.#selectedPipeline?.feature;
            const bundleId = this.#selectedPipeline?.bundle_id;
            featureBadge.classList.toggle('hidden', !feature && !bundleId);
            if (feature || bundleId) {
                featureBadge.textContent = feature ? `📦 ${feature}` : '📦 (unassigned)';
                featureBadge.title = bundleId ? `bundle_id ${bundleId}` : 'No bundle_id on this artifact';
            }
        }
    }

    // A sandbox created from an older ELM version is presented as ready for
    // cleanup rather than editing (see docs/ELM_DYNAMIC_PIPELINE_API.md §5).
    #isSupersededPipeline(p) {
        return p?.sandbox_state === 'superseded' || p?.sandbox_state === 'archived';
    }

    // A pipeline whose config restored but whose artifacts did not (usually a missing
    // bundle-imported plugin). Never started, cannot be repaired from the UI; delete is
    // the only remedy. See docs/handoff/frontend/incomplete-pipeline-state.md.
    #isIncompletePipeline(p) {
        return p?.state === 'incomplete';
    }

    // `problems` is omitted rather than emitted empty on healthy pipelines. `detail` is
    // always human-readable, so unknown codes still render usefully.
    #problemSummary(p) {
        const problems = p?.problems;
        if (!problems?.length) return '';
        const first = problems[0];
        const head = [first.code, first.ref].filter(Boolean).join(' ');
        const text = [head, first.detail].filter(Boolean).join(': ');
        return problems.length > 1 ? `${text} (+${problems.length - 1} more)` : text;
    }

    #renderDetailProblems() {
        const el = this.shadowRoot.getElementById('ps-detail-problems');
        if (!el) return;
        const problems = this.#selectedPipeline?.problems;
        el.classList.toggle('hidden', !problems?.length);
        if (!problems?.length) { el.innerHTML = ''; return; }
        const rows = problems.map(pr => `<div class="ps-detail-problem">
            <code>${this.#esc(pr.code || 'PROBLEM')}</code>${pr.ref ? ` <code>${this.#esc(pr.ref)}</code>` : ''}
            ${this.#esc(pr.detail || '')}
        </div>`).join('');
        el.innerHTML = `<div><strong>This pipeline could not be fully restored.</strong> Re-import the missing artifact, or delete it — the stored config persists across restarts until it is deleted.</div>${rows}`;
    }

    #renderDetailActions() {
        const container = this.shadowRoot.getElementById('ps-detail-actions');
        const state = this.#selectedPipeline?.state;
        const name = this.#selectedPipeline?.name;
        const superseded = this.#isSupersededPipeline(this.#selectedPipeline);
        const incomplete = this.#isIncompletePipeline(this.#selectedPipeline);
        const btns = [];
        if (!this.#readOnly) {
            if (state === 'running') {
                btns.push('<hs-toolbar-button data-action="stop" title="Stop"></hs-toolbar-button>');
                btns.push('<hs-toolbar-button data-action="restart" title="Restart"></hs-toolbar-button>');
            } else if (state === 'stopped' || state === 'error') {
                btns.push('<hs-toolbar-button data-action="start" title="Start"></hs-toolbar-button>');
                btns.push('<hs-toolbar-button data-action="restart" title="Restart"></hs-toolbar-button>');
            }
            // Save on an incomplete pipeline persists the mutated config and only then fails to
            // restart, destroying the original — so it never gets an Edit affordance.
            if (!superseded && !incomplete) btns.push('<hs-toolbar-button data-action="edit" title="Edit"></hs-toolbar-button>');
            btns.push('<hs-toolbar-button data-action="delete" title="Delete"></hs-toolbar-button>');
        }
        container.innerHTML = btns.join('');

        for (const btn of container.querySelectorAll('[data-action]')) {
            this.onEvent('click', async () => {
                if (btn.dataset.action === 'edit') {
                    await this.#showFormView(this.#selectedPipeline);
                    return;
                }
                await this.#handlePipelineAction(btn.dataset.action, name);
                if (btn.dataset.action === 'delete') {
                    this.#backToList();
                } else {
                    await this.#openPipelineDetail(name, this.#currentTab());
                }
            }, btn);
        }
    }

    #renderConfig() {
        const el = this.shadowRoot.getElementById('ps-config-content');
        el.textContent = JSON.stringify(this.#selectedPipeline?.config || this.#selectedPipeline, null, 2);
    }

    async #fetchStats(name) {
        const container = this.shadowRoot.getElementById('ps-stats-content');
        container.innerHTML = '<div class="ps-stats-placeholder">Loading stats\u2026</div>';
        try {
            const stats = await this.fetch(`${this.#baseUrl}/pipelines/${encodeURIComponent(name)}/stats`);
            this.#renderStats(stats);
        } catch (err) {
            Logger.error('Failed to fetch pipeline stats: ', err);
            container.innerHTML = '<div class="ps-stats-placeholder">Failed to load stats</div>';
        }
    }

    #renderStats(stats) {
        const container = this.shadowRoot.getElementById('ps-stats-content');
        const esc = v => this.#esc(String(v ?? '—'));
        const fmtMs = ms => ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Number(ms.toFixed(1))}ms`;
        const fmtTimestamp = ts => ts ? new Date(ts).toLocaleString() : '—';

        if (stats.state !== 'running') {
            container.innerHTML = `<div class="ps-stats-placeholder">Pipeline is ${esc(stats.state)} — stats available when running</div>`;
            return;
        }

        const countersHtml = `
            <div class="ps-stats-section">
                <div class="ps-stats-title">Counters</div>
                <div class="ps-stats-grid">
                    <div class="ps-stat-card"><div class="ps-stat-value">${esc(stats.messages_in)}</div><div class="ps-stat-label">Messages In</div></div>
                    <div class="ps-stat-card"><div class="ps-stat-value">${esc(stats.messages_out)}</div><div class="ps-stat-label">Messages Out</div></div>
                    <div class="ps-stat-card"><div class="ps-stat-value">${esc(stats.errors)}</div><div class="ps-stat-label">Errors</div></div>
                    <div class="ps-stat-card"><div class="ps-stat-value">${esc(stats.messages_in - stats.messages_out)}</div><div class="ps-stat-label">In Flight</div></div>
                </div>
            </div>`;

        const bufferPct = stats.buffer_capacity ? Math.round(stats.buffer_depth / stats.buffer_capacity * 100) : 0;
        const runtimeHtml = `
            <div class="ps-stats-section">
                <div class="ps-stats-title">Runtime</div>
                <div class="ps-stats-grid">
                    <div class="ps-stat-card"><div class="ps-stat-value">${fmtMs(stats.uptime_ms)}</div><div class="ps-stat-label">Uptime</div></div>
                    <div class="ps-stat-card"><div class="ps-stat-value">${fmtTimestamp(stats.last_message_at)}</div><div class="ps-stat-label">Last Message</div></div>
                    <div class="ps-stat-card"><div class="ps-stat-value">${esc(stats.buffer_depth)} / ${esc(stats.buffer_capacity)} (${bufferPct}%)</div><div class="ps-stat-label">Buffer</div></div>
                    <div class="ps-stat-card"><div class="ps-stat-value">${esc(stats.blocked_partitions)}</div><div class="ps-stat-label">Blocked Partitions</div></div>
                </div>
            </div>`;

        let lagHtml = '';
        if (stats.offset_lag && Object.keys(stats.offset_lag).length > 0) {
            const lagRows = Object.entries(stats.offset_lag)
                .map(([tp, lag]) => `<tr><td>${esc(tp)}</td><td>${esc(lag)}</td></tr>`).join('');
            lagHtml = `
                <div class="ps-stats-section">
                    <div class="ps-stats-title">Kafka Offset Lag</div>
                    <table class="ps-stats-table"><thead><tr><th>Topic:Partition</th><th>Lag</th></tr></thead><tbody>${lagRows}</tbody></table>
                </div>`;
        }

        let latencyHtml = '';
        if (stats.plugin_latency && Object.keys(stats.plugin_latency).length > 0) {
            const latRows = Object.entries(stats.plugin_latency)
                .map(([name, l]) => {
                    const avg = l.count ? l.total_ms / l.count : 0;
                    return `<tr><td>${esc(name)}</td><td>${esc(l.count)}</td><td>${fmtMs(avg)}</td><td>${fmtMs(l.total_ms)}</td></tr>`;
                }).join('');
            latencyHtml = `
                <div class="ps-stats-section">
                    <div class="ps-stats-title">Plugin Latency</div>
                    <table class="ps-stats-table"><thead><tr><th>Plugin</th><th>Count</th><th>Avg</th><th>Total</th></tr></thead><tbody>${latRows}</tbody></table>
                </div>`;
        }

        container.innerHTML = `
            <div class="ps-stats-toolbar"><button id="ps-stats-refresh-btn" class="btn btn-sm">Refresh</button></div>
            ${countersHtml}${runtimeHtml}${lagHtml}${latencyHtml}`;

        this.onEvent('click', () => this.#fetchStats(stats.name), this.shadowRoot.getElementById('ps-stats-refresh-btn'));
    }

    #currentTab() {
        const active = this.shadowRoot.querySelector('.ps-tab.active');
        return active?.dataset?.tab || 'logs';
    }

    #switchTab(tab) {
        const sd = this.shadowRoot;
        for (const t of sd.querySelectorAll('.ps-tab')) {
            t.classList.toggle('active', t.dataset.tab === tab);
        }
        for (const p of sd.querySelectorAll('.ps-tab-panel')) {
            p.classList.toggle('hidden', p.id !== `ps-tab-${tab}`);
        }

        if (tab === 'logs' && this.#selectedPipeline) {
            this.#connectSSE(this.#selectedPipeline.name);
            this.#populateLogTestBar();
        } else {
            this.#disconnectSSE();
        }
        if (tab === 'stats' && this.#selectedPipeline) {
            this.#fetchStats(this.#selectedPipeline.name);
        }
        if (tab === 'evidence' && this.#selectedPipeline) {
            this.#renderEvidencePanel();
        }
    }

    #backToList() {
        this.#disconnectSSE();
        this.#resetTelemetryDrawer();
        this.#selectedPipeline = null;
        this.#editingPipeline = null;
        this.#formSnapshot = null;
        this.#showView('list');
        this.#fetchPipelines();
    }

    #resetTelemetryDrawer() {
        const t = this.#telemetry;
        if (t.pendingRefresh) {
            clearTimeout(t.pendingRefresh);
            t.pendingRefresh = null;
        }
        t.open = false;
        t.autoRefresh = false;
        t.rows = [];
        t.expanded = new Set();
        t.error = null;
        const drawer = this.shadowRoot.getElementById('ps-telemetry-drawer');
        if (drawer) drawer.classList.add('collapsed');
    }

    // ---- SSE Log Streaming ----

    #_beforeUnload = () => this.#disconnectSSE();

    async #connectSSE(pipelineName) {
        this.#disconnectSSE();
        this.#sseAbortController = new AbortController();
        window.addEventListener('beforeunload', this.#_beforeUnload);
        this.#logsFor(pipelineName);   // ensure a retained buffer exists and mark it most-recently-used
        this.#logPaused = false;
        this.#logAutoScroll = true;
        this.#rerenderLogs();
        const pauseBtn = this.shadowRoot.getElementById('ps-log-pause-btn');
        if (pauseBtn) pauseBtn.textContent = 'Freeze';

        const url = `/dashboard${this.#baseUrl}/pipelines/${encodeURIComponent(pipelineName)}/logs`;
        const token = await Auth.getToken();

        fetchEventSource(url, {
            method: 'GET',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            signal: this.#sseAbortController.signal,
            openWhenHidden: true,
            onopen: async (response) => {
                if (response.ok) {
                    this.#setStatus(`Connected to ${pipelineName}`);
                } else {
                    Logger.error('SSE open failed: ', response.status);
                    this.#setStatus(`Connection failed (${response.status})`);
                }
            },
            onmessage: (event) => {
                if (!event.data) return;
                try {
                    const entry = JSON.parse(event.data);
                    this.#appendLogEntry(entry);
                } catch (err) {
                    Logger.debug('SSE parse error: ', err);
                }
            },
            onerror: (err) => {
                if (this.#sseAbortController?.signal?.aborted) throw err;
                Logger.error('SSE error: ', err);
                this.#setStatus('Connection lost — reconnecting...');
            },
        });
    }

    #disconnectSSE() {
        if (this.#sseAbortController) {
            this.#sseAbortController.abort();
            this.#sseAbortController = null;
        }
    }

    #appendLogEntry(entry) {
        const buffer = this.#logsFor();
        buffer.push(entry);
        if (buffer.length > this.#logLimit) {
            buffer.splice(0, buffer.length - this.#logLimit);
        }
        this.#maybeAutoRefreshTelemetry();

        if (this.#logPaused) return;

        const level = entry.level || 'info';
        if (!this.#logLevelFilters[level]) return;

        const logOutput = this.shadowRoot.getElementById('ps-log-output');
        const placeholder = logOutput.querySelector('.ps-log-placeholder');
        if (placeholder) placeholder.remove();

        const line = this.#createLogLine(entry);
        logOutput.appendChild(line);

        while (logOutput.childElementCount > this.#logLimit) {
            logOutput.removeChild(logOutput.firstElementChild);
        }

        this.#updateLogCount();
        if (this.#logAutoScroll) {
            logOutput.scrollTop = logOutput.scrollHeight;
        }
    }

    #createLogLine(entry) {
        const div = document.createElement('div');
        const level = entry.level || 'info';
        div.className = `ps-log-line level-${level}`;
        const ts = entry.ts ? new Date(entry.ts).toISOString().slice(11, 23) : '';
        const msgHtml = this.#formatLogMsg(entry.msg || '');
        div.innerHTML = `<span class="log-ts">${this.#esc(ts)}</span> <span class="log-level">[${this.#esc(level)}]</span> <span class="log-msg">${msgHtml}</span>`;
        return div;
    }

    #formatLogMsg(msg) {
        if (!this.#logPretty) return this.#esc(msg);
        const trimmed = String(msg).trim();
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            try {
                const parsed = JSON.parse(trimmed);
                const pretty = JSON.stringify(parsed, null, 2);
                return this.#colorizeJson(pretty);
            } catch { /* not valid JSON, fall through */ }
        }
        return this.#esc(msg);
    }

    #colorizeJson(json) {
        return json.replace(/("(?:\\.|[^"\\])*")\s*:/g, '<span class="log-json-key">$1</span>:')
            .replace(/:\s*("(?:\\.|[^"\\])*")/g, ': <span class="log-json-str">$1</span>')
            .replace(/:\s*(\d+(?:\.\d+)?)\b/g, ': <span class="log-json-num">$1</span>')
            .replace(/:\s*(true|false|null)\b/g, ': <span class="log-json-bool">$1</span>');
    }

    #rerenderLogs() {
        const logOutput = this.shadowRoot.getElementById('ps-log-output');
        if (!logOutput) return;
        const buffer = this.#logsFor();
        logOutput.innerHTML = '';
        const visible = buffer.filter(e => this.#logLevelFilters[e.level || 'info']);
        for (const entry of visible) {
            logOutput.appendChild(this.#createLogLine(entry));
        }
        if (!visible.length) {
            const placeholder = document.createElement('div');
            placeholder.className = 'ps-log-placeholder';
            placeholder.textContent = buffer.length
                ? 'All retained lines are hidden by the level filters'
                : 'Attached — waiting for output...';
            logOutput.appendChild(placeholder);
        }
        this.#updateLogCount();
        if (this.#logAutoScroll) {
            logOutput.scrollTop = logOutput.scrollHeight;
        }
    }

    #toggleLogPause() {
        this.#logPaused = !this.#logPaused;
        const btn = this.shadowRoot.getElementById('ps-log-pause-btn');
        btn.textContent = this.#logPaused ? 'Unfreeze' : 'Freeze';
        if (!this.#logPaused) {
            this.#rerenderLogs();
        }
    }

    #clearLogs() {
        this.#logsFor().length = 0;
        const logOutput = this.shadowRoot.getElementById('ps-log-output');
        logOutput.innerHTML = '<div class="ps-log-placeholder">Log buffer cleared</div>';
        this.#updateLogCount();
    }

    #toggleLogPretty() {
        this.#logPretty = !this.#logPretty;
        const btn = this.shadowRoot.getElementById('ps-log-pretty-btn');
        btn.classList.toggle('ps-toggle-on', this.#logPretty);
        this.#rerenderLogs();
    }

    #updateLogCount() {
        const el = this.shadowRoot.getElementById('ps-log-count');
        if (el) el.textContent = `${this.#logsFor().length} lines`;
    }

    // Retained ring buffer for a pipeline, created on demand. Re-inserting on every access keeps
    // the Map in least-recently-used order so eviction drops the stalest pipeline's lines.
    #logsFor(pipelineName = this.#selectedPipeline?.name) {
        if (!pipelineName) return [];
        const existing = this.#logBuffers.get(pipelineName);
        if (existing) {
            this.#logBuffers.delete(pipelineName);
            this.#logBuffers.set(pipelineName, existing);
            return existing;
        }
        while (this.#logBuffers.size >= MAX_RETAINED_LOG_PIPELINES) {
            this.#logBuffers.delete(this.#logBuffers.keys().next().value);
        }
        const buffer = [];
        this.#logBuffers.set(pipelineName, buffer);
        return buffer;
    }

    #initLogLimit() {
        let saved = null;
        try { saved = localStorage.getItem('ps.logLimit'); } catch { /* ignore */ }
        this.#logLimit = this.#clampLogLimit(saved);
        const input = this.shadowRoot.getElementById('ps-log-limit');
        if (input) input.value = String(this.#logLimit);
    }

    #clampLogLimit(value) {
        const n = Math.floor(Number(value));
        if (!Number.isFinite(n) || n <= 0) return LOG_LIMIT_DEFAULT;
        return Math.min(LOG_LIMIT_MAX, Math.max(LOG_LIMIT_MIN, n));
    }

    #setLogLimit(value) {
        this.#logLimit = this.#clampLogLimit(value);
        const input = this.shadowRoot.getElementById('ps-log-limit');
        if (input) input.value = String(this.#logLimit);
        try { localStorage.setItem('ps.logLimit', String(this.#logLimit)); } catch { /* ignore */ }
        for (const buffer of this.#logBuffers.values()) {
            if (buffer.length > this.#logLimit) buffer.splice(0, buffer.length - this.#logLimit);
        }
        this.#rerenderLogs();
    }

    // Populate the inline test case picker on the logs tab
    #populateLogTestBar() {
        const btn = this.shadowRoot.getElementById('ps-log-test-select');
        const hidden = this.shadowRoot.getElementById('ps-log-test-name');
        if (!btn || !hidden) return;
        const testData = this.#selectedPipeline?.test_data || {};
        const keys = Object.keys(testData);
        if (!keys.length) {
            btn.innerHTML = '<em>(no test cases)</em>';
            hidden.value = '';
            return;
        }
        hidden.value = keys[0];
        btn.textContent = keys[0];
    }

    async #runLogTest() {
        const name = this.#selectedPipeline?.name;
        if (!name) return;
        const chosen = this.shadowRoot.getElementById('ps-log-test-name')?.value?.trim();
        if (!chosen) { this.#showAlert('No test case selected. Create test cases in the Test tab.'); return; }
        const payload = this.#selectedPipeline?.test_data?.[chosen]?.payload || {};
        this.#clearLogs();
        try {
            const res = await this.fetch(`${this.#baseUrl}/pipelines/${encodeURIComponent(name)}/test`, {
                method: 'POST', payload: { payload },
            });
            // Surface the test result as a synthetic log entry; store compact JSON so the
            // pretty-print toggle controls formatting.
            const ok = res?.success !== false && !res?.error;
            this.#appendLogEntry({
                ts: Date.now(), level: ok ? 'info' : 'error',
                msg: JSON.stringify(res),
            });
        } catch (err) {
            this.#appendLogEntry({ ts: Date.now(), level: 'error', msg: `Test failed: ${err.message || err}` });
        }
    }

    #openTestCasePicker({ includeCustom, target }) {
        let picker = this.shadowRoot.querySelector('hs-list-picker');
        if (!picker) {
            picker = document.createElement('hs-list-picker');
            this.shadowRoot.appendChild(picker);
        }
        const testData = this.#selectedPipeline?.test_data || {};
        const items = [];
        if (includeCustom) items.push({ __custom: true, name: '' });
        for (const [name, tc] of Object.entries(testData)) {
            items.push({ name, description: tc?.description || '' });
        }
        picker.open({
            title: 'Choose test case',
            tabs: [{
                id: 'all',
                label: 'Test Cases',
                items,
                getSearchText: it => it.__custom ? ['custom'] : [it.name, it.description],
                renderItem: it => {
                    if (it.__custom) return { title: '(custom payload)', subtitle: 'clear the selection and edit the payload directly' };
                    return { title: it.name, subtitle: it.description || '' };
                },
                getValue: it => it,
                emptyText: 'No test cases saved',
            }],
            onSelect: (value) => {
                const chosen = value?.__custom ? '' : value?.name || '';
                if (target === 'log') this.#onLogTestCaseChanged(chosen);
                else this.#onTestCaseChanged(chosen);
            },
        });
    }

    #onLogTestCaseChanged(name) {
        const btn = this.shadowRoot.getElementById('ps-log-test-select');
        const hidden = this.shadowRoot.getElementById('ps-log-test-name');
        if (hidden) hidden.value = name;
        if (btn) btn.textContent = name || '(no test cases)';
    }

    // ---- Test Panel ----

    #bindTestDropZone() {
        const textarea = this.shadowRoot.getElementById('ps-test-payload');
        if (!textarea) return;
        this.onEvent('dragover', (_, ev) => { ev.preventDefault(); }, textarea);
        this.onEvent('drop', (_, ev) => {
            ev.preventDefault();
            const file = ev.dataTransfer?.files?.[0];
            if (file && file.type === 'application/json') {
                const reader = new FileReader();
                reader.onload = () => { textarea.value = reader.result; };
                reader.readAsText(file);
            }
        }, textarea);
    }

    async #runTest() {
        const name = this.#selectedPipeline?.name;
        if (!name) return;

        const textarea = this.shadowRoot.getElementById('ps-test-payload');
        const resultContainer = this.shadowRoot.getElementById('ps-test-output');
        const resultPre = this.shadowRoot.getElementById('ps-test-result');

        let payload;
        try {
            payload = JSON.parse(textarea.value || '{}');
        } catch (err) {
            resultContainer.classList.remove('hidden');
            resultPre.textContent = `Invalid JSON: ${err.message}`;
            return;
        }

        await this.#withLoading(async () => {
            try {
                const res = await this.fetch(`${this.#baseUrl}/pipelines/${encodeURIComponent(name)}/test`, {
                    method: 'POST',
                    payload: { payload },
                    expectedStatuses: [409],
                });
                resultContainer.classList.remove('hidden');
                resultPre.textContent = JSON.stringify(res, null, 2);
            } catch (err) {
                const body = await this.#errorBody(err);
                resultContainer.classList.remove('hidden');
                resultPre.textContent = body?.code === 'PIPELINE_INCOMPLETE'
                    ? `Pipeline is incomplete and cannot be tested. ${body.error || ''}`.trim()
                    : `Test failed: ${err.message || err}`;
            }
        });
    }

    // ---- Test Cases ----

    #populateTestCases() {
        const btn = this.shadowRoot.getElementById('ps-test-case-select');
        const hidden = this.shadowRoot.getElementById('ps-test-case-name');
        if (!btn || !hidden) return;
        hidden.value = '';
        btn.innerHTML = '<em>(custom payload)</em>';
        this.shadowRoot.getElementById('ps-test-delete-btn')?.classList.add('hidden');
        this.shadowRoot.getElementById('ps-test-case-desc')?.classList.add('hidden');
        this.#updateTestEditingIndicator('');
    }

    // Reflects which test case (if any) Save will write back to — an existing
    // selection shows its name and Save writes to it directly; no selection
    // shows "New test" and Save prompts for a name (i.e. acts as Save As).
    #updateTestEditingIndicator(name) {
        const label = this.shadowRoot.getElementById('ps-test-editing-label');
        const saveBtn = this.shadowRoot.getElementById('ps-test-save-btn');
        if (label) label.textContent = name ? `Editing: ${name}` : 'New test (unsaved)';
        saveBtn?.setTitle(name ? 'Save' : 'Save As…');
    }

    #onTestCaseSelect() {
        this.#onTestCaseChanged(this.shadowRoot.getElementById('ps-test-case-name')?.value || '');
    }

    #onTestCaseChanged(name) {
        const btn = this.shadowRoot.getElementById('ps-test-case-select');
        const hidden = this.shadowRoot.getElementById('ps-test-case-name');
        const deleteBtn = this.shadowRoot.getElementById('ps-test-delete-btn');
        const descEl = this.shadowRoot.getElementById('ps-test-case-desc');
        const textarea = this.shadowRoot.getElementById('ps-test-payload');
        if (hidden) hidden.value = name;

        if (!name) {
            if (btn) btn.innerHTML = '<em>(custom payload)</em>';
            deleteBtn?.classList.add('hidden');
            descEl?.classList.add('hidden');
            this.#updateTestEditingIndicator('');
            return;
        }

        const testCase = this.#selectedPipeline?.test_data?.[name];
        if (!testCase) return;

        if (btn) btn.textContent = name;
        if (textarea) textarea.value = JSON.stringify(testCase.payload, null, 2);
        deleteBtn?.classList.remove('hidden');
        if (testCase.description) {
            descEl.textContent = testCase.description;
            descEl.classList.remove('hidden');
        } else {
            descEl?.classList.add('hidden');
        }
        this.#updateTestEditingIndicator(name);
    }

    async #saveTestCase() {
        const pipeline = this.#selectedPipeline;
        if (!pipeline?.name) return;

        const textarea = this.shadowRoot.getElementById('ps-test-payload');
        let payload;
        try {
            payload = JSON.parse(textarea.value || '{}');
        } catch (err) {
            this.#showAlert(`Invalid JSON: ${err.message}`);
            return;
        }

        // An existing selection saves straight back to that test case (no
        // prompt). No selection means this is a new payload, so it behaves
        // like "Save As" and asks for a name.
        const existingName = this.shadowRoot.getElementById('ps-test-case-name')?.value?.trim();
        let caseName = existingName;
        if (!caseName) {
            const nameInput = await this.#prompt('Test case name:', 'e.g. connect_event');
            if (!nameInput?.trim()) return;
            caseName = nameInput.trim();
        }

        const existingDescription = pipeline.test_data?.[caseName]?.description;
        const testData = { ...(pipeline.test_data || {}), [caseName]: { payload } };
        if (existingDescription) testData[caseName].description = existingDescription;

        await this.#withLoading(async () => {
            try {
                await this.fetch(`${this.#baseUrl}/pipelines/${encodeURIComponent(pipeline.name)}`, {
                    method: 'PUT',
                    payload: { ...pipeline.config || pipeline, test_data: testData },
                });
                this.#selectedPipeline.test_data = testData;
                this.#onTestCaseChanged(caseName);
            } catch (err) {
                this.#showAlert(`Save failed: ${err.message || err}`);
            }
        });
    }

    async #deleteTestCase() {
        const pipeline = this.#selectedPipeline;
        const caseName = this.shadowRoot.getElementById('ps-test-case-name')?.value?.trim();
        if (!pipeline?.name || !caseName) return;
        if (!await this.#confirm(`Delete test case "${caseName}"?`)) return;

        const testData = { ...(pipeline.test_data || {}) };
        delete testData[caseName];

        await this.#withLoading(async () => {
            try {
                await this.fetch(`${this.#baseUrl}/pipelines/${encodeURIComponent(pipeline.name)}`, {
                    method: 'PUT',
                    payload: { ...pipeline.config || pipeline, test_data: testData },
                });
                this.#selectedPipeline.test_data = testData;
                this.#populateTestCases();
                this.shadowRoot.getElementById('ps-test-payload').value = '';
            } catch (err) {
                this.#showAlert(`Delete failed: ${err.message || err}`);
            }
        });
    }

    async #prompt(message, placeholder = '') {
        return new Promise(resolve => {
            const overlay = document.createElement('div');
            overlay.className = 'ps-confirm-overlay';
            overlay.innerHTML = `<div class="ps-confirm-dialog">
                <p>${this.#esc(message)}</p>
                <input type="text" class="ps-input" id="ps-prompt-input" placeholder="${this.#esc(placeholder)}" />
                <div class="ps-confirm-actions">
                    <button class="btn" id="ps-prompt-cancel">Cancel</button>
                    <button class="btn btn-primary" id="ps-prompt-ok">OK</button>
                </div>
            </div>`;
            this.shadowRoot.appendChild(overlay);
            const input = overlay.querySelector('#ps-prompt-input');
            input.focus();
            overlay.querySelector('#ps-prompt-cancel').onclick = () => { overlay.remove(); resolve(null); };
            overlay.querySelector('#ps-prompt-ok').onclick = () => { overlay.remove(); resolve(input.value); };
            input.onkeydown = (e) => { if (e.key === 'Enter') { overlay.remove(); resolve(input.value); } };
        });
    }

    // ---- Builder Catalog ----

    async #fetchCatalog() {
        try {
            const res = await this.fetch(`${this.#baseUrl}/builder/catalog`);
            this.#catalog = res?.categories || {};
        } catch (err) {
            Logger.error('Failed to fetch catalog: ', err);
            this.#catalog = {};
        }
    }

    #normalizeCatalogRef(cat, moduleName, fn) {
        if (fn?.ref) return fn.ref;
        if (fn?.name) return `${cat}:${moduleName}.${fn.name}`;
        return null;
    }

    // Flat "add step" dropdown of every promoted plugin ref — the scratch builder picks a
    // plugin from a list rather than browsing the catalog (see sandbox-refocus-view-tune.md § 3).
    #populateStepAddSelect() {
        const select = this.shadowRoot.getElementById('ps-add-step-select');
        if (!select) return;
        const refs = [];
        for (const [cat, modules] of Object.entries(this.#catalog)) {
            for (const mod of (modules || [])) {
                const moduleName = mod.module || mod.name || 'unknown';
                for (const fn of (mod.functions || [])) {
                    const ref = this.#normalizeCatalogRef(cat, moduleName, fn);
                    if (ref) refs.push(ref);
                }
            }
        }
        refs.sort();
        select.innerHTML = refs.length
            ? refs.map(ref => `<option value="${this.#esc(ref)}">${this.#esc(ref)}</option>`).join('')
            : '<option value="">(no plugins available)</option>';
    }

    // ---- Pipeline Builder ----

    #nextAvailableName(base) {
        const taken = new Set(this.#pipelines.map(p => p.name));
        if (!taken.has(base)) return base;
        for (let i = 2; ; i++) {
            const candidate = `${base}-${i}`;
            if (!taken.has(candidate)) return candidate;
        }
    }

    #getDeveloperNamespace() {
        const username = UserSession.get('userinfo')?.preferred_username?.trim() || '';
        return username.split('@')[0].trim();
    }

    #getEffectiveNamespace() {
        return this.#namespace || this.#getDeveloperNamespace();
    }

    async #showFormView(pipeline = null) {
        await this.#withLoading(async () => {
            this.#editingPipeline = pipeline;
            this.#isTuning = !!pipeline?.bundle_id;
            const sd = this.shadowRoot;
            const isEdit = !!pipeline;

        const suggestedName = isEdit ? '' : this.#nextAvailableName('my-pipeline');

        sd.getElementById('ps-form-title').textContent = isEdit ? `Edit: ${pipeline.name}` : 'Create Pipeline';
        sd.getElementById('ps-form-save-btn').textContent = isEdit ? 'Update' : 'Create';
        const notice = sd.getElementById('ps-form-mode-notice');
        if (notice) {
            notice.innerHTML = this.#isTuning
                ? `Tuning <code>${this.#esc(pipeline.name)}</code> (bundle_id <code>${this.#esc(pipeline.bundle_id)}</code>) — saving creates a new version (destroy-then-create); any evidence recorded against the previous version becomes stale until it's re-run in the Evidence tab.`
                : 'Scratch pipeline — no assertions or <code>rationale</code>, so it can never pass a <code>candidate</code> export. For a real feature, author it with an IDE agent and import it as a feature bundle instead.';
        }
        const nameInput = sd.getElementById('ps-form-name');
        nameInput.value = pipeline?.name || suggestedName || '';
        nameInput.placeholder = '';
        nameInput.disabled = false;
        sd.getElementById('ps-form-result').classList.add('hidden');
        sd.getElementById('ps-form-advanced').classList.add('hidden');

        const triggerCategory = this.#classifyTriggerCategory(pipeline?.trigger || pipeline?.config?.trigger);
        for (const r of sd.querySelectorAll('input[name="ps-trigger-type"]')) {
            r.checked = r.value === triggerCategory;
        }

        await Promise.all([this.#fetchCatalog(), this.#ensureSchemaDomains()]);

        this.#renderTriggerFields(triggerCategory, pipeline?.trigger || pipeline?.config?.trigger);

        sd.getElementById('ps-pipeline-steps').innerHTML = '';
        const steps = pipeline?.pipeline || pipeline?.config?.pipeline || [];
        for (const step of steps) {
            if (typeof step === 'string') {
                this.#addPipelineStep(step);
            } else if (step.tap) {
                for (const t of step.tap) {
                    this.#addPipelineStep(typeof t === 'string' ? t : t.plugin || '', { isTap: true });
                }
            } else if (step.plugin) {
                this.#addPipelineStep(step.plugin, {
                    opts: step.opts, concurrency: step.concurrency,
                    batch_size: step.batch_size, batch_ms: step.batch_ms, watchdog_ms: step.watchdog_ms,
                });
            }
        }

        const cfg = pipeline?.config || pipeline || {};
        sd.getElementById('ps-form-hwm').value = cfg.highWaterMark ?? 16;
        sd.getElementById('ps-form-serial').checked = !!cfg.serialPipeline;

        this.#populateStepAddSelect();
        // Seed auto-plugins only on Create, so legacy pipelines opened for Edit aren't silently mutated.
        if (!isEdit) this.#ensureTriggerAutoPlugins(triggerCategory);
        this.#renderFormQuickActions();
        this.#showView('form');
        // Snapshot AFTER rendering so any auto-injected steps count as the baseline.
        this.#formSnapshot = this.#serializeFormForCompare();
        });
    }

    // Captures every editable value in the builder form (name, trigger, steps + overrides, advanced
    // options). Used strictly for equality comparison against #formSnapshot to detect unsaved edits.
    #serializeFormForCompare() {
        const sd = this.shadowRoot;
        const stepRows = [...sd.querySelectorAll('.ps-step-row')].map(row => ({
            ref: row.querySelector('.ps-step-input')?.value || '',
            tap: !!row.querySelector('.ps-step-tap-cb')?.checked,
            overrides: row._stepOverrides || {},
        }));
        return JSON.stringify({
            name: sd.getElementById('ps-form-name')?.value || '',
            triggerCategory: sd.querySelector('input[name="ps-trigger-type"]:checked')?.value || '',
            triggerTopic: sd.getElementById('ps-trigger-topic')?.value || '',
            triggerFromBeginning: !!sd.getElementById('ps-trigger-from-beginning')?.checked,
            triggerCron: sd.getElementById('ps-trigger-cron')?.value || '',
            triggerTz: sd.getElementById('ps-trigger-tz')?.value || '',
            triggerTimeout: sd.getElementById('ps-trigger-timeout')?.value || '',
            triggerInDomain: sd.getElementById('ps-trigger-in-domain')?.value || '',
            triggerInMt: sd.getElementById('ps-trigger-in-mt')?.value || '',
            subprod: !!sd.getElementById('ps-form-subprod')?.checked,
            hwm: sd.getElementById('ps-form-hwm')?.value || '',
            serial: !!sd.getElementById('ps-form-serial')?.checked,
            steps: stepRows,
        });
    }

    #isFormDirty() {
        return this.#currentView === 'form'
            && this.#formSnapshot != null
            && this.#formSnapshot !== this.#serializeFormForCompare();
    }

    async #confirmDiscardIfDirty(message = 'You have unsaved changes. Discard them and continue?') {
        if (!this.#isFormDirty()) return true;
        return await this.#confirm(message);
    }

    // Renders Run / Logs / Test quick-nav buttons on the edit form header. Only shown when editing
    // an existing pipeline (Create has no state to run against). Any nav prompts for unsaved changes.
    #renderFormQuickActions() {
        const container = this.shadowRoot.getElementById('ps-form-quick-actions');
        if (!container) return;
        container.innerHTML = '';
        if (!this.#editingPipeline || this.#readOnly) return;
        const name = this.#editingPipeline.name;
        const state = this.#editingPipeline.state;
        let runAction = null;
        if (state === 'running') runAction = 'restart';
        else if (state === 'stopped' || state === 'error') runAction = 'start';

        const btns = [];
        if (runAction) btns.push(`<hs-toolbar-button data-quick-action="${runAction}" title="${runAction === 'start' ? 'Start' : 'Restart'}"></hs-toolbar-button>`);
        btns.push('<hs-toolbar-button data-quick-action="logs" title="Logs"></hs-toolbar-button>');
        btns.push('<hs-toolbar-button data-quick-action="test" title="Test"></hs-toolbar-button>');
        container.innerHTML = btns.join('');

        for (const btn of container.querySelectorAll('[data-quick-action]')) {
            this.onEvent('click', async () => {
                const action = btn.dataset.quickAction;
                if (!await this.#confirmDiscardIfDirty()) return;
                this.#formSnapshot = null;
                if (action === 'logs') this.#openPipelineDetail(name, 'logs');
                else if (action === 'test') this.#openPipelineDetail(name, 'test');
                else {
                    await this.#handlePipelineAction(action, name);
                    this.#openPipelineDetail(name, 'logs');
                }
            }, btn);
        }
    }

    #classifyTriggerCategory(trigger) {
        const type = trigger?.type;
        if (!type) return 'events';
        if (type === 'events') return 'events';
        if (type === 'state') return 'state';
        if (type === 'pipeline' || type === 'telemetry') return 'pipeline';
        if (type === 'schedule') return 'schedule';
        if (type === 'api') return 'api';
        if (type === 'kafka') {
            const topic = String(trigger?.topic || '');
            return RAW_FEED_TOPICS.includes(topic) ? 'kafka-events' : 'kafka-raw';
        }
        return 'kafka-raw';
    }

    #ensureTriggerAutoPlugins(category) {
        const refs = TRIGGER_AUTO_PLUGINS[category];
        if (!refs || !refs.length) return;
        const container = this.shadowRoot.getElementById('ps-pipeline-steps');
        if (!container) return;
        const existing = Array.from(container.querySelectorAll('.ps-step-row .ps-step-input'))
            .map(el => el.value.trim());
        const missing = refs.filter(ref => !existing.includes(ref));
        if (!missing.length) return;
        const before = container.firstChild;
        for (const ref of missing) {
            this.#addPipelineStep(ref);
            const row = container.lastElementChild;
            if (row && before) container.insertBefore(row, before);
        }
    }

    #renderTriggerFields(category, trigger = null) {
        const container = this.shadowRoot.getElementById('ps-trigger-fields');
        let html = '';
        if (category === 'events') {
            const types = trigger?.message_type
                ? (Array.isArray(trigger.message_type) ? trigger.message_type : [trigger.message_type])
                : [];
            const picked = trigger?.domain && types.length ? `${trigger.domain} / ${types.join(', ')}` : '';
            html = `<div class="ps-form-hint">Every event of the chosen message type(s), as it arrives. Parsed, filtered and normalized before your first step — no recv needed.</div>
                <label for="ps-trigger-in-picker" style="margin-top:6px">Event <span class="ps-cfg-required">*</span></label>
                <div class="ps-topic-row">
                    <button type="button" id="ps-trigger-in-picker" class="ps-input ps-picker-display" style="flex:1" title="Click to add a message type">${picked ? this.#esc(picked) : '<em>Choose message type…</em>'}</button>
                    <button type="button" id="ps-trigger-in-clear" class="btn btn-sm" title="Clear selection">✕</button>
                    <input type="hidden" id="ps-trigger-in-domain" value="${this.#esc(trigger?.domain || '')}" />
                    <input type="hidden" id="ps-trigger-in-mt" value="${this.#esc(types.join(','))}" />
                </div>
                <span class="ps-form-hint">Pick again to add another type from the same domain.</span>`;
        } else if (category === 'state') {
            html = `<div class="ps-form-hint">Fires when a cache source changes. The record is the message; change metadata arrives under <code>_state</code>. Debounced and merged — use Events if you need every occurrence.</div>
                <label for="ps-trigger-source-label" style="margin-top:6px">Source <span class="ps-cfg-required">*</span></label>
                <div class="ps-topic-row">
                    <button type="button" id="ps-trigger-source-label" class="ps-input ps-cfg-source-label ps-picker-display" style="flex:1" title="Click to change">${trigger?.source ? this.#esc(trigger.source) : '<em>None selected</em>'}</button>
                    <input type="hidden" id="ps-trigger-source" value="${this.#esc(trigger?.source || '')}" />
                </div>
                <label class="ps-form-row" style="margin-top:8px"><input type="checkbox" id="ps-trigger-from-beginning" ${trigger?.fromBeginning ? 'checked' : ''} /> Prime with the source's current contents first</label>`;
        } else if (category === 'pipeline') {
            const input = trigger?.inputs?.[0] || {};
            const picked = input.domain && input.message_type ? `${input.domain} / ${input.message_type}` : '';
            html = `<div class="ps-form-hint">Consume named telemetry another pipeline declares as an output. Producer is stopped/paused only when the two pipelines are in the same namespace.</div>
                <label for="ps-trigger-in-picker" style="margin-top:6px">Producer output <span class="ps-cfg-required">*</span></label>
                <div class="ps-topic-row">
                    <button type="button" id="ps-trigger-in-picker" class="ps-input ps-picker-display" style="flex:1" title="Click to change">${picked ? this.#esc(picked) : '<em>Choose message type…</em>'}</button>
                    <input type="hidden" id="ps-trigger-in-domain" value="${this.#esc(input.domain || '')}" />
                    <input type="hidden" id="ps-trigger-in-mt" value="${this.#esc(input.message_type || '')}" />
                </div>
                <label class="ps-form-row" style="margin-top:8px"><input type="checkbox" id="ps-trigger-in-subprod" ${input.subscribe_production ? 'checked' : ''} /> Also subscribe to production channel (sandbox pipelines)</label>`;
        } else if (category === 'schedule') {
            html = `<label for="ps-trigger-cron">Cron Expression</label>
                <input type="text" id="ps-trigger-cron" class="ps-input" placeholder="0 * * * *" value="${this.#esc(trigger?.cron || '')}" />
                <label for="ps-trigger-tz" style="margin-top:8px">Timezone</label>
                <input type="text" id="ps-trigger-tz" class="ps-input" placeholder="UTC" value="${this.#esc(trigger?.timezone || '')}" />`;
        } else if (category === 'api') {
            html = `<label for="ps-trigger-timeout">Timeout (ms)</label>
                <input type="number" id="ps-trigger-timeout" class="ps-input ps-input-sm" value="${trigger?.timeout ?? 30000}" min="1000" />`;
        } else if (category === 'kafka-events' || category === 'kafka-raw') {
            const showFromBeginning = !!trigger?.fromBeginning;
            const hint = category === 'kafka-events'
                ? 'Raw multi-event-type feed. recv + decrypt + unzip are auto-added and can be edited if needed.'
                : 'Raw Kafka topic. recv is auto-added; add decrypt/unzip if the feed is encrypted or compressed.';
            html = `<div class="ps-form-hint">${hint}</div>
                <label for="ps-trigger-topic" style="margin-top:6px">Topic <span class="ps-cfg-required">*</span></label>
                <div class="ps-topic-row">
                    <button type="button" id="ps-trigger-topic-label" class="ps-input ps-cfg-source-label ps-picker-display" style="flex:1" title="Click to change">${trigger?.topic ? this.#esc(trigger.topic) : '<em>None selected</em>'}</button>
                    <input type="hidden" id="ps-trigger-topic" value="${this.#esc(trigger?.topic || '')}" />
                </div>
                <label class="ps-form-row ${showFromBeginning ? '' : 'hidden'}" id="ps-from-beginning-row" style="margin-top:8px"><input type="checkbox" id="ps-trigger-from-beginning" ${trigger?.fromBeginning ? 'checked' : ''} /> From beginning</label>`;
        }
        container.innerHTML = html;

        const topicLabel = container.querySelector('#ps-trigger-topic-label');
        if (topicLabel) {
            const opener = category === 'kafka-events'
                ? () => this.#openKafkaEventsTopicPicker()
                : () => this.#openKafkaRawTopicPicker();
            this.onEvent('click', opener, topicLabel);
        }
        const triggerDisplay = container.querySelector('#ps-trigger-in-picker');
        if (triggerDisplay) {
            const domainInput = container.querySelector('#ps-trigger-in-domain');
            const typeInput = container.querySelector('#ps-trigger-in-mt');
            const multi = category === 'events';
            const render = () => {
                const types = typeInput.value.split(',').filter(Boolean);
                triggerDisplay.innerHTML = domainInput.value && types.length
                    ? this.#esc(`${domainInput.value} / ${types.join(', ')}`)
                    : '<em>Choose message type…</em>';
            };
            const open = () => this.#openSchemaTypePicker({ title: multi ? 'Add event type' : 'Choose producer output' }, ({ domain, message_type }) => {
                // events may carry several types, but only from one domain — switching domain resets the list
                const existing = multi && domainInput.value === domain ? typeInput.value.split(',').filter(Boolean) : [];
                if (multi && !existing.includes(message_type)) existing.push(message_type);
                domainInput.value = domain;
                typeInput.value = multi ? existing.join(',') : message_type;
                render();
            });
            this.onEvent('click', open, triggerDisplay);

            const clearBtn = container.querySelector('#ps-trigger-in-clear');
            if (clearBtn) {
                this.onEvent('click', () => { domainInput.value = ''; typeInput.value = ''; render(); }, clearBtn);
            }
        }

        const sourceLabel = container.querySelector('#ps-trigger-source-label');
        if (sourceLabel) {
            this.onEvent('click', () => this.#openCacheSourcePicker(), sourceLabel);
        }
    }

    async #openCacheSourcePicker() {
        let picker = this.shadowRoot.querySelector('hs-list-picker');
        if (!picker) {
            picker = document.createElement('hs-list-picker');
            this.shadowRoot.appendChild(picker);
        }

        const sources = await this.fetch('/api/sources').catch(() => []);
        const items = (sources || []).map(s => (typeof s === 'string' ? { name: s } : s));

        picker.open({
            title: 'Choose Cache Source',
            tabs: [
                {
                    id: 'sources',
                    label: 'Cache Sources',
                    items,
                    getSearchText: s => [s.name],
                    renderItem: s => ({ title: s.name, subtitle: s.type || '' }),
                    getValue: s => s.name,
                    emptyText: 'No cache sources available',
                },
            ],
            onSelect: (value) => {
                const sd = this.shadowRoot;
                const input = sd.getElementById('ps-trigger-source');
                const label = sd.getElementById('ps-trigger-source-label');
                if (input) input.value = value;
                if (label) label.textContent = value;
            },
        });
    }

    async #openSchemaTypePicker({ title = 'Choose telemetry message type', allowAny = false } = {}, onSelect) {
        let picker = this.shadowRoot.querySelector('hs-list-picker');
        if (!picker) {
            picker = document.createElement('hs-list-picker');
            this.shadowRoot.appendChild(picker);
        }

        await this.#ensureSchemaDomains();
        const domains = this.#schemaExplorer.domains || [];
        const perDomain = await Promise.all(domains.map(async d => ({
            domain: d.name,
            types: await this.#ensureSchemaTypes(d.name),
        })));

        const buildItems = (filterDomain) => {
            const items = [];
            if (allowAny) items.push({ __any: true, domain: '', message_type: '' });
            for (const { domain, types } of perDomain) {
                if (filterDomain && filterDomain !== domain) continue;
                for (const t of types) {
                    items.push({
                        domain,
                        message_type: t.name,
                        sandbox: !!t.sandbox,
                        version: t.version || '',
                    });
                }
            }
            return items;
        };

        const shared = {
            getSearchText: it => it.__any ? ['any', 'all'] : [it.message_type, it.domain],
            renderItem: it => {
                if (it.__any) return { title: '(any)', subtitle: 'no filter — matches every domain and message type' };
                return {
                    title: `${it.domain} / ${it.message_type}`,
                    subtitle: it.version ? `v${it.version}` : '',
                    badges: [it.sandbox ? 'overlay' : 'prod'],
                };
            },
            getValue: it => it,
            emptyText: 'No message types match',
        };

        const tabs = [{ id: '_all', label: 'All', items: buildItems(null), ...shared }];
        for (const { domain, types } of perDomain) {
            if (!types.length) continue;
            tabs.push({ id: domain, label: domain, items: buildItems(domain), ...shared });
        }

        picker.open({
            title,
            tabs,
            onSelect: (value) => {
                if (value?.__any) onSelect({ domain: '', message_type: '', sandbox: false });
                else onSelect({ domain: value.domain, message_type: value.message_type, sandbox: !!value.sandbox });
            },
        });
    }

    // Opens a tabbed picker of fields grouped by schema (trigger input, each declared output, common).
    async #openFieldPicker({ title = 'Choose field' } = {}, onSelect) {
        const sd = this.shadowRoot;
        await this.#ensureSchemaDomains();
        const commonProps = Object.keys(this.#schemaExplorer.common?.properties || {}).sort();

        const lookups = [];
        const triggerCategory = sd.querySelector('input[name="ps-trigger-type"]:checked')?.value;
        if (triggerCategory === 'events' || triggerCategory === 'pipeline') {
            const dom = sd.getElementById('ps-trigger-in-domain')?.value?.trim();
            const mts = (sd.getElementById('ps-trigger-in-mt')?.value || '').split(',').map(t => t.trim()).filter(Boolean);
            for (const mt of mts) {
                if (dom) lookups.push({ label: `Input: ${dom} / ${mt}`, domain: dom, message_type: mt });
            }
        }
        for (const out of this.#getFormOutputs()) {
            lookups.push({ label: `Output: ${out.domain} / ${out.message_type}`, domain: out.domain, message_type: out.message_type });
        }

        const details = await Promise.all(lookups.map(l => this.#ensureSchemaTypeDetail(l.domain, l.message_type)));
        const tabs = [];
        for (let i = 0; i < lookups.length; i++) {
            const fields = Object.keys(details[i]?.properties || {}).sort();
            if (!fields.length) continue;
            tabs.push({
                id: `${lookups[i].domain}::${lookups[i].message_type}::${i}`,
                label: lookups[i].label,
                items: fields.map(name => ({ name })),
                getSearchText: it => [it.name],
                renderItem: it => ({ title: it.name }),
                getValue: it => it.name,
                emptyText: 'No fields',
            });
        }
        if (commonProps.length) {
            tabs.push({
                id: 'common',
                label: 'Common',
                items: commonProps.map(name => ({ name })),
                getSearchText: it => [it.name],
                renderItem: it => ({ title: it.name }),
                getValue: it => it.name,
                emptyText: 'No common fields',
            });
        }
        if (!tabs.length) {
            this.#showAlert('No schemas available for field selection. Configure a trigger input or an emit_telemetry step first.');
            return;
        }

        let picker = this.shadowRoot.querySelector('hs-list-picker');
        if (!picker) {
            picker = document.createElement('hs-list-picker');
            this.shadowRoot.appendChild(picker);
        }
        picker.open({ title, tabs, onSelect: (value) => onSelect(value) });
    }

    #fieldPickerCellHtml(hiddenClass, value = '', placeholder = 'pick field') {
        return `<div class="ps-picker-row ps-field-picker-cell">
            <button type="button" class="ps-input ps-input-sm ps-picker-display ps-field-picker-btn" title="Click to pick a field">${value ? this.#esc(value) : `<em>${this.#esc(placeholder)}</em>`}</button>
            <input type="hidden" class="${this.#esc(hiddenClass)}" value="${this.#esc(value)}" />
        </div>`;
    }

    // Wires a picker button (delegated inside a container) so clicking any .ps-field-picker-btn opens
    // the field picker and populates its sibling hidden input.
    #bindFieldPickerDelegation(container, title = 'Choose field') {
        if (!container) return;
        container.addEventListener('click', (ev) => {
            const btn = ev.target.closest('.ps-field-picker-btn');
            if (!btn || !container.contains(btn)) return;
            const hidden = btn.parentElement?.querySelector('input[type="hidden"]');
            if (!hidden) return;
            this.#openFieldPicker({ title }, (value) => {
                // #openFieldPicker's tabs use `getValue: it => it.name`, so the picker returns a string.
                const name = typeof value === 'string' ? value : (value?.name || '');
                hidden.value = name;
                btn.innerHTML = name ? this.#esc(name) : `<em>pick field</em>`;
            });
        });
    }

    async #openKafkaEventsTopicPicker() {
        let picker = this.shadowRoot.querySelector('hs-list-picker');
        if (!picker) {
            picker = document.createElement('hs-list-picker');
            this.shadowRoot.appendChild(picker);
        }

        const items = RAW_FEED_TOPICS.map(name => ({ name }));

        picker.open({
            title: 'Choose Kafka Events Feed',
            tabs: [
                {
                    id: 'feeds',
                    label: 'Raw Feeds',
                    items,
                    getSearchText: t => [t.name],
                    renderItem: t => ({ title: t.name, subtitle: 'Encrypted, compressed, multi-event-type feed' }),
                    getValue: t => t.name,
                    emptyText: 'No raw feeds configured',
                },
            ],
            onSelect: (value) => this.#applyTopicSelection(value, { showFromBeginning: false }),
        });
    }

    async #openKafkaRawTopicPicker() {
        let picker = this.shadowRoot.querySelector('hs-list-picker');
        if (!picker) {
            picker = document.createElement('hs-list-picker');
            this.shadowRoot.appendChild(picker);
        }

        const [sources, kafkaResult] = await Promise.all([
            this.fetch('/api/sources').catch(() => []),
            this.fetch('/api/kafka/topics').catch(() => ({ topics: [] })),
        ]);
        // Data-source name is not guaranteed to equal its Kafka topic, so we exclude
        // only what we can positively identify: the curated raw-feed list and any
        // exact-name overlap with a data source.
        const sourceNames = new Set((sources || []).map(s => s.name));
        const excluded = new Set(RAW_FEED_TOPICS);
        const topics = (kafkaResult?.topics || [])
            .filter(t => !excluded.has(t))
            .map(name => ({ name, is_cache_source: sourceNames.has(name) }));

        picker.open({
            title: 'Choose Kafka Topic',
            tabs: [
                {
                    id: 'kafka',
                    label: 'Kafka Topics',
                    items: topics,
                    getSearchText: t => [t.name],
                    renderItem: t => ({ title: t.name }),
                    getValue: t => t.name,
                    emptyText: 'No Kafka topics available',
                },
            ],
            onSelect: (value) => this.#applyTopicSelection(value, { showFromBeginning: true }),
        });
    }

    #applyTopicSelection(value, { showFromBeginning }) {
        const topicInput = this.shadowRoot.getElementById('ps-trigger-topic');
        const topicLabel = this.shadowRoot.getElementById('ps-trigger-topic-label');
        if (topicInput) topicInput.value = value;
        if (topicLabel) topicLabel.textContent = value;
        const fromRow = this.shadowRoot.getElementById('ps-from-beginning-row');
        const fromCheckbox = this.shadowRoot.getElementById('ps-trigger-from-beginning');
        if (fromRow) fromRow.classList.toggle('hidden', !showFromBeginning);
        if (!showFromBeginning && fromCheckbox) fromCheckbox.checked = false;
    }

    async #openSourcePicker(targetInput, onSourceSelected) {
        let picker = this.shadowRoot.querySelector('hs-list-picker');
        if (!picker) {
            picker = document.createElement('hs-list-picker');
            this.shadowRoot.appendChild(picker);
        }

        const sources = await this.fetch('/api/sources').catch(() => []);
        // Enrich-able = anything the cache-manager exposes with a primary key we can look up by.
        // Compound sources set is_compound; plain cache-backed tables carry `keys` derived from the schema.
        const cacheSources = (Array.isArray(sources) ? sources : [])
            .filter(s => s.is_compound || (Array.isArray(s.keys) && s.keys.length) || (Array.isArray(s.cache_primary_key) && s.cache_primary_key.length));

        picker.open({
            title: 'Choose Data Source',
            tabs: [
                {
                    id: 'cache',
                    label: 'Cache Sources',
                    items: cacheSources,
                    getSearchText: s => [s.name, s.description, s.type],
                    renderItem: s => ({
                        title: s.name,
                        subtitle: s.description || '',
                        badges: [s.type, s.format, s.is_compound ? 'compound' : ''].filter(Boolean),
                    }),
                    getValue: s => `cache:${s.name}`,
                    emptyText: 'No cache sources available',
                },
            ],
            onSelect: (value, item) => {
                if (targetInput) targetInput.value = value;
                if (onSourceSelected) onSourceSelected(item);
            },
        });
    }

    #addPipelineStep(ref, overrides = {}) {
        if (!ref) return;
        const container = this.shadowRoot.getElementById('ps-pipeline-steps');
        const row = document.createElement('div');
        row.className = 'ps-step-row';
        const hasOverrides = overrides.opts || overrides.concurrency || overrides.batch_size || overrides.batch_ms || overrides.watchdog_ms;
        // Rich per-plugin forms (§4.1's kept-but-unwired block) only make sense when tuning
        // a real imported artifact — the scratch builder stays raw-JSON-only per §3.
        const showConfigure = this.#isTuning && this.#isConfigurableRef(ref);

        // Ref is an immutable label; a hidden input carries the value for #buildConfigFromForm.
        row.innerHTML = `<span class="ps-step-ref" title="${this.#esc(ref)}">${this.#esc(ref)}</span>
            <input type="hidden" class="ps-step-input" value="${this.#esc(ref)}" />
            <label class="ps-step-tap" title="Tap (observe only)"><input type="checkbox" class="ps-step-tap-cb"${overrides.isTap ? ' checked' : ''} />tap</label>
            ${showConfigure ? '<button class="btn btn-sm ps-step-configure" title="Configure plugin">Configure</button>' : ''}
            <button class="btn-icon ps-step-config" title="Step overrides (concurrency, batching, raw opts JSON)"${hasOverrides ? ' style="color:var(--accent-color,#0066cc)"' : ''}>⚙</button>
            <button class="btn-icon ps-step-explain${this.#pipelineExplainSubject ? '' : ' hidden'}" title="Explain this step">🔎</button>
            <button class="btn-icon ps-step-up" title="Move up">↑</button>
            <button class="btn-icon ps-step-down" title="Move down">↓</button>
            <button class="btn-icon ps-step-remove" title="Remove">✕</button>`;

        row._stepOverrides = {
            opts: overrides.opts || null,
            concurrency: overrides.concurrency ?? null,
            batch_size: overrides.batch_size ?? null,
            batch_ms: overrides.batch_ms ?? null,
            watchdog_ms: overrides.watchdog_ms ?? null,
        };

        container.appendChild(row);

        this.onEvent('click', () => row.remove(), row.querySelector('.ps-step-remove'));
        this.onEvent('click', () => this.#openStepConfig(row), row.querySelector('.ps-step-config'));
        this.onEvent('click', () => this.#openPipelineWalkthrough({ focusRow: row }), row.querySelector('.ps-step-explain'));
        const cfgBtn = row.querySelector('.ps-step-configure');
        if (cfgBtn) this.onEvent('click', () => this.#openConfigForm(row), cfgBtn);
        this.onEvent('click', () => {
            const prev = row.previousElementSibling;
            if (prev) container.insertBefore(row, prev);
        }, row.querySelector('.ps-step-up'));
        this.onEvent('click', () => {
            const next = row.nextElementSibling;
            if (next) container.insertBefore(next, row);
        }, row.querySelector('.ps-step-down'));
    }

    #openStepConfig(row) {
        const existing = this.shadowRoot.querySelector('.ps-step-popover');
        if (existing) existing.remove();

        const ov = row._stepOverrides || {};
        const popover = document.createElement('div');
        popover.className = 'ps-step-popover';
        popover.innerHTML = `<div class="ps-popover-content">
            <div class="ps-form-row"><label>concurrency<hs-help-icon key="pipeline.step.concurrency"></hs-help-icon></label><input type="number" class="ps-input ps-input-sm" data-key="concurrency" value="${ov.concurrency ?? ''}" min="1" placeholder="—" /></div>
            <div class="ps-form-row-label">Batching<hs-help-icon key="pipeline.step.batching"></hs-help-icon></div>
            <div class="ps-form-row"><label>batch_size</label><input type="number" class="ps-input ps-input-sm" data-key="batch_size" value="${ov.batch_size ?? ''}" min="1" placeholder="—" /></div>
            <div class="ps-form-row"><label>batch_ms</label><input type="number" class="ps-input ps-input-sm" data-key="batch_ms" value="${ov.batch_ms ?? ''}" min="1" placeholder="—" /></div>
            <div class="ps-form-row"><label>watchdog_ms<hs-help-icon key="pipeline.step.watchdog_ms"></hs-help-icon></label><input type="number" class="ps-input ps-input-sm" data-key="watchdog_ms" value="${ov.watchdog_ms ?? ''}" min="1" placeholder="—" /></div>
            <div class="ps-form-group"><label>opts (JSON)</label><textarea class="ps-input ps-opts-textarea" placeholder="{}" spellcheck="false">${this.#esc(ov.opts ? JSON.stringify(ov.opts, null, 2) : '')}</textarea></div>
            <div class="ps-popover-actions">
                <button class="btn btn-sm ps-popover-apply">Apply</button>
                <button class="btn btn-sm ps-popover-cancel">Cancel</button>
            </div>
        </div>`;
        row.appendChild(popover);

        // Position fixed popover relative to the row
        const rect = row.getBoundingClientRect();
        popover.style.top = `${rect.bottom + 4}px`;
        popover.style.left = `${rect.left + 40}px`;

        popover.querySelector('.ps-popover-apply').onclick = () => {
            const numVal = (key) => {
                const v = parseInt(popover.querySelector(`[data-key="${key}"]`).value, 10);
                return v > 0 ? v : null;
            };
            let opts = null;
            const optsText = popover.querySelector('.ps-opts-textarea').value.trim();
            if (optsText) {
                try { opts = JSON.parse(optsText); } catch { /* keep null */ }
            }
            row._stepOverrides = {
                concurrency: numVal('concurrency'), batch_size: numVal('batch_size'),
                batch_ms: numVal('batch_ms'), watchdog_ms: numVal('watchdog_ms'), opts,
            };
            const hasAny = Object.values(row._stepOverrides).some(v => v !== null);
            row.querySelector('.ps-step-config').style.color = hasAny ? 'var(--accent-color,#0066cc)' : '';
            popover.remove();
        };
        popover.querySelector('.ps-popover-cancel').onclick = () => popover.remove();
    }

    #buildConfigFromForm() {
        const sd = this.shadowRoot;
        const nameInput = sd.getElementById('ps-form-name');
        const name = nameInput.value.trim();
        if (!name || !PIPELINE_NAME_RE.test(name)) {
            throw new Error('Name must be alphanumeric with . - _ allowed; start/end with alphanumeric');
        }

        const namespace = this.#getEffectiveNamespace() || '';
        if (!namespace || !NAMESPACE_RE.test(namespace)) {
            throw new Error('Active workspace namespace is invalid. Choose a valid namespace via the header selector.');
        }

        const uiCategory = sd.querySelector('input[name="ps-trigger-type"]:checked')?.value || 'events';
        const elmType = UI_TO_ELM_TRIGGER[uiCategory] || 'kafka';
        const trigger = { type: elmType };

        if (uiCategory === 'kafka-events' || uiCategory === 'kafka-raw') {
            const topicInput = sd.getElementById('ps-trigger-topic');
            trigger.topic = topicInput?.value?.trim() || '';
            if (!trigger.topic) throw new Error('Kafka topic is required');
            if (sd.getElementById('ps-trigger-from-beginning')?.checked) trigger.fromBeginning = true;
        } else if (uiCategory === 'schedule') {
            trigger.cron = sd.getElementById('ps-trigger-cron')?.value?.trim() || '';
            if (!trigger.cron) throw new Error('Cron expression is required');
            const tz = sd.getElementById('ps-trigger-tz')?.value?.trim();
            if (tz) trigger.timezone = tz;
        } else if (uiCategory === 'api') {
            trigger.timeout = parseInt(sd.getElementById('ps-trigger-timeout')?.value, 10) || 30000;
        } else if (uiCategory === 'events') {
            const dom = sd.getElementById('ps-trigger-in-domain')?.value?.trim() || '';
            const types = (sd.getElementById('ps-trigger-in-mt')?.value || '').split(',').map(t => t.trim()).filter(Boolean);
            if (!dom || !types.length) throw new Error('Events trigger requires at least one message type');
            trigger.domain = dom;
            trigger.message_type = types.length === 1 ? types[0] : types;
        } else if (uiCategory === 'state') {
            const source = sd.getElementById('ps-trigger-source')?.value?.trim() || '';
            if (!source) throw new Error('State trigger requires a cache source');
            trigger.source = source;
            if (sd.getElementById('ps-trigger-from-beginning')?.checked) trigger.fromBeginning = true;
        } else if (uiCategory === 'pipeline') {
            const dom = sd.getElementById('ps-trigger-in-domain')?.value?.trim() || '';
            const mt = sd.getElementById('ps-trigger-in-mt')?.value?.trim() || '';
            if (!dom || !mt) throw new Error('Pipeline trigger requires a producer output');
            const input = { domain: dom, message_type: mt };
            if (sd.getElementById('ps-trigger-in-subprod')?.checked) input.subscribe_production = true;
            trigger.inputs = [input];
        }

        const pipeline = [];
        for (const row of sd.querySelectorAll('.ps-step-row')) {
            const ref = row.querySelector('.ps-step-input')?.value?.trim();
            if (!ref) continue;
            const isTap = row.querySelector('.ps-step-tap-cb')?.checked;
            const ov = row._stepOverrides || {};
            const hasOverrides = ov.opts || ov.concurrency || ov.batch_size || ov.batch_ms || ov.watchdog_ms;

            if (isTap) {
                pipeline.push({ tap: [hasOverrides ? this.#buildAnnotatedStep(ref, ov) : ref] });
            } else if (hasOverrides) {
                pipeline.push(this.#buildAnnotatedStep(ref, ov));
            } else {
                pipeline.push(ref);
            }
        }
        if (!pipeline.length) throw new Error('At least one pipeline step is required');

        const config = { namespace, name, trigger, pipeline };
        const hwm = parseInt(sd.getElementById('ps-form-hwm')?.value, 10);
        if (hwm && hwm !== 16) config.highWaterMark = hwm;
        if (sd.getElementById('ps-form-serial')?.checked) config.serialPipeline = true;

        const outputs = this.#deriveOutputsFromSteps(pipeline);
        if (outputs.length) config.outputs = outputs;

        return config;
    }

    #buildAnnotatedStep(ref, ov) {
        const step = { plugin: ref };
        if (ov.opts) step.opts = ov.opts;
        if (ov.concurrency) step.concurrency = ov.concurrency;
        if (ov.batch_size) step.batch_size = ov.batch_size;
        if (ov.batch_ms) step.batch_ms = ov.batch_ms;
        if (ov.watchdog_ms) step.watchdog_ms = ov.watchdog_ms;
        return step;
    }

    async #validatePipeline() {
        const resultEl = this.shadowRoot.getElementById('ps-form-result');
        let config;
        try {
            config = this.#buildConfigFromForm();
        } catch (err) {
            resultEl.textContent = err.message;
            resultEl.className = 'ps-form-result invalid';
            return;
        }

        await this.#withLoading(async () => {
            try {
                const res = await this.fetch(`${this.#baseUrl}/pipelines/validate`, {
                    method: 'POST',
                    payload: { namespace: config.namespace, config },
                });
                if (res.valid) {
                    resultEl.textContent = `Valid ✓  Plugins loaded: ${(res.plugins_loaded || []).join(', ')}`;
                    resultEl.className = 'ps-form-result valid';
                } else {
                    resultEl.textContent = `Invalid:\n${(res.errors || []).join('\n')}`;
                    resultEl.className = 'ps-form-result invalid';
                }
            } catch (err) {
                resultEl.textContent = `Validation error: ${err.message || err}`;
                resultEl.className = 'ps-form-result invalid';
            }
        });
    }

    async #savePipeline() {
        const resultEl = this.shadowRoot.getElementById('ps-form-result');
        let config;
        try {
            config = this.#buildConfigFromForm();
        } catch (err) {
            resultEl.textContent = err.message;
            resultEl.className = 'ps-form-result invalid';
            return;
        }

        await this.#withLoading(async () => {
            try {
                const validation = await this.fetch(`${this.#baseUrl}/pipelines/validate`, {
                    method: 'POST',
                    payload: { namespace: config.namespace, config },
                });
                if (!validation.valid) {
                    const errors = (validation.errors || []).join('\n') || 'Pipeline validation failed';
                    resultEl.textContent = errors;
                    resultEl.className = 'ps-form-result invalid';
                    return;
                }

                if (this.#editingPipeline) {
                    const originalName = this.#editingPipeline.name;
                    if (originalName !== config.name) {
                        const ok = await this.#confirm(`Rename "${originalName}" to "${config.name}"?\n\nThis will delete the existing pipeline and create a new one. Logs and stats history from the previous name will be lost. The new pipeline will start in stopped state.`);
                        if (!ok) return;
                        await this.fetch(`${this.#baseUrl}/pipelines`, {
                            method: 'POST',
                            payload: config,
                        });
                        await this.fetch(`${this.#baseUrl}/pipelines/${encodeURIComponent(originalName)}`, {
                            method: 'DELETE',
                        });
                    } else {
                        await this.fetch(`${this.#baseUrl}/pipelines/${encodeURIComponent(config.name)}`, {
                            method: 'PUT',
                            payload: config,
                        });
                    }
                } else {
                    await this.fetch(`${this.#baseUrl}/pipelines`, {
                        method: 'POST',
                        payload: config,
                    });
                }
                this.#formSnapshot = null;
                this.#backToList();
            } catch (err) {
                resultEl.textContent = `Save failed: ${err.message || err}`;
                resultEl.className = 'ps-form-result invalid';
            }
        });
    }

    // ---- Plugin Manager ----

    #showPluginView() {
        const namespaceInput = this.shadowRoot.getElementById('ps-plugin-namespace');
        namespaceInput.value = this.#getEffectiveNamespace();
        namespaceInput.disabled = true;
        this.#showView('plugin');
        this.#fetchCatalogForPluginView();
        if (this.#isAdmin) this.#fetchSubscriptions();
    }

    async #fetchCatalogForPluginView() {
        await this.#fetchCatalog();
        this.#renderPluginList();
    }

    // ---- Topic subscriptions (developer-admin only) ----
    // Unused subscriptions are the expected steady state, not a problem — the
    // backend never unsubscribes automatically so pipeline delete/restart stays
    // instant. This panel is a neutral, optional "reclaim broker bandwidth"
    // convenience, not a cleanup/maintenance task.

    async #fetchSubscriptions() {
        try {
            const res = await this.fetch(`${this.#baseUrl}/pipelines/subscriptions`);
            this.#renderSubscriptions(res);
        } catch (err) {
            Logger.error('Failed to fetch topic subscriptions: ', err);
        }
    }

    #renderSubscriptions(data) {
        const countEl = this.shadowRoot.getElementById('ps-subscriptions-count');
        const listEl = this.shadowRoot.getElementById('ps-subscriptions-list');
        const pruneBtn = this.shadowRoot.getElementById('ps-subscriptions-prune-btn');
        const unused = data?.unused || [];
        const count = data?.unused_count ?? unused.length;
        if (countEl) countEl.textContent = `Topic subscriptions: ${count} unused`;
        if (listEl) {
            listEl.innerHTML = unused.length
                ? unused.map(t => `<li>${this.#esc(t)}</li>`).join('')
                : '<li><em>None right now</em></li>';
        }
        pruneBtn?.setDisabled(!unused.length);
    }

    // Prune costs one consumer rebalance (~7s) and the request doesn't return
    // until it completes — #withLoading's overlay covers the "show progress
    // and disable while it runs" requirement without a bespoke spinner.
    async #pruneSubscriptions() {
        await this.#withLoading(async () => {
            try {
                const res = await this.fetch(`${this.#baseUrl}/pipelines/subscriptions/prune`, { method: 'POST' });
                this.#renderSubscriptions(res);
            } catch (err) {
                this.#showAlert(`Failed to reclaim broker bandwidth: ${err.message || err}`);
            }
        });
    }

    #renderPluginList() {
        const container = this.shadowRoot.getElementById('ps-plugin-list');
        const cats = Object.keys(this.#catalog);
        if (!cats.length) {
            container.innerHTML = '<div class="ps-empty-state">No plugins available</div>';
            return;
        }

        let html = '';
        for (const cat of cats) {
            const modules = this.#catalog[cat] || [];
            if (!modules.length) continue;
            html += `<div class="ps-plugin-category-header">${this.#esc(cat)}</div>`;
            for (const mod of modules) {
                const isCustom = cat !== 'core';
                const promoteBtn = this.#isAdmin && isCustom ? `<button class="btn btn-sm" data-action="promote" data-cat="${this.#esc(cat)}" data-plugin="${this.#esc(mod.module)}">Promote</button>` : '';
                const demoteBtn = this.#isAdmin ? `<button class="btn btn-sm" data-action="demote" data-cat="${this.#esc(cat)}" data-plugin="${this.#esc(mod.module)}">Demote</button>` : '';
                const deleteBtn = this.#isAdmin && isCustom ? `<button class="btn btn-sm btn-danger" data-action="delete" data-cat="${this.#esc(cat)}" data-plugin="${this.#esc(mod.module)}">Delete</button>` : '';

                const funcsHtml = (mod.functions || []).map(f => {
                    const badges = [
                        f.async ? 'async' : '',
                        f.stream ? 'stream' : '',
                        f.multi ? 'multi' : '',
                        f.configurable ? 'configurable' : '',
                    ].filter(Boolean).map(b => `<span class="ps-fn-badge">${b}</span>`).join('');
                    const desc = f.description ? `<span class="ps-fn-desc">${this.#esc(f.description)}</span>` : '';
                    const helpIcon = f.help_key ? `<hs-help-icon key="${this.#esc(f.help_key)}"></hs-help-icon>` : '';
                    return `<div class="ps-fn-row"><span class="ps-fn-name">${this.#esc(f.name)}</span>${badges}${helpIcon}${desc}</div>`;
                }).join('');

                html += `<div class="ps-plugin-card">
                    <div class="ps-plugin-info">
                        <div class="ps-plugin-name">${this.#esc(cat)}:${this.#esc(mod.module)}</div>
                        <div class="ps-plugin-label">${this.#esc(mod.label || mod.module)}</div>
                        ${mod.description ? `<div class="ps-plugin-desc">${this.#esc(mod.description)}</div>` : ''}
                        ${funcsHtml ? `<div class="ps-fn-list">${funcsHtml}</div>` : ''}
                    </div>
                    <div class="ps-plugin-actions">
                        <button class="btn btn-sm" data-action="validate" data-cat="${this.#esc(cat)}" data-plugin="${this.#esc(mod.module)}">Validate</button>
                        ${promoteBtn}
                        ${demoteBtn}
                        ${deleteBtn}
                    </div>
                </div>`;
            }
        }
        container.innerHTML = html;

        for (const btn of container.querySelectorAll('[data-action]')) {
            this.onEvent('click', () => {
                this.#handlePluginAction(btn.dataset.action, btn.dataset.cat, btn.dataset.plugin);
            }, btn);
        }
    }

    async #handlePluginAction(action, category, moduleName) {
        const pluginPath = `${encodeURIComponent(category)}/${encodeURIComponent(moduleName)}`;
        if (action === 'delete') {
            if (!await this.#confirm(`Delete plugin "${category}:${moduleName}"?`)) return;
            await this.#withLoading(async () => {
                try {
                    await this.fetch(`${this.#baseUrl}/plugins/${pluginPath}`, { method: 'DELETE' });
                } catch (err) {
                    Logger.error('Plugin delete failed: ', err);
                }
                await this.#fetchCatalogForPluginView();
            });
        } else if (action === 'validate') {
            await this.#withLoading(async () => {
                try {
                    const namespace = this.shadowRoot.getElementById('ps-plugin-namespace')?.value?.trim() || '';
                    if (namespace && !NAMESPACE_RE.test(namespace)) {
                        this.#showAlert('Namespace must start with a letter, end with a letter or number, and contain only letters, numbers, . - _');
                        return;
                    }
                    const validatePath = namespace
                        ? `${pluginPath}/namespaces/${encodeURIComponent(namespace)}/validate`
                        : `${pluginPath}/validate`;
                    const res = await this.fetch(`${this.#baseUrl}/plugins/${validatePath}`, { method: 'POST' });
                    let msg;
                    if (res.valid) {
                        msg = `Plugin "${category}:${moduleName}" is valid.\n\nExports:\n${(res.exports || []).map(e => `  ${e.name} — ${e.description || e.type}${e.async ? ' [async]' : ''}`).join('\n')}`;
                    } else {
                        msg = `Plugin "${category}:${moduleName}" has issues:\n${(res.warnings || []).join('\n')}`;
                    }
                    this.#showAlert(msg);
                } catch (err) {
                    this.#showAlert(`Validation failed: ${err.message || err}`);
                }
            });
        } else if (action === 'promote') {
            const label = prompt(`Label for "${moduleName}" (e.g. "JSON Input"):`);
            if (!label?.trim()) return;
            const description = prompt(`Description for "${moduleName}":`);
            if (!description?.trim()) return;
            await this.#withLoading(async () => {
                try {
                    await this.fetch(`${this.#baseUrl}/builder/catalog/promote`, {
                        method: 'POST',
                        payload: { module: moduleName, category, label: label.trim(), description: description.trim() },
                    });
                    await this.#fetchCatalogForPluginView();
                } catch (err) {
                    this.#showAlert(`Promotion failed: ${err.message || err}`);
                }
            });
        } else if (action === 'demote') {
            if (!await this.#confirm(`Demote plugin "${moduleName}" from the builder catalog?`)) return;
            await this.#withLoading(async () => {
                try {
                    await this.fetch(`${this.#baseUrl}/builder/catalog/${encodeURIComponent(moduleName)}`, { method: 'DELETE' });
                    await this.#fetchCatalogForPluginView();
                } catch (err) {
                    this.#showAlert(`Demote failed: ${err.message || err}`);
                }
            });
        }
    }

    #bindPluginDropZone() {
        const sd = this.shadowRoot;
        const zone = sd.getElementById('ps-plugin-drop-zone');
        const fileInput = sd.getElementById('ps-plugin-file-input');
        if (!zone || !fileInput) return;

        this.onEvent('click', () => fileInput.click(), sd.getElementById('ps-plugin-upload-btn'));
        this.onEvent('change', () => {
            if (fileInput.files?.[0]) this.#uploadPlugin(fileInput.files[0]);
            fileInput.value = '';
        }, fileInput);

        this.onEvent('dragover', (_, ev) => {
            ev.preventDefault();
            zone.classList.add('drag-over');
        }, zone);
        this.onEvent('dragleave', () => zone.classList.remove('drag-over'), zone);
        this.onEvent('drop', (_, ev) => {
            ev.preventDefault();
            zone.classList.remove('drag-over');
            const file = ev.dataTransfer?.files?.[0];
            if (file) this.#uploadPlugin(file);
        }, zone);
    }

    async #uploadPlugin(file) {
        const moduleName = file.name.replace(/\.js$/i, '').replace(/[^a-z0-9_-]/gi, '_').toLowerCase();
        if (!moduleName) return;

        const namespace = this.shadowRoot.getElementById('ps-plugin-namespace')?.value?.trim() || '';
        if (namespace && !NAMESPACE_RE.test(namespace)) {
            this.#showAlert('Namespace must start with a letter, end with a letter or number, and contain only letters, numbers, . - _');
            return;
        }

        await this.#withLoading(async () => {
            const body = await file.arrayBuffer();
            const customModules = this.#catalog['custom'] || [];
            const existing = customModules.find(m => m.module === moduleName);
            const method = namespace || existing ? 'PUT' : 'POST';
            const pluginPath = namespace
                ? `plugins/custom/${encodeURIComponent(moduleName)}/namespaces/${encodeURIComponent(namespace)}`
                : `plugins/custom/${encodeURIComponent(moduleName)}`;

            try {
                const token = Auth.getToken();
                const res = await fetch(`/dashboard${this.#baseUrl}/${pluginPath}`, {
                    method,
                    headers: {
                        'Content-Type': 'application/octet-stream',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                    },
                    body,
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    throw new Error(err.error || `Upload failed (${res.status})`);
                }
                await this.#fetchCatalogForPluginView();
            } catch (err) {
                Logger.error('Plugin upload failed: ', err);
                this.#showAlert(`Upload failed: ${err.message || err}`);
            }
        });
    }

    // ---- Configurable Plugin Forms ----
    // Re-wired to the step row's "Configure" button in Tuning mode only (§ 4.1's Tuning
    // surface) — the capability index's plugin `options[]` is name+help text only, no
    // type/required/range (unlike kernel params), so a truly generic index-driven
    // replacement isn't buildable yet; these hand-coded forms remain the right choice
    // until the index is enriched (reported back separately).

    #isConfigurableRef(ref) {
        if (!ref) return null;
        const base = ref.replace(/\(.*\)$/, '');
        // Check catalog data for configurable flag
        for (const [cat, modules] of Object.entries(this.#catalog)) {
            for (const mod of (modules || [])) {
                const moduleName = mod.module || mod.name || 'unknown';
                for (const fn of (mod.functions || [])) {
                    const fnRef = this.#normalizeCatalogRef(cat, moduleName, fn);
                    if (fnRef === base && fn.configurable) return base;
                }
            }
        }
        // Fallback for known configurable refs not yet in catalog
        const CONFIGURABLE = [
            'pipeline_ops.map', 'pipeline_ops.filter', 'pipeline_ops.flatMap', 'pipeline_ops.enrich', 'pipeline_ops.window',
            'core:transforms.map', 'core:transforms.filter', 'core:transforms.flatMap', 'core:transforms.enrich', 'core:transforms.window',
            'core:crypto.decrypt', 'core:crypto.encrypt',
            'core:compression.unzip', 'core:compression.zip',
            'core:debug.print',
            'core:schema.apply_defaults', 'core:schema.validate',
            'infrastructure:kafka.publish',
            'json_output.emit_telemetry', 'infrastructure:json_output.emit_telemetry',
        ];
        return CONFIGURABLE.includes(base) ? base : null;
    }

    #parseConfigurableRef(ref) {
        const m = ref.match(/^([^(]+)\((.+)\)$/s);
        if (!m) return { base: ref, config: {} };
        try { return { base: m[1], config: JSON.parse(m[2]) }; }
        catch { return { base: m[1], config: {} }; }
    }

    #openConfigForm(row) {
        const existing = this.shadowRoot.querySelector('.ps-confirm-overlay.ps-cfg-overlay');
        if (existing) existing.remove();

        const ref = row.querySelector('.ps-step-input')?.value?.trim() || '';
        const { base, config } = this.#parseConfigurableRef(ref);
        const fnName = base.split('.').pop();

        const overlay = document.createElement('div');
        overlay.className = 'ps-confirm-overlay ps-cfg-overlay';

        const panel = document.createElement('div');
        panel.className = 'ps-cfg-modal';

        let html = `<div class="ps-cfg-header"><strong>${this.#esc(base)}</strong> configuration<button class="btn-icon ps-cfg-close" title="Close">✕</button></div><div class="ps-cfg-body">`;

        if (fnName === 'map') html += this.#renderMapForm(config);
        else if (fnName === 'filter') html += this.#renderFilterForm(config);
        else if (fnName === 'flatMap') html += this.#renderFlatMapForm(config);
        else if (fnName === 'enrich') html += this.#renderEnrichForm(config);
        else if (fnName === 'window') html += this.#renderWindowForm(config);
        else if (fnName === 'decrypt' || fnName === 'encrypt') html += this.#renderCryptoForm(config, fnName);
        else if (fnName === 'unzip' || fnName === 'zip') html += this.#renderCompressionForm(config, fnName);
        else if (fnName === 'print') html += this.#renderDebugForm(config);
        else if (fnName === 'apply_defaults') html += this.#renderApplyDefaultsForm(config);
        else if (fnName === 'validate') html += this.#renderValidateForm(config);
        else if (fnName === 'publish') html += this.#renderKafkaPublishForm(config);
        else if (fnName === 'emit_telemetry') html += this.#renderEmitTelemetryForm(config);

        html += `</div><div class="ps-cfg-footer"><button class="btn btn-primary btn-sm ps-cfg-apply">Apply</button><button class="btn btn-sm ps-cfg-cancel">Cancel</button></div>`;
        panel.innerHTML = html;

        overlay.appendChild(panel);
        this.shadowRoot.appendChild(overlay);

        panel.querySelector('.ps-cfg-close').onclick = () => overlay.remove();
        panel.querySelector('.ps-cfg-cancel').onclick = () => overlay.remove();
        panel.querySelector('.ps-cfg-apply').onclick = () => {
            try {
            let cfg;
            if (fnName === 'map') cfg = this.#readMapForm(panel);
            else if (fnName === 'filter') cfg = this.#readFilterForm(panel);
            else if (fnName === 'flatMap') cfg = this.#readFlatMapForm(panel);
            else if (fnName === 'enrich') cfg = this.#readEnrichForm(panel);
            else if (fnName === 'window') cfg = this.#readWindowForm(panel);
            else if (fnName === 'decrypt' || fnName === 'encrypt') cfg = this.#readCryptoForm(panel);
            else if (fnName === 'unzip' || fnName === 'zip') cfg = this.#readCompressionForm(panel);
            else if (fnName === 'print') cfg = this.#readDebugForm(panel);
            else if (fnName === 'apply_defaults') cfg = this.#readApplyDefaultsForm(panel);
            else if (fnName === 'validate') cfg = this.#readValidateForm(panel);
            else if (fnName === 'publish') cfg = this.#readKafkaPublishForm(panel);
            else if (fnName === 'emit_telemetry') cfg = this.#readEmitTelemetryForm(panel);
            if (cfg && Object.keys(cfg).length) {
                row.querySelector('.ps-step-input').value = `${base}(${JSON.stringify(cfg)})`;
            } else if (cfg) {
                row.querySelector('.ps-step-input').value = base;
            }
            const newRef = row.querySelector('.ps-step-input').value;
            const refLabel = row.querySelector('.ps-step-ref');
            if (refLabel) {
                refLabel.textContent = newRef;
                refLabel.title = newRef;
            }
            overlay.remove();
            } catch (err) { this.#showAlert(err.message); }
        };

        this.#bindConfigFormEvents(panel, fnName, config);
    }

    #bindConfigFormEvents(panel, fnName, existingCfg) {
        if (fnName === 'map') {
            panel.querySelector('.ps-cfg-add-rename')?.addEventListener('click', () => this.#addRenameRow(panel.querySelector('.ps-cfg-rename-rows')));
            panel.querySelector('.ps-cfg-add-set')?.addEventListener('click', () => this.#addSetRow(panel.querySelector('.ps-cfg-set-rows'), '', '', false));
            panel.querySelector('.ps-cfg-add-omit')?.addEventListener('click', () => this.#addPickerListItem(panel.querySelector('.ps-cfg-omit-list')));
            panel.querySelector('.ps-cfg-add-pick')?.addEventListener('click', () => this.#addPickerListItem(panel.querySelector('.ps-cfg-pick-list')));
            this.#bindFieldPickerDelegation(panel.querySelector('.ps-cfg-rename-rows'), 'Choose source field');
            this.#bindFieldPickerDelegation(panel.querySelector('.ps-cfg-omit-list'), 'Choose field to omit');
            this.#bindFieldPickerDelegation(panel.querySelector('.ps-cfg-pick-list'), 'Choose field to keep');
        } else if (fnName === 'filter') {
            panel.querySelector('.ps-cfg-add-rule')?.addEventListener('click', () => this.#addFilterRule(panel.querySelector('.ps-cfg-filter-rules')));
            this.#bindFieldPickerDelegation(panel.querySelector('.ps-cfg-filter-rules'), 'Choose filter field');
            // Wire up the initial seeded row(s) rendered from the template — op-select and dyn-cb need handlers.
            for (const row of panel.querySelectorAll('.ps-cfg-filter-rules .ps-cfg-filter-rule')) {
                const opSel = row.querySelector('.ps-cfg-rule-op');
                const valIn = row.querySelector('.ps-cfg-rule-value');
                const dynCb = row.querySelector('.ps-cfg-rule-dyn input');
                const vfWrap = row.querySelector('.ps-cfg-rule-vfield-wrap');
                if (opSel) opSel.onchange = () => { valIn.disabled = opSel.value === 'exists' || opSel.value === 'not_exists'; };
                if (dynCb) dynCb.onchange = () => { vfWrap.classList.toggle('hidden', !dynCb.checked); };
                row.querySelector('.ps-cfg-row-remove').onclick = () => row.remove();
            }
        } else if (fnName === 'flatMap') {
            panel.querySelector('.ps-cfg-add-match')?.addEventListener('click', () => this.#addKvRow(panel.querySelector('.ps-cfg-match-rows'), '', ''));
            panel.querySelector('.ps-cfg-join-add-manual')?.addEventListener('click', () => this.#addManualJoinField(panel, existingCfg));
            const srcInput = panel.querySelector('.ps-cfg-source');
            const scanWarn = panel.querySelector('.ps-cfg-scan-warning');
            const dbWarn = panel.querySelector('.ps-cfg-db-warning');
            panel.querySelector('.ps-cfg-source-browse')?.addEventListener('click', () => this.#openSourcePicker(srcInput, (item) => this.#populateJoinFieldsForSource(panel, `cache:${item.name}`, existingCfg)));
            if (srcInput) {
                srcInput.addEventListener('input', () => {
                    const v = srcInput.value.trim();
                    dbWarn?.classList.toggle('hidden', !v.startsWith('db:'));
                });
                srcInput.addEventListener('change', () => this.#populateJoinFieldsForSource(panel, srcInput.value.trim(), existingCfg));
                // If editing an existing config with a source already set, load its fields
                if (srcInput.value.trim()) this.#populateJoinFieldsForSource(panel, srcInput.value.trim(), existingCfg);
            }
            const scanCb = panel.querySelector('.ps-cfg-allow-scan');
            if (scanCb && scanWarn) {
                scanCb.addEventListener('change', () => scanWarn.classList.toggle('hidden', !scanCb.checked));
            }
        } else if (fnName === 'enrich') {
            const srcInput = panel.querySelector('.ps-cfg-source');
            const srcLabel = panel.querySelector('.ps-cfg-source-label');
            const openSourcePicker = () => this.#openSourcePicker(srcInput, () => {
                if (srcLabel) srcLabel.textContent = srcInput.value;
                this.#populateEnrichSource(panel, srcInput.value, existingCfg);
            });
            if (srcLabel) srcLabel.addEventListener('click', openSourcePicker);
            panel.querySelector('.ps-cfg-join-add-manual')?.addEventListener('click', () => this.#addManualJoinField(panel, existingCfg));
            // If editing an existing config with a source already set, load its metadata
            if (srcInput?.value?.trim()) this.#populateEnrichSource(panel, srcInput.value, existingCfg);
        } else if (fnName === 'window') {
            panel.querySelector('.ps-cfg-add-wfield')?.addEventListener('click', () => this.#addListItem(panel.querySelector('.ps-cfg-wfield-list'), ''));
            const mergeSel = panel.querySelector('.ps-cfg-merge-strategy');
            const mfRow = panel.querySelector('.ps-cfg-merge-field-row');
            if (mergeSel && mfRow) {
                mergeSel.addEventListener('change', () => mfRow.classList.toggle('hidden', mergeSel.value !== 'concat_field'));
            }
            const journalRadios = panel.querySelectorAll('input[name="ps-cfg-journal-type"]');
            const customRow = panel.querySelector('.ps-cfg-journal-custom');
            for (const r of journalRadios) {
                r.addEventListener('change', () => customRow?.classList.toggle('hidden', r.value !== 'custom' || !r.checked));
            }
            const compactCb = panel.querySelector('.ps-cfg-compact-enable');
            const compactSection = panel.querySelector('.ps-cfg-compact-section');
            if (compactCb && compactSection) {
                compactCb.addEventListener('change', () => compactSection.classList.toggle('hidden', !compactCb.checked));
            }
            const tsModeSel = panel.querySelector('.ps-cfg-compact-tsmode');
            if (tsModeSel) {
                tsModeSel.addEventListener('change', () => {
                    panel.querySelector('.ps-cfg-compact-computed')?.classList.toggle('hidden', tsModeSel.value !== 'computed');
                    panel.querySelector('.ps-cfg-compact-field-ts')?.classList.toggle('hidden', tsModeSel.value !== 'field');
                    panel.querySelector('.ps-cfg-compact-fixed')?.classList.toggle('hidden', tsModeSel.value !== 'fixed_interval');
                });
            }
            const storeCb = panel.querySelector('.ps-cfg-compact-store-enable');
            const storeSection = panel.querySelector('.ps-cfg-compact-store-section');
            if (storeCb && storeSection) {
                storeCb.addEventListener('change', () => storeSection.classList.toggle('hidden', !storeCb.checked));
            }
        } else if (fnName === 'validate') {
            panel.querySelector('.ps-cfg-add-reqfield')?.addEventListener('click', () => this.#addListItem(panel.querySelector('.ps-cfg-reqfield-list'), ''));
        } else if (fnName === 'print') {
            panel.querySelector('.ps-cfg-add-printfield')?.addEventListener('click', () => this.#addListItem(panel.querySelector('.ps-cfg-printfield-list'), ''));
        } else if (fnName === 'emit_telemetry') {
            const btn = panel.querySelector('.ps-cfg-emit-picker');
            if (btn) {
                btn.addEventListener('click', () => this.#openSchemaTypePicker({ title: 'Choose output message type' }, ({ domain, message_type }) => {
                    panel.querySelector('.ps-cfg-emit-domain').value = domain;
                    panel.querySelector('.ps-cfg-emit-mt').value = message_type;
                    btn.innerHTML = domain && message_type
                        ? this.#esc(`${domain} / ${message_type}`)
                        : '<em>Choose message type…</em>';
                }));
            }
        }
    }

    // -- Map form --
    #renderMapForm(cfg) {
        let html = '<div class="ps-cfg-section"><label>Rename fields</label><div class="ps-cfg-rename-rows">';
        for (const [k, v] of Object.entries(cfg.rename || {})) html += this.#renameKvRowHtml(k, v);
        html += '</div><button class="link-btn ps-cfg-add-rename">+ rename</button></div>';

        html += '<div class="ps-cfg-section"><label>Set fields</label><div class="ps-cfg-set-rows">';
        for (const [k, v] of Object.entries(cfg.set || {})) {
            const isDynamic = typeof v === 'string' && v.startsWith('${') && v.endsWith('}');
            html += this.#setRowHtml(k, isDynamic ? v.slice(2, -1) : String(v), isDynamic);
        }
        html += '</div><button class="link-btn ps-cfg-add-set">+ set field</button></div>';

        html += `<div class="ps-cfg-section"><label>merge_from</label><input type="text" class="ps-input ps-input-sm ps-cfg-merge-from" value="${this.#esc(cfg.merge_from || '')}" placeholder="field.path" /></div>`;

        html += '<div class="ps-cfg-section"><label>Omit fields</label><div class="ps-cfg-omit-list">';
        for (const f of (cfg.omit || [])) html += this.#pickerListItemHtml(f);
        html += '</div><button class="link-btn ps-cfg-add-omit">+ omit</button></div>';

        html += '<div class="ps-cfg-section"><label>Pick fields (allowlist)</label><div class="ps-cfg-pick-list">';
        for (const f of (cfg.pick || [])) html += this.#pickerListItemHtml(f);
        html += '</div><button class="link-btn ps-cfg-add-pick">+ pick</button></div>';

        html += `<div class="ps-cfg-section ps-cfg-toggles">
            <label><input type="checkbox" class="ps-cfg-flatten" ${cfg.flatten ? 'checked' : ''} /> Flatten</label>
            <label><input type="checkbox" class="ps-cfg-inflate" ${cfg.inflate ? 'checked' : ''} /> Inflate</label>
        </div>`;
        return html;
    }

    #readMapForm(panel) {
        const cfg = {};
        const rename = {};
        for (const row of panel.querySelectorAll('.ps-cfg-rename-rows .ps-cfg-rename-row')) {
            const from = row.querySelector('.ps-cfg-rename-from')?.value?.trim();
            const to = row.querySelector('.ps-cfg-rename-to')?.value?.trim();
            if (from) rename[from] = to || '';
        }
        if (Object.keys(rename).length) cfg.rename = rename;
        const set = {};
        for (const row of panel.querySelectorAll('.ps-cfg-set-row')) {
            const k = row.querySelector('.ps-cfg-set-key')?.value?.trim();
            const v = row.querySelector('.ps-cfg-set-val')?.value?.trim();
            const dyn = row.querySelector('.ps-cfg-set-dyn')?.checked;
            if (k && v) set[k] = dyn ? `\${${v}}` : v;
        }
        if (Object.keys(set).length) cfg.set = set;
        const mf = panel.querySelector('.ps-cfg-merge-from')?.value?.trim();
        if (mf) cfg.merge_from = mf;
        const omit = this.#readPickerListItems(panel.querySelector('.ps-cfg-omit-list'));
        if (omit.length) cfg.omit = omit;
        const pick = this.#readPickerListItems(panel.querySelector('.ps-cfg-pick-list'));
        if (pick.length) cfg.pick = pick;
        if (panel.querySelector('.ps-cfg-flatten')?.checked) cfg.flatten = true;
        if (panel.querySelector('.ps-cfg-inflate')?.checked) cfg.inflate = true;
        return cfg;
    }

    // -- Filter form --
    #renderFilterForm(cfg) {
        // Merge include+exclude into a single ordered list; each rule carries an "invert" flag.
        // Include rules are the common case (keep messages matching); invert flips to drop.
        const rules = [
            ...(cfg.include || []).map(r => ({ ...r, invert: false })),
            ...(cfg.exclude || []).map(r => ({ ...r, invert: true })),
        ];
        if (!rules.length) rules.push({});
        return `<div class="ps-cfg-section">
            <label>Rules <span class="ps-form-hint" style="font-weight:normal">(default is <strong>keep if all match</strong>; check <em>drop</em> on a rule to invert it)</span></label>
            <div class="ps-cfg-filter-rules">${rules.map(r => this.#filterRuleHtml(r)).join('')}</div>
            <button class="link-btn ps-cfg-add-rule">+ add rule</button>
        </div>`;
    }

    #readFilterForm(panel) {
        const cfg = {};
        const include = [];
        const exclude = [];
        for (const row of panel.querySelectorAll('.ps-cfg-filter-rules .ps-cfg-filter-rule')) {
            const field = row.querySelector('.ps-cfg-rule-field')?.value?.trim();
            const op = row.querySelector('.ps-cfg-rule-op')?.value;
            if (!field || !op) continue;
            const rule = { field, op };
            const useVField = row.querySelector('.ps-cfg-rule-dyn input')?.checked;
            if (op !== 'exists' && op !== 'not_exists') {
                if (useVField) {
                    rule.value_field = row.querySelector('.ps-cfg-rule-vfield')?.value?.trim() || '';
                } else {
                    rule.value = this.#parseRuleValue(row.querySelector('.ps-cfg-rule-value')?.value?.trim());
                }
            }
            (row.querySelector('.ps-cfg-rule-invert-cb')?.checked ? exclude : include).push(rule);
        }
        if (include.length) cfg.include = include;
        if (exclude.length) cfg.exclude = exclude;
        return cfg;
    }

    #filterRuleHtml(rule = {}) {
        const ops = ['==', '!=', '===', '!==', '>', '>=', '<', '<=', 'in', 'not_in', 'contains', 'not_contains', 'exists', 'not_exists', 'matches', 'not_matches'];
        const opOpts = ops.map(o => `<option value="${o}"${rule.op === o ? ' selected' : ''}>${this.#esc(o)}</option>`).join('');
        const hasValueField = !!rule.value_field;
        const noValue = rule.op === 'exists' || rule.op === 'not_exists';
        return `<div class="ps-cfg-filter-rule">
            ${this.#fieldPickerCellHtml('ps-cfg-rule-field', rule.field || '', 'field.path')}
            <select class="ps-input ps-input-sm ps-cfg-rule-op">${opOpts}</select>
            <input type="text" class="ps-input ps-input-sm ps-cfg-rule-value" value="${this.#esc(noValue ? '' : (hasValueField ? '' : this.#serializeRuleValue(rule.value)))}" placeholder="value" ${noValue ? 'disabled' : ''} />
            <label class="ps-cfg-rule-dyn" title="Compare to another field"><input type="checkbox" ${hasValueField ? 'checked' : ''} />field</label>
            <div class="ps-cfg-rule-vfield-wrap ${hasValueField ? '' : 'hidden'}">${this.#fieldPickerCellHtml('ps-cfg-rule-vfield', rule.value_field || '', 'other.field')}</div>
            <label class="ps-cfg-rule-invert" title="Drop messages matching this rule instead of keeping them"><input type="checkbox" class="ps-cfg-rule-invert-cb" ${rule.invert ? 'checked' : ''} />drop</label>
            <button class="btn-icon ps-cfg-row-remove">✕</button>
        </div>`;
    }

    #serializeRuleValue(val) {
        if (val === undefined || val === null) return '';
        if (typeof val === 'string') return val;
        return JSON.stringify(val);
    }

    #parseRuleValue(str) {
        if (!str) return undefined;
        try { return JSON.parse(str); } catch { return str; }
    }

    #addFilterRule(container) {
        if (!container) return;
        container.insertAdjacentHTML('beforeend', this.#filterRuleHtml());
        const row = container.lastElementChild;
        row.querySelector('.ps-cfg-row-remove').onclick = () => row.remove();
        const opSel = row.querySelector('.ps-cfg-rule-op');
        const valIn = row.querySelector('.ps-cfg-rule-value');
        const dynCb = row.querySelector('.ps-cfg-rule-dyn input');
        const vfWrap = row.querySelector('.ps-cfg-rule-vfield-wrap');
        opSel.onchange = () => { valIn.disabled = opSel.value === 'exists' || opSel.value === 'not_exists'; };
        dynCb.onchange = () => { vfWrap.classList.toggle('hidden', !dynCb.checked); };
    }

    // -- Lookup form: flatMap (with performance warnings) --
    #renderFlatMapForm(cfg) {
        let html = `<div class="ps-cfg-section"><label>Source</label>
            <div style="display:flex;gap:6px;align-items:center">
            <input type="text" class="ps-input ps-input-sm ps-cfg-source" value="${this.#esc(cfg.source || '')}" placeholder="cache:name or db:keyspace.table" style="flex:1" />
            <button class="btn btn-sm ps-cfg-source-browse">Browse...</button></div>
            <div class="ps-cfg-db-warning ps-cfg-warning${cfg.source?.startsWith('db:') ? '' : ' hidden'}">⚠ DB queries execute per message — extremely slow, will not scale for most use cases. Use a cache source instead.</div></div>`;

        html += '<div class="ps-cfg-section"><label>Match (lookup field → message field)</label><div class="ps-cfg-match-rows">';
        for (const [k, v] of Object.entries(cfg.match || {})) html += this.#kvRowHtml(k, v, 'lookup field', '${msg.field}');
        html += '</div><button class="link-btn ps-cfg-add-match">+ match</button></div>';

        html += this.#renderJoinProjectionSection(cfg);

        html += `<div class="ps-cfg-section ps-cfg-toggles">
            <label><input type="checkbox" class="ps-cfg-allow-scan" ${cfg.allow_scan ? 'checked' : ''} /> allow_scan</label>
            <div class="ps-cfg-scan-warning ps-cfg-warning${cfg.allow_scan ? '' : ' hidden'}">⚠ O(N) full cache scan on every message — use only for specialized fan-out cases.</div>
        </div>`;

        html += `<div class="ps-cfg-section ps-cfg-perf-guide">
            <label>Performance tiers</label>
            <div class="ps-form-hint">
                <strong>1. Cache + primary key match</strong> — O(1), preferred<br/>
                <strong>2. Cache + allow_scan</strong> — O(N), slow, specialized only<br/>
                <strong>3. DB query</strong> — bounded partition read, almost never recommended
            </div></div>`;
        return html;
    }

    #readFlatMapForm(panel) {
        const cfg = {};
        const src = panel.querySelector('.ps-cfg-source')?.value?.trim();
        if (src) cfg.source = src;
        const match = this.#readKvRows(panel.querySelector('.ps-cfg-match-rows'));
        if (Object.keys(match).length) cfg.match = match;

        let joinFields;
        try { joinFields = this.#readJoinProjectionFields(panel); }
        catch (err) { throw new Error(`flatMap: ${err.message}`); }
        Object.assign(cfg, joinFields);

        if (panel.querySelector('.ps-cfg-allow-scan')?.checked) cfg.allow_scan = true;
        return cfg;
    }

    // -- Lookup form: enrich (cache-only, primary key enforced) --
    #renderEnrichForm(cfg) {
        let html = `<div class="ps-cfg-section"><label>Source (cache only) <span class="ps-cfg-required">*</span></label>
            <div style="display:flex;gap:6px;align-items:center">
            <button type="button" class="ps-input ps-input-sm ps-cfg-source-label ps-picker-display" style="flex:1" title="Click to change">${cfg.source ? this.#esc(cfg.source) : '<em>None selected</em>'}</button>
            <input type="hidden" class="ps-cfg-source" value="${this.#esc(cfg.source || '')}" />
            </div>
            <div class="ps-form-hint">Select an adorning source. Match is enforced on its primary key.</div></div>`;

        html += `<div class="ps-cfg-section ps-cfg-enrich-match-section">
            <label>Match (message field → cache key)</label>
            <div class="ps-cfg-enrich-match"></div>
            <div class="ps-form-hint ps-cfg-enrich-match-hint hidden">Compound keys are concatenated with "." for cache lookup. All key fields must be mapped.</div></div>`;

        html += this.#renderJoinProjectionSection(cfg);
        return html;
    }

    #readEnrichForm(panel) {
        const cfg = {};
        const src = panel.querySelector('.ps-cfg-source')?.value?.trim();
        if (!src) throw new Error('Source is required for enrich');
        cfg.source = src;
        // Read match from structured pairs. Every primary-key row must be mapped —
        // a missing entry causes ELM's compile_match to throw on Object.entries(undefined).
        const pairs = [...panel.querySelectorAll('.ps-cfg-match-pair')];
        if (!pairs.length) {
            throw new Error('Selected source has no primary key defined — cannot use enrich with this source.');
        }
        const match = {};
        const missing = [];
        for (const pair of pairs) {
            const left = pair.querySelector('.ps-cfg-match-left')?.value?.trim();
            const right = pair.querySelector('.ps-cfg-match-right')?.value?.trim();
            if (!right) continue;
            if (!left) { missing.push(right); continue; }
            match[right] = '${msg.' + left + '}';
        }
        if (missing.length) {
            throw new Error(`enrich: pick a message field for every primary key. Missing: ${missing.join(', ')}`);
        }
        cfg.match = match;

        let joinFields;
        try { joinFields = this.#readJoinProjectionFields(panel); }
        catch (err) { throw new Error(`enrich: ${err.message}`); }
        Object.assign(cfg, joinFields);
        return cfg;
    }

    // -- Shared per-field join projection widget (enrich + flatMap) --
    // Both plugins are the same join operation differing only in cardinality and
    // share one config shape: fields[{from,as,on_miss,default,on_conflict}], on_no_match.
    // No nesting/container key — adornment is a flat repair, not a structural change.
    // See docs/handoff/frontend/enrich-config-ui.md.

    #renderJoinProjectionSection(cfg) {
        const onNoMatch = cfg.on_no_match || 'skip';
        let html = `<div class="ps-cfg-section ps-cfg-join-defaults">
            <label>Defaults for newly added fields</label>
            <div class="ps-cfg-join-defaults-row">
                <label>On miss
                    <select class="ps-input ps-input-sm ps-cfg-join-default-onmiss">
                        <option value="skip">skip</option>
                        <option value="default">use default</option>
                    </select></label>
                <label>If already present
                    <select class="ps-input ps-input-sm ps-cfg-join-default-onconflict">
                        <option value="skip">skip</option>
                        <option value="overwrite">overwrite</option>
                        <option value="error">error</option>
                    </select></label>
            </div>
            <div class="ps-form-hint">Applied to each field's row when it's added — set this first if you're about to select many fields at once. Existing rows aren't changed retroactively; edit them individually.</div></div>`;

        html += `<div class="ps-cfg-section ps-cfg-join-fields-section">
            <label>Fields to merge</label>
            <div class="ps-cfg-join-fields"></div>
            <button type="button" class="link-btn ps-cfg-join-add-manual hidden">+ field (type a name)</button></div>`;

        html += `<div class="ps-cfg-section ps-cfg-join-projection-section hidden">
            <label>Field mapping</label>
            <div class="ps-cfg-projection-header"><span>Source</span><span>Merge as</span><span>On miss</span><span>Default</span><span>If present</span><span></span></div>
            <div class="ps-cfg-join-projection"></div>
            <div class="ps-form-hint">"Merge as" defaults to the source column name — edit to rename. "On miss" only applies when the record was found but this particular column is empty. "If present" controls whether an existing value already on the message is kept or replaced.</div></div>`;

        html += `<div class="ps-cfg-section"><label>On no match (no record found at all)</label>
            <select class="ps-input ps-input-sm ps-cfg-on-no-match">
                <option value="skip"${onNoMatch === 'skip' ? ' selected' : ''}>skip — don't add any fields</option>
                <option value="use_defaults"${onNoMatch === 'use_defaults' ? ' selected' : ''}>use defaults — fill in each field's configured default</option>
                <option value="error"${onNoMatch === 'error' ? ' selected' : ''}>error — fail the message</option>
            </select>
            <div class="ps-form-hint">Applies to the whole lookup when nothing matches at all — separate from each field's own "On miss," which only covers an empty column on a record that <em>was</em> found.</div></div>`;
        return html;
    }

    // One row of the shared per-field projection list: source column (locked
    // unless manually typed), editable "merge as" rename, per-field miss/conflict
    // behavior + default, and a remove button.
    #projectionRowHtml(field, fieldCfg = {}, { manual = false } = {}) {
        const as = fieldCfg.as || '';
        const onMiss = fieldCfg.on_miss === 'default' ? 'default' : 'skip';
        const onConflict = ['overwrite', 'error'].includes(fieldCfg.on_conflict) ? fieldCfg.on_conflict : 'skip';
        const defaultStr = fieldCfg.default !== undefined ? JSON.stringify(fieldCfg.default) : '';
        return `<div class="ps-cfg-projection-row" data-from="${this.#esc(field)}" data-manual="${manual ? '1' : ''}">
            <span class="ps-cfg-projection-source">${this.#esc(field)}</span>
            <input type="text" class="ps-input ps-input-sm ps-cfg-projection-as" value="${this.#esc(as)}" placeholder="${this.#esc(field)}" title="Merge as" />
            <select class="ps-input ps-input-sm ps-cfg-projection-onmiss" title="On miss (this column empty)">
                <option value="skip"${onMiss === 'skip' ? ' selected' : ''}>skip</option>
                <option value="default"${onMiss === 'default' ? ' selected' : ''}>use default</option>
            </select>
            <input type="text" class="ps-input ps-input-sm ps-cfg-projection-default${onMiss === 'default' ? '' : ' hidden'}" value="${this.#esc(defaultStr)}" placeholder='e.g. "US" or null' />
            <select class="ps-input ps-input-sm ps-cfg-projection-onconflict" title="If the message already has this field">
                <option value="skip"${onConflict === 'skip' ? ' selected' : ''}>skip</option>
                <option value="overwrite"${onConflict === 'overwrite' ? ' selected' : ''}>overwrite</option>
                <option value="error"${onConflict === 'error' ? ' selected' : ''}>error</option>
            </select>
            <button type="button" class="btn-icon ps-cfg-projection-remove" title="Remove">✕</button>
        </div>`;
    }

    #bindProjectionRow(panel, row) {
        const onMissSel = row.querySelector('.ps-cfg-projection-onmiss');
        const defInput = row.querySelector('.ps-cfg-projection-default');
        onMissSel?.addEventListener('change', () => {
            defInput?.classList.toggle('hidden', onMissSel.value !== 'default');
        });
        row.querySelector('.ps-cfg-projection-remove')?.addEventListener('click', () => {
            if (row.dataset.manual) { row.remove(); return; }
            // Checkbox-driven row: uncheck the box so its own handler removes the row.
            const cb = panel.querySelector(`.ps-cfg-join-fields input[type="checkbox"][value="${CSS.escape(row.dataset.from)}"]`);
            if (cb) { cb.checked = false; cb.dispatchEvent(new Event('change')); }
            else row.remove();
        });
    }

    // Adds/removes a single field's projection row in response to its checkbox,
    // without touching rows for any other already-checked field.
    #setJoinFieldChecked(panel, field, checked, existingCfg) {
        const projectionContainer = panel.querySelector('.ps-cfg-join-projection');
        const section = panel.querySelector('.ps-cfg-join-projection-section');
        if (!projectionContainer) return;
        const existingRow = projectionContainer.querySelector(`.ps-cfg-projection-row[data-from="${CSS.escape(field)}"]`);
        if (checked) {
            if (!existingRow) {
                const existingFieldsRaw = existingCfg?.fields || existingCfg?.select || [];
                const existingFieldCfg = existingFieldsRaw.find(f => (typeof f === 'string' ? f : f.from) === field);
                const fieldCfg = existingFieldCfg && typeof existingFieldCfg === 'object' ? existingFieldCfg : this.#joinFieldDefaults(panel);
                projectionContainer.insertAdjacentHTML('beforeend', this.#projectionRowHtml(field, fieldCfg));
                this.#bindProjectionRow(panel, projectionContainer.lastElementChild);
            }
        } else if (existingRow) {
            existingRow.remove();
        }
        section?.classList.toggle('hidden', !projectionContainer.children.length);
    }

    // Reads the "defaults for newly added fields" control to seed a brand-new row.
    #joinFieldDefaults(panel) {
        return {
            on_miss: panel.querySelector('.ps-cfg-join-default-onmiss')?.value || 'skip',
            on_conflict: panel.querySelector('.ps-cfg-join-default-onconflict')?.value || 'skip',
        };
    }

    // Schema-driven mode: a checkbox grid built from a resolved list of field names.
    #populateJoinFieldsFromSchema(panel, mergeableFields, existingCfg) {
        const fieldsContainer = panel.querySelector('.ps-cfg-join-fields');
        if (!fieldsContainer) return;
        panel.querySelector('.ps-cfg-join-add-manual')?.classList.add('hidden');

        const existingFieldsRaw = existingCfg?.fields || existingCfg?.select || [];
        const existingFieldNames = new Set(existingFieldsRaw.map(f => typeof f === 'string' ? f : f.from));
        let fieldsHtml = '';
        if (!mergeableFields.length) {
            fieldsHtml = '<div class="ps-form-hint">No additional fields available from this source</div>';
        } else {
            fieldsHtml += '<div class="ps-cfg-field-controls"><label class="ps-cfg-select-all-label"><input type="checkbox" class="ps-cfg-select-all" /> Select all</label></div>';
            fieldsHtml += '<div class="ps-cfg-field-grid">';
            for (const f of mergeableFields) {
                fieldsHtml += `<label class="ps-cfg-field-cb"><input type="checkbox" value="${this.#esc(f)}" ${existingFieldNames.has(f) ? 'checked' : ''} /> ${this.#esc(f)}</label>`;
            }
            fieldsHtml += '</div>';
        }
        fieldsContainer.innerHTML = fieldsHtml;

        const projectionContainer = panel.querySelector('.ps-cfg-join-projection');
        if (projectionContainer) projectionContainer.innerHTML = '';
        for (const cb of fieldsContainer.querySelectorAll('.ps-cfg-field-cb input')) {
            cb.addEventListener('change', () => this.#setJoinFieldChecked(panel, cb.value, cb.checked, existingCfg));
            if (cb.checked) this.#setJoinFieldChecked(panel, cb.value, true, existingCfg);
        }
        const selectAll = fieldsContainer.querySelector('.ps-cfg-select-all');
        if (selectAll) {
            selectAll.addEventListener('change', () => {
                for (const cb of fieldsContainer.querySelectorAll('.ps-cfg-field-cb input')) {
                    cb.checked = selectAll.checked;
                    this.#setJoinFieldChecked(panel, cb.value, cb.checked, existingCfg);
                }
            });
        }
    }

    // Fallback when the source's schema can't be resolved (most db: sources, or
    // an unrecognized cache source) — type field names in manually. Produces the
    // exact same fields[] shape as the checkbox-driven path.
    #populateJoinFieldsManually(panel, existingCfg, hintMessage) {
        const fieldsContainer = panel.querySelector('.ps-cfg-join-fields');
        if (!fieldsContainer) return;
        fieldsContainer.innerHTML = `<div class="ps-form-hint">${this.#esc(hintMessage || 'Field list unavailable for this source — add fields to merge manually.')}</div>`;
        panel.querySelector('.ps-cfg-join-add-manual')?.classList.remove('hidden');

        const projectionContainer = panel.querySelector('.ps-cfg-join-projection');
        if (!projectionContainer) return;
        projectionContainer.innerHTML = '';
        const existingFieldsRaw = existingCfg?.fields || existingCfg?.select || [];
        for (const f of existingFieldsRaw) {
            const field = typeof f === 'string' ? f : f.from;
            if (!field) continue;
            const fieldCfg = typeof f === 'object' ? f : {};
            projectionContainer.insertAdjacentHTML('beforeend', this.#projectionRowHtml(field, fieldCfg, { manual: true }));
            this.#bindProjectionRow(panel, projectionContainer.lastElementChild);
        }
        panel.querySelector('.ps-cfg-join-projection-section')?.classList.toggle('hidden', !projectionContainer.children.length);
    }

    async #addManualJoinField(panel, existingCfg) {
        const name = await this.#prompt('Field name to merge:', 'e.g. address');
        const field = name?.trim();
        if (!field) return;
        const projectionContainer = panel.querySelector('.ps-cfg-join-projection');
        if (!projectionContainer || projectionContainer.querySelector(`.ps-cfg-projection-row[data-from="${CSS.escape(field)}"]`)) return;
        const existingFieldsRaw = existingCfg?.fields || existingCfg?.select || [];
        const existingFieldCfg = existingFieldsRaw.find(f => (typeof f === 'string' ? f : f.from) === field);
        const fieldCfg = existingFieldCfg && typeof existingFieldCfg === 'object' ? existingFieldCfg : this.#joinFieldDefaults(panel);
        projectionContainer.insertAdjacentHTML('beforeend', this.#projectionRowHtml(field, fieldCfg, { manual: true }));
        this.#bindProjectionRow(panel, projectionContainer.lastElementChild);
        panel.querySelector('.ps-cfg-join-projection-section')?.classList.remove('hidden');
    }

    // Resolves the field list for flatMap's Fields-to-merge widget — cache (and any
    // other source /api/sources has a schema for) gets the checkbox list; anything
    // unresolvable (most db: sources) falls back to manual entry.
    async #populateJoinFieldsForSource(panel, sourceName, existingCfg) {
        if (!sourceName) {
            this.#populateJoinFieldsManually(panel, existingCfg, 'Choose a source above to see its available fields.');
            return;
        }
        const sources = await this.fetch('/api/sources').catch(() => []);
        const name = sourceName.replace(/^(cache|db):/, '');
        const src = (Array.isArray(sources) ? sources : []).find(s => s.name === name);
        const fields = (src?.fields || []).slice().sort();
        if (src && fields.length) {
            this.#populateJoinFieldsFromSchema(panel, fields, existingCfg);
        } else {
            this.#populateJoinFieldsManually(panel, existingCfg,
                src ? 'This source has no field list available — add fields to merge manually.'
                    : 'Field list unavailable for this source — add fields to merge manually.');
        }
    }

    // Populate enrich's match rows (primary-key enforced) + shared fields widget.
    async #populateEnrichSource(panel, sourceName, existingCfg) {
        const matchContainer = panel.querySelector('.ps-cfg-enrich-match');
        const matchHint = panel.querySelector('.ps-cfg-enrich-match-hint');
        if (!matchContainer) return;

        const sources = await this.fetch('/api/sources').catch(() => []);
        const name = sourceName.replace(/^cache:/, '');
        const src = (Array.isArray(sources) ? sources : []).find(s => s.name === name);
        if (!src) {
            matchContainer.innerHTML = '<div class="ps-form-hint" style="color:var(--danger)">Source not found — cannot resolve primary key</div>';
            this.#populateJoinFieldsManually(panel, existingCfg, 'Source not found — add fields to merge manually.');
            return;
        }

        const primaryKey = [].concat(src.cache_primary_key || src.keys || []);
        const srcFields = (src.fields || []).slice().sort();

        // Build match rows — one per primary key field, right side locked
        const existingMatch = existingCfg?.match || {};
        let matchHtml = '';
        if (!primaryKey.length) {
            matchHtml = '<div class="ps-form-hint" style="color:var(--danger)">Source has no primary key defined</div>';
        } else {
            for (const keyField of primaryKey) {
                const existingVal = existingMatch[keyField] || '';
                const stripped = existingVal.replace(/^\$\{msg\./, '').replace(/\}$/, '');
                matchHtml += `<div class="ps-cfg-match-pair">
                    ${this.#fieldPickerCellHtml('ps-cfg-match-left', stripped, 'message field')}
                    <span>→</span>
                    <input type="text" class="ps-input ps-input-sm ps-cfg-match-right" value="${this.#esc(keyField)}" readonly tabindex="-1" />
                </div>`;
            }
            if (primaryKey.length > 1) matchHint?.classList.remove('hidden');
            else matchHint?.classList.add('hidden');
        }
        matchContainer.innerHTML = matchHtml;
        this.#bindFieldPickerDelegation(matchContainer, 'Choose message field');

        const keySet = new Set(primaryKey);
        const mergeableFields = srcFields.filter(f => !keySet.has(f));
        if (mergeableFields.length) {
            this.#populateJoinFieldsFromSchema(panel, mergeableFields, existingCfg);
        } else {
            this.#populateJoinFieldsManually(panel, existingCfg, 'No additional fields available from this source.');
        }
    }

    // Shared reader for the fields[]/on_no_match shape — used by both enrich and
    // flatMap (see docs/handoff/frontend/enrich-config-ui.md). No nesting/container
    // key — merge is always flat onto the message.
    #readJoinProjectionFields(panel) {
        const rows = [...panel.querySelectorAll('.ps-cfg-projection-row')];
        if (!rows.length) throw new Error('select at least one field to merge');

        const fields = [];
        const seenAs = new Set();
        for (const row of rows) {
            const from = row.dataset.from;
            if (!from) continue;
            const as = row.querySelector('.ps-cfg-projection-as')?.value?.trim() || from;
            if (seenAs.has(as)) {
                throw new Error(`two fields resolve to the same "Merge as" name: "${as}"`);
            }
            seenAs.add(as);

            const field = { from };
            if (as !== from) field.as = as;

            const onMiss = row.querySelector('.ps-cfg-projection-onmiss')?.value || 'skip';
            if (onMiss === 'default') {
                const defRaw = row.querySelector('.ps-cfg-projection-default')?.value;
                if (defRaw === undefined || defRaw.trim() === '') {
                    throw new Error(`field "${from}" is set to use a default but no default value is provided`);
                }
                field.on_miss = 'default';
                try { field.default = JSON.parse(defRaw); } catch { field.default = defRaw; }
            }

            const onConflict = row.querySelector('.ps-cfg-projection-onconflict')?.value || 'skip';
            if (onConflict !== 'skip') field.on_conflict = onConflict;

            fields.push(field);
        }

        const result = { fields };
        const onNoMatch = panel.querySelector('.ps-cfg-on-no-match')?.value || 'skip';
        if (onNoMatch !== 'skip') result.on_no_match = onNoMatch;
        return result;
    }

    // -- Crypto form (decrypt / encrypt) --
    #renderCryptoForm(cfg, fnName) {
        const defaults = fnName === 'decrypt'
            ? { key_field: 'client_id', payload_field: 'encryptedData' }
            : { key_field: 'deviceid', payload_field: 'payload' };
        return `<div class="ps-cfg-section"><label>Key field</label>
            <input type="text" class="ps-input ps-input-sm ps-cfg-key-field" value="${this.#esc(cfg.key_field || '')}" placeholder="${defaults.key_field} (default)" /></div>
        <div class="ps-cfg-section"><label>Payload field</label>
            <input type="text" class="ps-input ps-input-sm ps-cfg-payload-field" value="${this.#esc(cfg.payload_field || '')}" placeholder="${defaults.payload_field} (default)" /></div>
        <div class="ps-cfg-section"><label>Output field</label>
            <input type="text" class="ps-input ps-input-sm ps-cfg-output-field" value="${this.#esc(cfg.output_field || '')}" placeholder="(empty = replace message)" />
            <div class="ps-form-hint">Leave empty to replace the message with the result. Set a field name to write the result there instead.</div></div>
        <div class="ps-cfg-section ps-cfg-toggles">
            <label><input type="checkbox" class="ps-cfg-conditional" ${cfg.conditional ? 'checked' : ''} /> Conditional — skip if payload field is not present</label>
        </div>`;
    }

    #readCryptoForm(panel) {
        const cfg = {};
        const kf = panel.querySelector('.ps-cfg-key-field')?.value?.trim();
        if (kf) cfg.key_field = kf;
        const pf = panel.querySelector('.ps-cfg-payload-field')?.value?.trim();
        if (pf) cfg.payload_field = pf;
        const of_ = panel.querySelector('.ps-cfg-output-field')?.value?.trim();
        if (of_) cfg.output_field = of_;
        if (panel.querySelector('.ps-cfg-conditional')?.checked) cfg.conditional = true;
        return cfg;
    }

    // -- Compression form (unzip / zip) --
    #renderCompressionForm(cfg, fnName) {
        const isUnzip = fnName === 'unzip';
        return `<div class="ps-cfg-section"><label>Payload field</label>
            <input type="text" class="ps-input ps-input-sm ps-cfg-payload-field" value="${this.#esc(cfg.payload_field || '')}" placeholder="payload (default)" /></div>
        <div class="ps-cfg-section"><label>${isUnzip ? 'Input' : 'Output'} encoding</label>
            <select class="ps-input ps-input-sm ps-cfg-encoding">
                <option value="base64"${(cfg.encoding || 'base64') === 'base64' ? ' selected' : ''}>base64</option>
                <option value="hex"${cfg.encoding === 'hex' ? ' selected' : ''}>hex</option>
                <option value="binary"${cfg.encoding === 'binary' ? ' selected' : ''}>binary</option>
            </select></div>
        <div class="ps-cfg-section"><label>Format</label>
            <select class="ps-input ps-input-sm ps-cfg-format">
                <option value="gzip"${(cfg.format || 'gzip') === 'gzip' ? ' selected' : ''}>gzip</option>
                <option value="deflate"${cfg.format === 'deflate' ? ' selected' : ''}>deflate</option>
                <option value="brotli"${cfg.format === 'brotli' ? ' selected' : ''}>brotli</option>
            </select></div>
        <div class="ps-cfg-section"><label>Output field</label>
            <input type="text" class="ps-input ps-input-sm ps-cfg-output-field" value="${this.#esc(cfg.output_field || '')}" placeholder="(empty = replace message)" /></div>
        <div class="ps-cfg-section ps-cfg-toggles">
            <label><input type="checkbox" class="ps-cfg-conditional" ${cfg.conditional ? 'checked' : ''} /> Conditional — skip if payload field is not ${isUnzip ? 'a string' : 'present'}</label>
        </div>`;
    }

    #readCompressionForm(panel) {
        const cfg = {};
        const pf = panel.querySelector('.ps-cfg-payload-field')?.value?.trim();
        if (pf) cfg.payload_field = pf;
        const enc = panel.querySelector('.ps-cfg-encoding')?.value;
        if (enc && enc !== 'base64') cfg.encoding = enc;
        const fmt = panel.querySelector('.ps-cfg-format')?.value;
        if (fmt && fmt !== 'gzip') cfg.format = fmt;
        const of_ = panel.querySelector('.ps-cfg-output-field')?.value?.trim();
        if (of_) cfg.output_field = of_;
        if (panel.querySelector('.ps-cfg-conditional')?.checked) cfg.conditional = true;
        return cfg;
    }

    // -- Debug print form --
    #renderDebugForm(cfg) {
        let html = '<div class="ps-cfg-section"><label>Fields to print (empty = entire message)</label><div class="ps-cfg-printfield-list">';
        for (const f of (cfg.fields || [])) html += this.#listItemHtml(f);
        html += '</div><button class="link-btn ps-cfg-add-printfield">+ field</button></div>';
        html += `<div class="ps-cfg-section ps-cfg-toggles">
            <label><input type="checkbox" class="ps-cfg-pretty" ${cfg.pretty ? 'checked' : ''} /> Pretty-print JSON</label>
        </div>`;
        return html;
    }

    #readDebugForm(panel) {
        const cfg = {};
        const fields = this.#readListItems(panel.querySelector('.ps-cfg-printfield-list'));
        if (fields.length) cfg.fields = fields;
        if (panel.querySelector('.ps-cfg-pretty')?.checked) cfg.pretty = true;
        return cfg;
    }

    // -- Schema apply_defaults form --
    #renderApplyDefaultsForm(cfg) {
        return `<div class="ps-cfg-section"><label>Defaults path (on pipeline.schema)</label>
            <input type="text" class="ps-input ps-input-sm ps-cfg-defaults-path" value="${this.#esc(cfg.defaults_path || '')}" placeholder="default_values (default)" /></div>
        <div class="ps-cfg-section"><label>Type field</label>
            <input type="text" class="ps-input ps-input-sm ps-cfg-type-field" value="${this.#esc(cfg.type_field || '')}" placeholder="message_type (default)" /></div>`;
    }

    #readApplyDefaultsForm(panel) {
        const cfg = {};
        const dp = panel.querySelector('.ps-cfg-defaults-path')?.value?.trim();
        if (dp) cfg.defaults_path = dp;
        const tf = panel.querySelector('.ps-cfg-type-field')?.value?.trim();
        if (tf) cfg.type_field = tf;
        return cfg;
    }

    // -- Schema validate form --
    #renderValidateForm(cfg) {
        let html = '<div class="ps-cfg-section"><label>Required fields</label><div class="ps-cfg-reqfield-list">';
        for (const f of (cfg.required_fields || [])) html += this.#listItemHtml(f);
        html += '</div><button class="link-btn ps-cfg-add-reqfield">+ field</button></div>';
        return html;
    }

    #readValidateForm(panel) {
        const cfg = {};
        const fields = this.#readListItems(panel.querySelector('.ps-cfg-reqfield-list'));
        if (fields.length) cfg.required_fields = fields;
        return cfg;
    }

    // -- Kafka publish form --
    #renderKafkaPublishForm(cfg) {
        const keyVal = Array.isArray(cfg.key) ? cfg.key.join(', ') : (cfg.key || '');
        return `<div class="ps-cfg-section"><label>Topic (required)</label>
            <input type="text" class="ps-input ps-input-sm ps-cfg-topic" value="${this.#esc(cfg.topic || '')}" placeholder="output-topic-name" /></div>
        <div class="ps-cfg-section"><label>Key field(s)</label>
            <input type="text" class="ps-input ps-input-sm ps-cfg-publish-key" value="${this.#esc(keyVal)}" placeholder="deviceid (default)" />
            <div class="ps-form-hint">Comma-separated for fallback priority (first truthy field wins).</div></div>`;
    }

    #readKafkaPublishForm(panel) {
        const cfg = {};
        const topic = panel.querySelector('.ps-cfg-topic')?.value?.trim();
        if (topic) cfg.topic = topic;
        const keyStr = panel.querySelector('.ps-cfg-publish-key')?.value?.trim();
        if (keyStr) {
            const parts = keyStr.split(',').map(s => s.trim()).filter(Boolean);
            cfg.key = parts.length > 1 ? parts : parts[0];
        }
        return cfg;
    }

    // -- emit_telemetry form --
    #renderEmitTelemetryForm(cfg) {
        const picked = cfg.domain && cfg.message_type ? `${cfg.domain} / ${cfg.message_type}` : '';
        const persistChecked = cfg.persist !== false ? 'checked' : '';
        const publishChecked = cfg.publish !== false ? 'checked' : '';
        return `<div class="ps-cfg-section"><label>Output message type <span class="ps-cfg-required">*</span></label>
            <div class="ps-picker-row">
                <button type="button" class="ps-input ps-picker-display ps-cfg-emit-picker" title="Click to change">
                    ${picked ? this.#esc(picked) : '<em>Choose message type…</em>'}
                </button>
                <input type="hidden" class="ps-cfg-emit-domain" value="${this.#esc(cfg.domain || '')}" />
                <input type="hidden" class="ps-cfg-emit-mt" value="${this.#esc(cfg.message_type || '')}" />
            </div>
            <div class="ps-form-hint">This becomes a declared output of the pipeline on save.</div></div>
        <div class="ps-cfg-section"><label>Overrides</label>
            <label class="ps-schema-editor-inline"><input type="checkbox" class="ps-cfg-emit-persist" ${persistChecked} /> persist</label>
            <label class="ps-schema-editor-inline"><input type="checkbox" class="ps-cfg-emit-publish" ${publishChecked} /> publish</label>
            <div class="ps-form-hint">Uncheck to omit persist/publish for this emit.</div></div>`;
    }

    #readEmitTelemetryForm(panel) {
        const cfg = {};
        const domain = panel.querySelector('.ps-cfg-emit-domain')?.value?.trim();
        const message_type = panel.querySelector('.ps-cfg-emit-mt')?.value?.trim();
        if (!domain || !message_type) throw new Error('emit_telemetry requires a message type (domain + message_type)');
        cfg.domain = domain;
        cfg.message_type = message_type;
        const persistEl = panel.querySelector('.ps-cfg-emit-persist');
        const publishEl = panel.querySelector('.ps-cfg-emit-publish');
        if (persistEl) cfg.persist = persistEl.checked;
        if (publishEl) cfg.publish = publishEl.checked;
        return cfg;
    }

    // -- Window form --
    #renderWindowForm(cfg) {
        const durMatch = (cfg.window || '').match(/^(\d+)([smhd])$/);
        const durVal = durMatch ? durMatch[1] : '';
        const durUnit = durMatch ? durMatch[2] : 'h';

        let journalType = 'auto';
        let journalCustom = {};
        if (typeof cfg.journal === 'string' && cfg.journal.startsWith('builtin:')) {
            journalType = cfg.journal;
        } else if (typeof cfg.journal === 'object' && cfg.journal) {
            journalType = 'custom';
            journalCustom = cfg.journal;
        }

        let html = `<div class="ps-cfg-section"><label>Key (partition field)</label>
            <input type="text" class="ps-input ps-input-sm ps-cfg-wkey" value="${this.#esc(cfg.key || '')}" placeholder="\${deviceuuid}" /></div>`;

        html += `<div class="ps-cfg-section"><label>Window duration</label>
            <div class="ps-cfg-dur-row">
                <input type="number" class="ps-input ps-input-sm ps-cfg-wdur-val" value="${this.#esc(durVal)}" min="1" placeholder="1" />
                <select class="ps-input ps-input-sm ps-cfg-wdur-unit">
                    <option value="s"${durUnit === 's' ? ' selected' : ''}>seconds</option>
                    <option value="m"${durUnit === 'm' ? ' selected' : ''}>minutes</option>
                    <option value="h"${durUnit === 'h' ? ' selected' : ''}>hours</option>
                    <option value="d"${durUnit === 'd' ? ' selected' : ''}>days</option>
                </select>
            </div></div>`;

        html += `<div class="ps-cfg-section"><label>Timestamp field</label>
            <input type="text" class="ps-input ps-input-sm ps-cfg-wts" value="${this.#esc(cfg.timestamp_field || '')}" placeholder="utc_timestamp_inms (default)" /></div>`;

        html += '<div class="ps-cfg-section"><label>Fields to store per entry</label><div class="ps-cfg-wfield-list">';
        for (const f of (cfg.fields || [])) html += this.#listItemHtml(f);
        html += '</div><button class="link-btn ps-cfg-add-wfield">+ field</button></div>';

        const merge = cfg.merge || 'collect';
        html += `<div class="ps-cfg-section"><label>Merge strategy</label>
            <select class="ps-input ps-input-sm ps-cfg-merge-strategy">
                <option value="collect"${merge === 'collect' ? ' selected' : ''}>collect — array of entries</option>
                <option value="concat_field"${merge === 'concat_field' ? ' selected' : ''}>concat_field — concatenate nested arrays</option>
                <option value="merge_latest"${merge === 'merge_latest' ? ' selected' : ''}>merge_latest — shallow merge, last wins</option>
            </select></div>`;

        html += `<div class="ps-cfg-section ps-cfg-merge-field-row${merge !== 'concat_field' ? ' hidden' : ''}"><label>merge_field (array field to concatenate)</label>
            <input type="text" class="ps-input ps-input-sm ps-cfg-wmerge-field" value="${this.#esc(cfg.merge_field || '')}" placeholder="motion_events" /></div>`;

        html += `<div class="ps-cfg-section"><label>Merge as</label>
            <input type="text" class="ps-input ps-input-sm ps-cfg-wmerge-as" value="${this.#esc(cfg.merge_as || '')}" placeholder="window" /></div>`;

        html += `<div class="ps-cfg-section"><label>Max entries per key</label>
            <input type="number" class="ps-input ps-input-sm ps-cfg-wmax" value="${cfg.max_entries ?? ''}" min="1" placeholder="1000 (default)" /></div>`;

        html += `<div class="ps-cfg-section"><label>Journal (backfill source)</label>
            <div class="ps-radio-group">
                <label><input type="radio" name="ps-cfg-journal-type" value="auto"${journalType === 'auto' ? ' checked' : ''} /> Auto-detect from schema</label>
                <label><input type="radio" name="ps-cfg-journal-type" value="builtin:device"${journalType === 'builtin:device' ? ' checked' : ''} /> builtin:device</label>
                <label><input type="radio" name="ps-cfg-journal-type" value="builtin:user"${journalType === 'builtin:user' ? ' checked' : ''} /> builtin:user</label>
                <label><input type="radio" name="ps-cfg-journal-type" value="custom"${journalType === 'custom' ? ' checked' : ''} /> Custom table</label>
            </div>
            <div class="ps-cfg-journal-custom${journalType !== 'custom' ? ' hidden' : ''}" style="margin-top:6px">
                <div class="ps-cfg-kv-inline"><label>keyspace</label><input type="text" class="ps-input ps-input-sm ps-cfg-jks" value="${this.#esc(journalCustom.keyspace || '')}" placeholder="my_keyspace" /></div>
                <div class="ps-cfg-kv-inline"><label>table</label><input type="text" class="ps-input ps-input-sm ps-cfg-jtbl" value="${this.#esc(journalCustom.table || '')}" placeholder="my_journal" /></div>
                <div class="ps-cfg-kv-inline"><label>id</label><input type="text" class="ps-input ps-input-sm ps-cfg-jid" value="${this.#esc(journalCustom.id || '')}" placeholder="deviceuuid" /></div>
            </div>
            <div class="ps-form-hint" style="margin-top:4px">After restart, the window refills from the journal on the next event per key.</div>
        </div>`;

        // -- Compact mode section --
        const compact = cfg.compact || {};
        const hasCompact = !!cfg.compact;
        html += `<div class="ps-cfg-section"><label><input type="checkbox" class="ps-cfg-compact-enable" ${hasCompact ? 'checked' : ''} /> Enable compact mode</label>
            <div class="ps-form-hint">Stores samples in fixed-size TypedArrays (Float64Array) instead of JSON objects. Much lower memory and GC pressure for numeric time-series.</div></div>`;

        const tsMode = compact.timestamp_mode || 'computed';
        html += `<div class="ps-cfg-compact-section${!hasCompact ? ' hidden' : ''}">
            <div class="ps-cfg-warning">⚠ Compact output is a <code>{timestamps: Float64Array, values: Float64Array}</code> struct — not a JSON array. Downstream steps must handle TypedArrays. Do not journal compact data through normal DB output.</div>

            <div class="ps-cfg-section"><label>Value type</label>
                <select class="ps-input ps-input-sm ps-cfg-compact-vtype">
                    <option value="float64"${(compact.value_type || 'float64') === 'float64' ? ' selected' : ''}>float64 (default)</option>
                    <option value="int32"${compact.value_type === 'int32' ? ' selected' : ''}>int32</option>
                    <option value="uint32"${compact.value_type === 'uint32' ? ' selected' : ''}>uint32</option>
                </select></div>

            <div class="ps-cfg-section"><label>Sample field (numeric value to store)</label>
                <input type="text" class="ps-input ps-input-sm ps-cfg-compact-samplefield" value="${this.#esc(compact.sample_field || '')}" placeholder="value" /></div>

            <div class="ps-cfg-section"><label>Timestamp mode</label>
                <select class="ps-input ps-input-sm ps-cfg-compact-tsmode">
                    <option value="computed"${tsMode === 'computed' ? ' selected' : ''}>computed (derive from window boundaries)</option>
                    <option value="field"${tsMode === 'field' ? ' selected' : ''}>field (read from message)</option>
                    <option value="fixed_interval"${tsMode === 'fixed_interval' ? ' selected' : ''}>fixed_interval (regular spacing)</option>
                </select></div>

            <div class="ps-cfg-compact-computed${tsMode !== 'computed' ? ' hidden' : ''}">
                <div class="ps-form-hint">Timestamps are computed from window start time and sample index.</div></div>

            <div class="ps-cfg-compact-field-ts${tsMode !== 'field' ? ' hidden' : ''}">
                <div class="ps-cfg-section"><label>Timestamp field</label>
                    <input type="text" class="ps-input ps-input-sm ps-cfg-compact-tsfield" value="${this.#esc(compact.timestamp_field || '')}" placeholder="utc_timestamp_inms" /></div></div>

            <div class="ps-cfg-compact-fixed${tsMode !== 'fixed_interval' ? ' hidden' : ''}">
                <div class="ps-cfg-section"><label>Interval (ms)</label>
                    <input type="number" class="ps-input ps-input-sm ps-cfg-compact-interval" value="${compact.interval_ms ?? ''}" min="1" placeholder="1000" /></div></div>

            <div class="ps-cfg-section"><label><input type="checkbox" class="ps-cfg-compact-store-enable" ${compact.store ? 'checked' : ''} /> Periodic flush to Cassandra</label></div>
            <div class="ps-cfg-compact-store-section${!compact.store ? ' hidden' : ''}">
                <div class="ps-cfg-kv-inline"><label>keyspace</label><input type="text" class="ps-input ps-input-sm ps-cfg-compact-store-ks" value="${this.#esc((compact.store || {}).keyspace || '')}" placeholder="my_keyspace" /></div>
                <div class="ps-cfg-kv-inline"><label>table</label><input type="text" class="ps-input ps-input-sm ps-cfg-compact-store-tbl" value="${this.#esc((compact.store || {}).table || '')}" placeholder="compact_samples" /></div>
                <div class="ps-cfg-kv-inline"><label>flush_interval (sec)</label><input type="number" class="ps-input ps-input-sm ps-cfg-compact-store-flush" value="${(compact.store || {}).flush_interval_sec ?? ''}" min="1" placeholder="60" /></div>
            </div>
        </div>`;

        return html;
    }

    #readWindowForm(panel) {
        const cfg = {};
        const key = panel.querySelector('.ps-cfg-wkey')?.value?.trim();
        if (key) cfg.key = key;
        const durVal = panel.querySelector('.ps-cfg-wdur-val')?.value?.trim();
        const durUnit = panel.querySelector('.ps-cfg-wdur-unit')?.value || 'h';
        if (durVal) cfg.window = `${durVal}${durUnit}`;
        const tsField = panel.querySelector('.ps-cfg-wts')?.value?.trim();
        if (tsField) cfg.timestamp_field = tsField;
        const fields = this.#readListItems(panel.querySelector('.ps-cfg-wfield-list'));
        if (fields.length) cfg.fields = fields;
        const merge = panel.querySelector('.ps-cfg-merge-strategy')?.value || 'collect';
        cfg.merge = merge;
        if (merge === 'concat_field') {
            const mf = panel.querySelector('.ps-cfg-wmerge-field')?.value?.trim();
            if (mf) cfg.merge_field = mf;
        }
        const mergeAs = panel.querySelector('.ps-cfg-wmerge-as')?.value?.trim();
        if (mergeAs) cfg.merge_as = mergeAs;
        const maxE = parseInt(panel.querySelector('.ps-cfg-wmax')?.value, 10);
        if (maxE > 0 && maxE !== 1000) cfg.max_entries = maxE;
        const journalType = panel.querySelector('input[name="ps-cfg-journal-type"]:checked')?.value;
        if (journalType === 'builtin:device' || journalType === 'builtin:user') {
            cfg.journal = journalType;
        } else if (journalType === 'custom') {
            const ks = panel.querySelector('.ps-cfg-jks')?.value?.trim();
            const tbl = panel.querySelector('.ps-cfg-jtbl')?.value?.trim();
            const id = panel.querySelector('.ps-cfg-jid')?.value?.trim();
            if (ks && tbl && id) cfg.journal = { keyspace: ks, table: tbl, id };
        }
        const debounce = parseInt(panel.querySelector('.ps-cfg-wdebounce')?.value, 10);
        if (debounce > 0 && debounce !== 2000) cfg.backfill_debounce_ms = debounce;

        // Compact mode
        if (panel.querySelector('.ps-cfg-compact-enable')?.checked) {
            const compact = {};
            const vtype = panel.querySelector('.ps-cfg-compact-vtype')?.value;
            if (vtype && vtype !== 'float64') compact.value_type = vtype;
            const sf = panel.querySelector('.ps-cfg-compact-samplefield')?.value?.trim();
            if (sf) compact.sample_field = sf;
            const tsMode = panel.querySelector('.ps-cfg-compact-tsmode')?.value || 'computed';
            compact.timestamp_mode = tsMode;
            if (tsMode === 'field') {
                const tsf = panel.querySelector('.ps-cfg-compact-tsfield')?.value?.trim();
                if (tsf) compact.timestamp_field = tsf;
            } else if (tsMode === 'fixed_interval') {
                const intv = parseInt(panel.querySelector('.ps-cfg-compact-interval')?.value, 10);
                if (intv > 0) compact.interval_ms = intv;
            }
            if (panel.querySelector('.ps-cfg-compact-store-enable')?.checked) {
                const store = {};
                const sks = panel.querySelector('.ps-cfg-compact-store-ks')?.value?.trim();
                if (sks) store.keyspace = sks;
                const stbl = panel.querySelector('.ps-cfg-compact-store-tbl')?.value?.trim();
                if (stbl) store.table = stbl;
                const flush = parseInt(panel.querySelector('.ps-cfg-compact-store-flush')?.value, 10);
                if (flush > 0) store.flush_interval_sec = flush;
                compact.store = store;
            }
            cfg.compact = compact;
        }

        return cfg;
    }

    // -- Shared form helpers --
    #kvRowHtml(key, val, kPlaceholder = 'key', vPlaceholder = 'value') {
        return `<div class="ps-cfg-kv-row"><input type="text" class="ps-input ps-input-sm" value="${this.#esc(key)}" placeholder="${this.#esc(kPlaceholder)}" /><span>→</span><input type="text" class="ps-input ps-input-sm" value="${this.#esc(val)}" placeholder="${this.#esc(vPlaceholder)}" /><button class="btn-icon ps-cfg-row-remove">✕</button></div>`;
    }

    // Rename row: picker for source field (from), free text for renamed target.
    #renameKvRowHtml(from, to) {
        return `<div class="ps-cfg-kv-row ps-cfg-rename-row">${this.#fieldPickerCellHtml('ps-cfg-rename-from', from, 'source field')}<span>→</span><input type="text" class="ps-input ps-input-sm ps-cfg-rename-to" value="${this.#esc(to)}" placeholder="renamed to" /><button class="btn-icon ps-cfg-row-remove">✕</button></div>`;
    }

    // List item using a field picker (map.omit, map.pick).
    #pickerListItemHtml(val) {
        return `<div class="ps-cfg-list-item">${this.#fieldPickerCellHtml('ps-cfg-list-value', val, 'field')}<button class="btn-icon ps-cfg-row-remove">✕</button></div>`;
    }

    #setRowHtml(key, val, isDynamic) {
        return `<div class="ps-cfg-set-row"><input type="text" class="ps-input ps-input-sm ps-cfg-set-key" value="${this.#esc(key)}" placeholder="field.path" /><span>=</span><input type="text" class="ps-input ps-input-sm ps-cfg-set-val" value="${this.#esc(val)}" placeholder="value" /><label title="From message field"><input type="checkbox" class="ps-cfg-set-dyn" ${isDynamic ? 'checked' : ''} />\${}</label><button class="btn-icon ps-cfg-row-remove">✕</button></div>`;
    }

    #listItemHtml(val) {
        return `<div class="ps-cfg-list-item"><input type="text" class="ps-input ps-input-sm" value="${this.#esc(val)}" placeholder="field" /><button class="btn-icon ps-cfg-row-remove">✕</button></div>`;
    }

    #addKvRow(container, key, val) {
        if (!container) return;
        container.insertAdjacentHTML('beforeend', this.#kvRowHtml(key, val));
        container.lastElementChild.querySelector('.ps-cfg-row-remove').onclick = (e) => e.target.closest('.ps-cfg-kv-row').remove();
    }

    #addRenameRow(container) {
        if (!container) return;
        container.insertAdjacentHTML('beforeend', this.#renameKvRowHtml('', ''));
        container.lastElementChild.querySelector('.ps-cfg-row-remove').onclick = (e) => e.target.closest('.ps-cfg-kv-row').remove();
    }

    #addSetRow(container, key, val, isDyn) {
        if (!container) return;
        container.insertAdjacentHTML('beforeend', this.#setRowHtml(key, val, isDyn));
        container.lastElementChild.querySelector('.ps-cfg-row-remove').onclick = (e) => e.target.closest('.ps-cfg-set-row').remove();
    }

    #addListItem(container, val) {
        if (!container) return;
        container.insertAdjacentHTML('beforeend', this.#listItemHtml(val));
        container.lastElementChild.querySelector('.ps-cfg-row-remove').onclick = (e) => e.target.closest('.ps-cfg-list-item').remove();
    }

    #addPickerListItem(container) {
        if (!container) return;
        container.insertAdjacentHTML('beforeend', this.#pickerListItemHtml(''));
        container.lastElementChild.querySelector('.ps-cfg-row-remove').onclick = (e) => e.target.closest('.ps-cfg-list-item').remove();
    }

    #readKvRows(container) {
        const obj = {};
        if (!container) return obj;
        for (const row of container.querySelectorAll('.ps-cfg-kv-row')) {
            const inputs = row.querySelectorAll('input[type="text"]');
            const k = inputs[0]?.value?.trim();
            const v = inputs[1]?.value?.trim();
            if (k) obj[k] = v || '';
        }
        return obj;
    }

    #readListItems(container) {
        if (!container) return [];
        return [...container.querySelectorAll('.ps-cfg-list-item input[type="text"]')]
            .map(i => i.value.trim()).filter(Boolean);
    }

    #readPickerListItems(container) {
        if (!container) return [];
        return [...container.querySelectorAll('.ps-cfg-list-item .ps-cfg-list-value')]
            .map(i => i.value.trim()).filter(Boolean);
    }

    // ---- Utilities ----

    #setStatus(text) {
        const el = this.shadowRoot.getElementById('ps-status');
        if (el) el.textContent = text;
    }

    #esc(str) {
        return escapeHtmlAttribute(str);
    }

    // ELM refusals carry a machine-readable `code` (e.g. 409 PIPELINE_INCOMPLETE) in the
    // body; branch on that, never on the message text. Returns null when there is no body.
    async #errorBody(err) {
        try { return await err?.response?.json() ?? null; }
        catch { return null; }
    }

    async #confirm(message) {
        return new Promise(resolve => {
            const overlay = document.createElement('div');
            overlay.className = 'ps-confirm-overlay';
            overlay.innerHTML = `<div class="ps-confirm-dialog">
                <p>${this.#esc(message)}</p>
                <div class="ps-confirm-actions">
                    <button class="btn" id="ps-confirm-cancel">Cancel</button>
                    <button class="btn btn-danger" id="ps-confirm-ok">Confirm</button>
                </div>
            </div>`;
            this.shadowRoot.appendChild(overlay);
            overlay.querySelector('#ps-confirm-cancel').onclick = () => { overlay.remove(); resolve(false); };
            overlay.querySelector('#ps-confirm-ok').onclick = () => { overlay.remove(); resolve(true); };
        });
    }

    #showAlert(message) {
        const overlay = document.createElement('div');
        overlay.className = 'ps-confirm-overlay';
        overlay.innerHTML = `<div class="ps-confirm-dialog ps-alert-dialog">
            <pre class="ps-alert-content">${this.#esc(message)}</pre>
            <div class="ps-confirm-actions">
                <button class="btn btn-primary" id="ps-alert-ok">OK</button>
            </div>
        </div>`;
        this.shadowRoot.appendChild(overlay);
        overlay.querySelector('#ps-alert-ok').onclick = () => overlay.remove();
        overlay.querySelector('#ps-alert-ok').focus();
    }

    #close() {
        this.#disconnectSSE();
        this.emit({ type: 'hs-close-pipeline-sandbox' });
    }

    // Scoped to this component's own host element (not <html>/<body>) so the
    // main dashboard is never affected — see :host(.hs-theme-dark) in
    // pipeline-sandbox.css. Custom properties set there inherit down into
    // every nested child (ml-models/ml-labeling/help-*), so those
    // components need no theme-specific code of their own.
    #applyTheme(theme) {
        this.classList.toggle('hs-theme-dark', theme === 'dark');
        const btn = this.shadowRoot.getElementById('ps-theme-toggle-btn');
        if (btn) {
            btn.textContent = theme === 'dark' ? '🌙' : '☀️';
            btn.title = theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme';
        }
    }

    // ==== Concept help panel (tier 2) ==========================================
    // Docked drawer, not a modal — created once and toggled thereafter so a
    // developer can keep it open while editing the pipeline it explains.

    #getConceptPanel() {
        if (!this.#conceptPanel) {
            this.#conceptPanel = document.createElement('hs-concept-panel');
            this.shadowRoot.appendChild(this.#conceptPanel);
        }
        return this.#conceptPanel;
    }

    #toggleConceptPanel() {
        this.#getConceptPanel().toggle();
    }

    // Deep-link support (docs task 5): `#help=<key>` in the URL opens the
    // panel straight to that entry once the registry has loaded. Cheap,
    // additive — only acts while the sandbox is already mounted, and does
    // nothing if the hash is absent or unresolvable.
    #openDeepLinkedConcept() {
        const match = /^#help=(.+)$/.exec(location.hash);
        if (!match) return;
        const key = decodeURIComponent(match[1]);
        this.#getConceptPanel().openKey(key);
    }

    // ==== Explain panel (tier 3) ================================================
    // Contextual, computed-per-configuration explanations
    // (docs/CONTEXTUAL_EXPLAINERS_DESIGN.md,
    // handoff/frontend/{tier3-explain-ui,tier3-pipeline-walkthrough}.md).
    // `concurrency_and_backpressure` and `pipeline` (the step-by-step
    // walkthrough — fields/cardinality/undefined-field detection) are both
    // wired here; `inference_schedule` is wired from the ML wizard instead,
    // since its context (a manifest draft) lives there.

    #getExplainPanel() {
        if (!this.#explainPanel) {
            this.#explainPanel = document.createElement('hs-explain-panel');
            this.shadowRoot.appendChild(this.#explainPanel);
            this.onEvent('hs-explain-nav', (detail) => this.#handleExplainNav(detail), this.#explainPanel);
        }
        return this.#explainPanel;
    }

    // concepts[]/help_keys[] links reuse the tier-1/tier-2 surfaces already
    // built rather than a second lookup (tier3-explain-ui.md task 2).
    #handleExplainNav({ kind, key }) {
        if (kind === 'concept') {
            this.#getConceptPanel().openKey(key);
            return;
        }
        if (kind === 'help') {
            const entry = HelpRegistry.getEntry(key);
            if (!entry?.long) return;
            let modal = this.shadowRoot.querySelector('hs-help-modal');
            if (!modal) {
                modal = document.createElement('hs-help-modal');
                this.shadowRoot.appendChild(modal);
            }
            modal.open({ key, entry });
        }
    }

    // Checked once per load — hides the explain affordances entirely rather
    // than showing a button that 400s, mirroring hs-help-icon's "hidden
    // until resolvable" precedent. Subjects are driven from the API, not
    // hardcoded (task 1) — this only decides whether OUR specific attachment
    // points light up, not a general subject menu. Per-row icons open the
    // richer "pipeline" walkthrough (fields/cardinality/undefined-field
    // detection), not "concurrency_and_backpressure" — the latter is only
    // reachable from the toolbar-level button.
    async #refreshExplainAvailability() {
        const [concurrency, pipeline] = await Promise.all([
            ExplainClient.getSubject('concurrency_and_backpressure'),
            ExplainClient.getSubject('pipeline'),
        ]);
        this.#concurrencyExplainSubject = concurrency;
        this.#pipelineExplainSubject = pipeline;
        this.shadowRoot.getElementById('ps-explain-concurrency-btn')?.classList.toggle('hidden', !concurrency);
        this.shadowRoot.getElementById('ps-explain-pipeline-btn')?.classList.toggle('hidden', !pipeline);
        this.shadowRoot.getElementById('ps-detail-explain-concurrency-btn')?.classList.toggle('hidden', !concurrency);
        this.shadowRoot.getElementById('ps-detail-explain-pipeline-btn')?.classList.toggle('hidden', !pipeline);
        for (const btn of this.shadowRoot.querySelectorAll('.ps-step-explain')) {
            btn.classList.toggle('hidden', !pipeline);
        }
    }

    // Same per-row read as #buildConfigFromForm's step loop, except tap
    // wrappers are unwrapped to their inner step — both "pipeline" and
    // concurrency_and_backpressure's normalize_step() destructure `.plugin`
    // directly and have no tap-wrapper handling, so a `{ tap: [...] }` entry
    // would 500 as "plugin not found".
    #collectStepsForExplain() {
        const steps = [];
        for (const row of this.shadowRoot.querySelectorAll('.ps-step-row')) {
            const ref = row.querySelector('.ps-step-input')?.value?.trim();
            if (!ref) continue;
            const ov = row._stepOverrides || {};
            const hasOverrides = ov.opts || ov.concurrency || ov.batch_size || ov.batch_ms || ov.watchdog_ms;
            steps.push(hasOverrides ? this.#buildAnnotatedStep(ref, ov) : ref);
        }
        return steps;
    }

    #buildConcurrencyExplainContext() {
        const sd = this.shadowRoot;
        const hwm = parseInt(sd.getElementById('ps-form-hwm')?.value, 10);
        return {
            steps: this.#collectStepsForExplain(),
            highWaterMark: hwm > 0 ? hwm : 16,
            serialPipeline: !!sd.getElementById('ps-form-serial')?.checked,
            namespace: this.#getEffectiveNamespace() || null,
        };
    }

    // "pipeline" only requires `steps` (docs/handoff/frontend/
    // tier3-pipeline-walkthrough.md) — no highWaterMark/serialPipeline/namespace.
    #buildPipelineExplainContext() {
        return { steps: this.#collectStepsForExplain() };
    }

    #openConcurrencyExplain() {
        this.#trackedExplainSubject = 'concurrency_and_backpressure';
        this.#getExplainPanel().open({
            subject: 'concurrency_and_backpressure',
            subjectTitle: 'Concurrency & back-pressure',
            context: this.#buildConcurrencyExplainContext(),
        });
        this.#observeConcurrencyContext();
    }

    // The primary "highest-value surface" (per the handoff) — a step-by-step
    // account of what the pipeline does, field/cardinality tracking and
    // undefined-field findings. `focusRow`'s position (0-based) is used as a
    // step_index fallback since step_id is a server-computed hash unknown
    // until the response arrives — matches fold.js#assign_step_ids, which is
    // also 0-based.
    #openPipelineWalkthrough({ focusRow } = {}) {
        const container = this.shadowRoot.getElementById('ps-pipeline-steps');
        const focusStepIndex = focusRow ? Array.from(container.children).indexOf(focusRow) : null;
        this.#trackedExplainSubject = 'pipeline';
        this.#getExplainPanel().open({
            subject: 'pipeline',
            subjectTitle: 'Pipeline walkthrough',
            context: this.#buildPipelineExplainContext(),
            focusStepIndex: focusStepIndex != null && focusStepIndex >= 0 ? focusStepIndex : null,
        });
        this.#observeConcurrencyContext();
    }

    // Live recomputation (task 5): a MutationObserver on the step list is one
    // generic hook instead of threading an "explain changed" call through
    // every add/remove/reorder/override-save call site individually — the
    // step-config popover's Apply already flips a style attribute on save, so
    // even override edits are caught by `attributes: true`. Shared by both
    // subjects tracked from this form; `#trackedExplainSubject` decides which
    // context shape to rebuild. updateContext() itself no-ops unless the
    // panel is open and tracking a subject.
    #observeConcurrencyContext() {
        if (this.#concurrencyObserver) return;
        const sd = this.shadowRoot;
        const container = sd.getElementById('ps-pipeline-steps');
        if (!container) return;
        const notify = () => {
            const context = this.#trackedExplainSubject === 'pipeline'
                ? this.#buildPipelineExplainContext()
                : this.#buildConcurrencyExplainContext();
            this.#getExplainPanel().updateContext(context);
        };
        this.#concurrencyObserver = new MutationObserver(notify);
        this.#concurrencyObserver.observe(container, { childList: true, subtree: true, attributes: true, characterData: true });
        this.onEvent('input', notify, sd.getElementById('ps-form-hwm'));
        this.onEvent('change', notify, sd.getElementById('ps-form-serial'));
    }

    // ---- Explain from the saved Detail view (read-only, no live tracking needed) ----

    // Detail reads the saved wire-format config, which really does carry `{tap:[...]}`
    // wrappers — unlike #collectStepsForExplain's live-form rows, which never store one
    // in the first place (tap-wrapping only happens at save time).
    #buildStepsFromSavedConfig() {
        const steps = this.#selectedPipeline?.config?.pipeline || [];
        const out = [];
        for (const step of steps) {
            if (typeof step === 'string') { out.push(step); continue; }
            if (step?.tap) { out.push(step.tap[0]); continue; }
            if (step?.plugin) { out.push(step); continue; }
        }
        return out.filter(Boolean);
    }

    #buildDetailPipelineExplainContext() {
        return { steps: this.#buildStepsFromSavedConfig() };
    }

    #buildDetailConcurrencyExplainContext() {
        const cfg = this.#selectedPipeline?.config || {};
        return {
            steps: this.#buildStepsFromSavedConfig(),
            highWaterMark: cfg.highWaterMark || 16,
            serialPipeline: !!cfg.serialPipeline,
            namespace: this.#getEffectiveNamespace() || null,
        };
    }

    #openDetailPipelineWalkthrough() {
        this.#getExplainPanel().open({
            subject: 'pipeline',
            subjectTitle: 'Pipeline walkthrough',
            context: this.#buildDetailPipelineExplainContext(),
        });
    }

    #openDetailConcurrencyExplain() {
        this.#getExplainPanel().open({
            subject: 'concurrency_and_backpressure',
            subjectTitle: 'Concurrency & back-pressure',
            context: this.#buildDetailConcurrencyExplainContext(),
        });
    }

    // ---- Evidence tab (Detail view) — run a bundle's test_data, show assertion outcomes ----
    // docs/FEATURE_BUNDLE_EXCHANGE_DESIGN.md §4 via POST /sandbox/bundles/:bundle_id/run
    // (confirmed against lib/elm_api.js + lib/bundle/evidence.js, not just the design doc).

    async #runBundleEvidence() {
        const p = this.#selectedPipeline;
        if (!p?.bundle_id || this.#isIncompletePipeline(p)) return;
        const ns = this.#getEffectiveNamespace();
        await this.#withLoading(async () => {
            try {
                const res = await this.fetch(
                    `${this.#baseUrl}/sandbox/bundles/${encodeURIComponent(p.bundle_id)}/run?namespace=${encodeURIComponent(ns)}`,
                    { method: 'POST', payload: {}, expectedStatuses: [409] },
                );
                this.#evidenceRuns = res?.runs || [];
            } catch (err) {
                Logger.error('Bundle evidence run failed: ', err);
                const body = await this.#errorBody(err);
                this.#evidenceRuns = {
                    error: body?.code === 'PIPELINE_INCOMPLETE'
                        ? `a pipeline in this bundle is incomplete — ${body.error || 'its artifacts did not restore'}`
                        : (err.message || String(err)),
                };
            }
            this.#renderEvidencePanel();
        });
    }

    #renderEvidenceRun(run) {
        const assertions = Array.isArray(run.assertions) ? run.assertions : [];
        const evaluated = assertions.length;
        const passed = assertions.filter(a => a.pass).length;
        const noInput = Number(run.input_count) === 0;
        // A run that evaluated nothing is not a pass, whatever run.pass says.
        const noEvidence = evaluated === 0 || noInput;
        const reason = [
            noInput ? 'nothing reached the pipeline' : '',
            evaluated === 0 ? 'no assertions were evaluated' : '',
        ].filter(Boolean).join('; ');

        const chipClass = noEvidence
            ? 'ps-bundle-disposition-none'
            : (run.pass ? 'ps-bundle-disposition-applied' : 'ps-bundle-disposition-carried');
        const chipLabel = noEvidence ? 'no evidence' : (run.pass ? 'assertions passed' : 'assertions failed');

        const assertionRows = assertions.map(a => `<div class="ps-bundle-row${a.pass ? '' : ' ps-bundle-row-error'}">
            <span class="ps-bundle-row-type">${this.#esc(a.kind || '')}</span>
            <code>${a.pass ? 'pass' : 'fail'}</code>
            ${a.path ? `<code>${this.#esc(a.path)}</code>` : ''}
            ${a.op ? `${this.#esc(a.op)} ${this.#esc(JSON.stringify(a.value))}` : ''}
            <div class="ps-bundle-row-reason">actual: ${this.#esc(JSON.stringify(a.actual))}${a.error ? ` — ${this.#esc(a.error)}` : ''}</div>
        </div>`).join('');
        const guardRow = noEvidence
            ? `<div class="ps-bundle-row ps-bundle-row-warn">
                <span class="ps-bundle-row-type">no evidence</span>
                <div class="ps-bundle-row-reason">This run establishes nothing — ${this.#esc(reason)}.</div>
            </div>`
            : '';

        return `<div class="ps-bundle-section">
            <div class="ps-bundle-section-title">
                <code>${this.#esc(run.pipeline)}</code> / <code>${this.#esc(run.test_data)}</code>
                <span class="ps-bundle-disposition-chip ${chipClass}" title="Assertions cover the pipeline's output stream only. They do not check side effects such as persisted rows or emitted telemetry, so a passing run does not prove your data was written.">${chipLabel}</span>
            </div>
            <div class="ps-form-hint">assertions ${evaluated} evaluated, ${passed} passed \u2014 input ${run.input_count} \u2192 output ${run.output_count}, dropped ${run.dropped}, errors ${run.errors}, ${run.duration_ms}ms \u2014 artifact_version <code>${this.#esc(run.artifact_version)}</code></div>
            <div class="ps-bundle-rows">${guardRow}${assertionRows}</div>
        </div>`;
    }

    #renderEvidencePanel() {
        const panel = this.shadowRoot.getElementById('ps-evidence-panel');
        if (!panel) return;
        const p = this.#selectedPipeline;
        // Gate on state, not bundle_id — an incomplete pipeline that came from a bundle has a
        // bundle_id by definition, so the bundle check below would let it through.
        if (this.#isIncompletePipeline(p)) {
            const summary = this.#problemSummary(p);
            panel.innerHTML = `<div class="ps-empty-state">This pipeline is incomplete — its artifacts did not restore, so assertions cannot run against it.${summary ? ` ${this.#esc(summary)}` : ''}</div>`;
            return;
        }
        if (!p?.bundle_id) {
            panel.innerHTML = '<div class="ps-empty-state">This pipeline was not created by a feature bundle import — no assertions to run. Scratch pipelines can never produce export evidence.</div>';
            return;
        }

        const header = `<div class="ps-bundle-import-row">
            <span class="ps-form-hint">bundle_id <code>${this.#esc(p.bundle_id)}</code> \u2014 running executes every <code>test_data</code> artifact in this bundle, which may include other pipelines it created. Assertions cover the output stream only \u2014 they do not check side effects such as persisted rows or emitted telemetry.</span>
            <button id="ps-evidence-run-btn" class="btn btn-sm btn-primary">Run assertions</button>
        </div>`;

        if (this.#evidenceRuns?.error) {
            panel.innerHTML = header + this.#renderBundleError(`Run failed: ${this.#evidenceRuns.error}`);
        } else if (!this.#evidenceRuns) {
            panel.innerHTML = `${header}<div class="ps-empty-state">No results yet — click Run assertions.</div>`;
        } else if (!this.#evidenceRuns.length) {
            panel.innerHTML = `${header}<div class="ps-empty-state">This bundle has no test_data artifacts.</div>`;
        } else {
            const full = JSON.stringify(this.#evidenceRuns, null, 2);
            panel.innerHTML = `${header}
                ${this.#evidenceRuns.map(r => this.#renderEvidenceRun(r)).join('')}
                <div class="ps-bundle-raw-row">
                    <button class="btn btn-sm ps-bundle-copy-btn" data-label="Copy for agent" data-content="${this.#esc(full)}">Copy for agent</button>
                </div>`;
            this.#wireBundleResultButtons(panel);
        }
        panel.querySelector('#ps-evidence-run-btn')?.addEventListener('click', () => this.#runBundleEvidence());
    }

    // ==== Namespace Workspace ==================================================

    #initNamespace() {
        let saved = null;
        try { saved = localStorage.getItem('ps.namespace'); } catch { /* ignore */ }
        this.#namespace = (saved && NAMESPACE_RE.test(saved)) ? saved : this.#getDeveloperNamespace() || null;
    }

    async #setNamespace(ns) {
        const value = (ns || '').trim();
        if (value && !NAMESPACE_RE.test(value)) {
            this.#showAlert(`Invalid namespace "${value}". Must start with a letter, end with a letter or digit, and contain only letters, digits, ".", "-", "_".`);
            return false;
        }
        if (this.#currentView === 'form') {
            if (!await this.#confirmDiscardIfDirty('You have unsaved changes. Discard them and switch namespace?')) return false;
            this.#formSnapshot = null;
        }
        this.#namespace = value || null;
        try {
            if (value) localStorage.setItem('ps.namespace', value);
            else localStorage.removeItem('ps.namespace');
        } catch { /* ignore */ }
        this.#renderNamespaceLabel();
        this.#onNamespaceChanged();
        return true;
    }

    #renderNamespaceLabel() {
        const btn = this.shadowRoot.getElementById('ps-namespace-current');
        if (btn) btn.textContent = this.#getEffectiveNamespace() || '(none)';
        const badge = this.shadowRoot.getElementById('ps-schema-explorer-ns');
        if (badge) badge.textContent = this.#getEffectiveNamespace() || '';
    }

    #onNamespaceChanged() {
        // Views that already loaded need a re-render against the new namespace.
        if (this.#currentView === 'schema-explorer') {
            this.#refreshSchemaExplorer();
        } else if (this.#currentView === 'topology') {
            this.#loadTopology();
        } else if (this.#currentView === 'list') {
            this.#fetchPipelines();
        } else if (this.#currentView === 'detail' || this.#currentView === 'form') {
            // The selected pipeline/edit target may not exist in the new namespace.
            this.#backToList();
        }
    }

    async #refreshKnownNamespaces() {
        const set = new Set();
        for (const p of this.#pipelines || []) {
            if (p?.namespace) set.add(p.namespace);
        }
        try {
            const res = await this.fetch(`${this.#baseUrl}/schemas/_sandbox`);
            for (const c of res?.candidates || []) {
                if (c?.namespace) set.add(c.namespace);
            }
        } catch (err) {
            Logger.debug('sandbox namespace list unavailable: ', err?.message || err);
        }
        try {
            const res = await this.fetch(`${this.#baseUrl}/telemetry/sandbox/namespaces`);
            for (const n of res?.namespaces || []) set.add(n);
        } catch (err) {
            Logger.debug('telemetry namespace list unavailable: ', err?.message || err);
        }
        this.#knownNamespaces = set;
    }

    async #openNamespacePicker(anchor) {
        await this.#refreshKnownNamespaces();
        const rect = anchor.getBoundingClientRect();
        const overlay = document.createElement('div');
        overlay.className = 'ps-confirm-overlay';
        overlay.style.background = 'transparent';
        const known = Array.from(this.#knownNamespaces).sort();
        const current = this.#getEffectiveNamespace() || '';
        const items = known.map(ns => `
            <div class="ps-ns-popover-item${ns === current ? ' selected' : ''}" data-ns="${this.#esc(ns)}">
                <span>${this.#esc(ns)}</span>
                ${ns === this.#getDeveloperNamespace() ? '<span class="ps-ns-popover-item-badge">you</span>' : ''}
            </div>`).join('') || '<div class="ps-empty-state" style="padding:8px 12px">No known namespaces.</div>';
        overlay.innerHTML = `
            <div class="ps-ns-popover" style="top:${Math.round(rect.bottom + 4)}px; left:${Math.round(rect.left)}px">
                <div class="ps-ns-popover-title">Namespace workspace</div>
                <div class="ps-ns-popover-hint">Type a namespace or pick from the list.</div>
                <input id="ps-ns-input" class="ps-input ps-input-sm ps-ns-popover-input" value="${this.#esc(current)}" placeholder="e.g. alice.myfeature" spellcheck="false" />
                <div class="ps-ns-popover-list">${items}</div>
                <div class="ps-ns-popover-actions">
                    <button class="btn btn-sm" id="ps-ns-reset">Reset to default</button>
                    <button class="btn btn-sm" id="ps-ns-cancel">Cancel</button>
                    <button class="btn btn-sm btn-primary" id="ps-ns-apply">Apply</button>
                </div>
            </div>`;
        this.shadowRoot.appendChild(overlay);
        const input = overlay.querySelector('#ps-ns-input');
        input.focus();
        input.select();
        const close = () => overlay.remove();
        const apply = async () => {
            if (await this.#setNamespace(input.value)) close();
        };
        for (const item of overlay.querySelectorAll('.ps-ns-popover-item')) {
            item.addEventListener('click', () => { input.value = item.dataset.ns; apply(); });
        }
        overlay.querySelector('#ps-ns-cancel').onclick = close;
        overlay.querySelector('#ps-ns-reset').onclick = () => {
            input.value = this.#getDeveloperNamespace() || '';
            apply();
        };
        overlay.querySelector('#ps-ns-apply').onclick = apply;
        input.addEventListener('keydown', (ev) => {
            if (ev.key === 'Enter') apply();
            else if (ev.key === 'Escape') close();
        });
        overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
    }

    // ==== Schema Explorer (v2) =================================================

    async #ensureSchemaDomains() {
        if (this.#schemaCache.domainsLoaded) return this.#schemaExplorer.domains;
        try {
            const [domainsRes, commonRes] = await Promise.all([
                this.fetch(`${this.#baseUrl}/schemas`),
                this.fetch(`${this.#baseUrl}/schemas/common`).catch(() => null),
            ]);
            this.#schemaExplorer.domains = domainsRes?.domains || [];
            this.#schemaExplorer.common = commonRes || null;
            this.#schemaCache.domainsLoaded = true;
        } catch (err) {
            Logger.error('schemas fetch failed: ', err);
        }
        return this.#schemaExplorer.domains;
    }

    async #ensureSchemaTypes(domain) {
        if (!domain) return [];
        const cached = this.#schemaCache.typesByDomain.get(domain);
        if (cached) return cached;
        const params = new URLSearchParams();
        const ns = this.#getEffectiveNamespace();
        if (ns) params.set('namespace', ns);
        const q = params.toString() ? `?${params}` : '';
        try {
            const res = await this.fetch(`${this.#baseUrl}/schemas/${encodeURIComponent(domain)}/message-types${q}`);
            const list = res?.message_types || [];
            this.#schemaCache.typesByDomain.set(domain, list);
            return list;
        } catch (err) {
            Logger.error('message-types fetch failed: ', err);
            return [];
        }
    }

    async #ensureSchemaTypeDetail(domain, messageType) {
        if (!domain || !messageType) return null;
        const key = `${domain}::${messageType}`;
        const cached = this.#schemaCache.typeDetails.get(key);
        if (cached !== undefined) return cached;
        const params = new URLSearchParams();
        const ns = this.#getEffectiveNamespace();
        if (ns) params.set('namespace', ns);
        const q = params.toString() ? `?${params}` : '';
        try {
            const res = await this.fetch(`${this.#baseUrl}/schemas/${encodeURIComponent(domain)}/message-types/${encodeURIComponent(messageType)}${q}`);
            this.#schemaCache.typeDetails.set(key, res || null);
            return res || null;
        } catch (err) {
            Logger.debug(`type detail fetch failed for ${key}: `, err);
            this.#schemaCache.typeDetails.set(key, null);
            return null;
        }
    }

    // ---- Outputs derivation ----

    #extractEmitTelemetryConfig(ref) {
        const m = String(ref || '').match(/^([^(]+)\((.*)\)\s*$/);
        if (!m) return { base: ref, cfg: null };
        const base = m[1].trim();
        if (!base.endsWith('.emit_telemetry')) return { base, cfg: null };
        try { return { base, cfg: JSON.parse(m[2]) }; }
        catch { return { base, cfg: null }; }
    }

    // Walks the pipeline steps array (already built by #buildConfigFromForm) and returns
    // the union of (domain, message_type) tuples emitted, with persist/publish defaults.
    #deriveOutputsFromSteps(steps) {
        const byKey = new Map();
        const visit = (ref) => {
            const { cfg } = this.#extractEmitTelemetryConfig(ref);
            if (!cfg || !cfg.domain || !cfg.message_type) return;
            const key = `${cfg.domain}::${cfg.message_type}`;
            const existing = byKey.get(key) || { domain: cfg.domain, message_type: cfg.message_type, persist: true, publish: true };
            if (cfg.persist === false) existing.persist = false;
            if (cfg.publish === false) existing.publish = false;
            byKey.set(key, existing);
        };
        for (const step of steps || []) {
            if (typeof step === 'string') visit(step);
            else if (step?.plugin) visit(step.plugin);
            else if (step?.tap) for (const t of step.tap) visit(typeof t === 'string' ? t : t?.plugin || '');
        }
        return Array.from(byKey.values());
    }

    #getFormOutputs() {
        // emit_telemetry config modal calls this to know which outputs the pipeline declares.
        // With auto-derivation, we scan the current step rows for other emit_telemetry configs.
        const outputs = [];
        const seen = new Set();
        for (const row of this.shadowRoot.querySelectorAll('.ps-step-row .ps-step-input')) {
            const { cfg } = this.#extractEmitTelemetryConfig(row.value);
            if (!cfg || !cfg.domain || !cfg.message_type) continue;
            const key = `${cfg.domain}::${cfg.message_type}`;
            if (seen.has(key)) continue;
            seen.add(key);
            outputs.push({ domain: cfg.domain, message_type: cfg.message_type });
        }
        return outputs;
    }

    async #showSchemaExplorer() {
        this.#showView('schema-explorer');
        await this.#refreshSchemaExplorer();
    }

    async #refreshSchemaExplorer() {
        await this.#withLoading(async () => {
            this.#schemaCache.domainsLoaded = false;
            this.#schemaCache.typesByDomain.clear();
            this.#schemaCache.typeDetails.clear();
            await this.#ensureSchemaDomains();
            if (!this.#schemaExplorer.selectedDomain && this.#schemaExplorer.domains.length) {
                this.#schemaExplorer.selectedDomain = this.#schemaExplorer.domains[0].name;
            }
            this.#renderExplorerDomains();
            this.#renderExplorerCommon();
            if (this.#schemaExplorer.selectedDomain) {
                await this.#loadExplorerTypes(this.#schemaExplorer.selectedDomain);
            }
            this.#refreshOrphanBadge().catch(err => Logger.debug('orphan badge refresh failed: ', err));
        });
    }

    async #refreshOrphanBadge() {
        const btn = this.shadowRoot.getElementById('ps-schema-explorer-orphans-btn');
        const countEl = this.shadowRoot.getElementById('ps-schema-explorer-orphans-count');
        if (!btn || !countEl) return;
        try {
            const res = await this.fetch(`${this.#baseUrl}/schemas/_sandbox/orphans`);
            const count = (res?.orphans || []).length;
            countEl.textContent = String(count);
            btn.classList.toggle('hidden', count === 0);
        } catch (err) {
            Logger.debug('orphans fetch failed: ', err);
            btn.classList.add('hidden');
        }
    }

    async #openOrphansModal() {
        let res;
        try {
            res = await this.fetch(`${this.#baseUrl}/schemas/_sandbox/orphans`);
        } catch (err) {
            this.#showAlert(`Failed to load orphans: ${err.message || err}`);
            return;
        }
        const orphans = res?.orphans || [];
        const overlay = document.createElement('div');
        overlay.className = 'ps-confirm-overlay';
        if (!orphans.length) {
            overlay.innerHTML = `<div class="ps-confirm-dialog">
                <p>No orphaned sandbox namespaces.</p>
                <div class="ps-confirm-actions"><button class="btn btn-primary" id="ps-orphans-close">Close</button></div>
            </div>`;
            this.shadowRoot.appendChild(overlay);
            overlay.querySelector('#ps-orphans-close').onclick = () => overlay.remove();
            return;
        }
        const rows = orphans.map(o => `<div class="ps-orphan-row" data-ns="${this.#esc(o.namespace)}">
            <div class="ps-orphan-info">
                <div class="ps-orphan-ns">${this.#esc(o.namespace)}</div>
                <div class="ps-orphan-count-inline">${o.candidate_count} candidate${o.candidate_count === 1 ? '' : 's'}</div>
            </div>
            <div class="ps-orphan-actions">
                <label class="ps-schema-editor-inline"><input type="checkbox" class="ps-orphan-purge-telemetry" checked /> also purge telemetry rows</label>
                <button class="btn btn-sm btn-danger ps-orphan-clean-btn">Delete</button>
            </div>
        </div>`).join('');
        overlay.innerHTML = `<div class="ps-confirm-dialog ps-alert-dialog" style="max-width:640px">
            <h3 style="margin-top:0">Orphaned sandbox namespaces</h3>
            <div class="ps-form-hint">Sandbox candidates that no active pipeline references.</div>
            <div class="ps-orphan-list">${rows}</div>
            <div class="ps-confirm-actions">
                <button class="btn" id="ps-orphans-close">Close</button>
            </div>
        </div>`;
        this.shadowRoot.appendChild(overlay);
        overlay.querySelector('#ps-orphans-close').onclick = () => overlay.remove();
        for (const row of overlay.querySelectorAll('.ps-orphan-row')) {
            const ns = row.dataset.ns;
            row.querySelector('.ps-orphan-clean-btn').onclick = async () => {
                const purgeTelemetry = row.querySelector('.ps-orphan-purge-telemetry').checked;
                await this.#cleanupSandboxNamespace(ns, { purgeTelemetry });
                row.remove();
                if (!overlay.querySelector('.ps-orphan-row')) overlay.remove();
                this.#refreshOrphanBadge();
            };
        }
    }

    #showOrphanCleanupModal(orphaned) {
        const ns = orphaned.namespace;
        const schemasCount = orphaned.schemas || (orphaned.candidates || []).length;
        const overlay = document.createElement('div');
        overlay.className = 'ps-confirm-overlay';
        overlay.innerHTML = `<div class="ps-confirm-dialog" style="max-width:520px">
            <h3 style="margin-top:0">Sandbox cleanup</h3>
            <p>Namespace <code>${this.#esc(ns)}</code> has no more active sandbox pipelines but retains <strong>${schemasCount}</strong> schema candidate${schemasCount === 1 ? '' : 's'}.</p>
            <label class="ps-form-row"><input type="checkbox" id="ps-orphan-delete-schemas" checked /> Delete ${schemasCount} sandbox schema candidate${schemasCount === 1 ? '' : 's'}</label>
            <label class="ps-form-row"><input type="checkbox" id="ps-orphan-purge-telemetry" checked /> Purge sandbox telemetry rows (journal + state)</label>
            <div class="ps-confirm-actions">
                <button class="btn" id="ps-orphan-cancel">Skip</button>
                <button class="btn btn-danger" id="ps-orphan-ok">Clean up</button>
            </div>
        </div>`;
        this.shadowRoot.appendChild(overlay);
        overlay.querySelector('#ps-orphan-cancel').onclick = () => overlay.remove();
        overlay.querySelector('#ps-orphan-ok').onclick = async () => {
            const deleteSchemas = overlay.querySelector('#ps-orphan-delete-schemas').checked;
            const purgeTelemetry = overlay.querySelector('#ps-orphan-purge-telemetry').checked;
            overlay.remove();
            await this.#cleanupSandboxNamespace(ns, { deleteSchemas, purgeTelemetry });
        };
    }

    async #cleanupSandboxNamespace(namespace, { deleteSchemas = true, purgeTelemetry = false } = {}) {
        await this.#withLoading(async () => {
            const messages = [];
            if (deleteSchemas) {
                try {
                    const res = await this.fetch(`${this.#baseUrl}/schemas/_sandbox/${encodeURIComponent(namespace)}`, { method: 'DELETE' });
                    messages.push(`deleted ${res?.deleted_count ?? 0} schema${res?.deleted_count === 1 ? '' : 's'}`);
                } catch (err) {
                    Logger.error('Sandbox schema cleanup failed: ', err);
                    messages.push(`schema cleanup failed: ${err.message || err}`);
                }
            }
            if (purgeTelemetry) {
                for (const kind of ['journal', 'state']) {
                    try {
                        const res = await this.fetch(`${this.#baseUrl}/telemetry/${kind}?namespace=${encodeURIComponent(namespace)}`, { method: 'DELETE' });
                        messages.push(`${kind}: purged ${res?.deleted ?? 0} rows`);
                    } catch (err) {
                        Logger.error(`Sandbox ${kind} cleanup failed: `, err);
                        messages.push(`${kind} cleanup failed: ${err.message || err}`);
                    }
                }
            }
            this.#setStatus(`Cleanup ${namespace}: ${messages.join('; ')}`);
        });
    }

    // ==== Telemetry drawer (step 5) ===========================================

    #toggleTelemetryDrawer(force) {
        const drawer = this.shadowRoot.getElementById('ps-telemetry-drawer');
        const willOpen = typeof force === 'boolean' ? force : drawer.classList.contains('collapsed');
        drawer.classList.toggle('collapsed', !willOpen);
        this.#telemetry.open = willOpen;
        if (willOpen) this.#initTelemetryDrawer();
    }

    #initTelemetryDrawer() {
        const sd = this.shadowRoot;
        const p = this.#selectedPipeline;

        // Prefill providerid / entity id from pipeline outputs if not set yet.
        if (!this.#telemetry.providerid || !this.#telemetry.entityId) {
            const outs = p?.config?.outputs || p?.outputs || [];
            if (outs.length && !this.#telemetry.domain) {
                this.#telemetry.domain = outs[0].domain || '';
                this.#telemetry.messageType = outs[0].message_type || '';
            }
        }
        sd.getElementById('ps-tel-providerid').value = this.#telemetry.providerid;
        sd.getElementById('ps-tel-entity-id').value = this.#telemetry.entityId;
        sd.getElementById('ps-tel-domain').value = this.#telemetry.domain || '';
        sd.getElementById('ps-tel-mt').value = this.#telemetry.messageType || '';
        this.#renderTelemetryPickerLabel();
        this.#renderTelemetryProviderLabel();
        this.#renderTelemetryEntityLabel();

        const openTypePicker = () => this.#openSchemaTypePicker({ title: 'Filter telemetry by message type', allowAny: true }, ({ domain, message_type }) => {
            this.#telemetry.domain = domain;
            this.#telemetry.messageType = message_type;
            sd.getElementById('ps-tel-domain').value = domain;
            sd.getElementById('ps-tel-mt').value = message_type;
            this.#renderTelemetryPickerLabel();
            this.#maybeAutoFetchTelemetry();
        });
        sd.getElementById('ps-tel-picker').onclick = openTypePicker;

        const openProviderPicker = () => this.#openProviderPicker(({ providerid, label }) => {
            this.#telemetry.providerid = providerid;
            this.#telemetry.providerLabel = label;
            sd.getElementById('ps-tel-providerid').value = providerid;
            this.#renderTelemetryProviderLabel();
            this.#maybeAutoFetchTelemetry();
        });
        sd.getElementById('ps-tel-provider-picker').onclick = openProviderPicker;

        const openEntityPicker = () => this.#openEntityPicker(({ id, label, entityType }) => {
            this.#telemetry.entityId = id;
            this.#telemetry.entityLabel = label;
            this.#telemetry.entityType = entityType;
            sd.getElementById('ps-tel-entity-id').value = id;
            this.#renderTelemetryEntityLabel();
            this.#maybeAutoFetchTelemetry();
        });
        sd.getElementById('ps-tel-entity-picker').onclick = openEntityPicker;

        this.#renderTelemetryRangeChips();
        this.#renderTelemetryModeButtons();
        this.#maybeAutoFetchTelemetry();
    }

    #maybeAutoFetchTelemetry() {
        if (!this.#telemetry.open) return;
        if (!this.#telemetry.providerid || !this.#telemetry.entityId) {
            this.#renderTelemetryNeedsSelection();
            return;
        }
        this.#fetchTelemetryRows();
    }

    #renderTelemetryNeedsSelection() {
        const container = this.shadowRoot.getElementById('ps-tel-rows');
        const countEl = this.shadowRoot.getElementById('ps-tel-count');
        if (container) container.innerHTML = '<div class="ps-empty-state">Choose a provider and entity to load telemetry.</div>';
        if (countEl) countEl.textContent = '';
    }

    #renderTelemetryPickerLabel() {
        const displayBtn = this.shadowRoot.getElementById('ps-tel-picker');
        if (!displayBtn) return;
        const dom = this.#telemetry.domain;
        const mt = this.#telemetry.messageType;
        if (dom && mt) displayBtn.innerHTML = this.#esc(`${dom} / ${mt}`);
        else if (dom) displayBtn.innerHTML = this.#esc(`${dom} / (any)`);
        else displayBtn.innerHTML = '<em>(any)</em>';
    }

    #renderTelemetryProviderLabel() {
        const btn = this.shadowRoot.getElementById('ps-tel-provider-picker');
        if (!btn) return;
        const id = this.#telemetry.providerid;
        const label = this.#telemetry.providerLabel;
        if (id && label) btn.innerHTML = `${this.#esc(label)} <span class="ps-picker-id">${this.#esc(id)}</span>`;
        else if (id) btn.innerHTML = this.#esc(id);
        else btn.innerHTML = '<em>Choose provider…</em>';
    }

    #renderTelemetryEntityLabel() {
        const btn = this.shadowRoot.getElementById('ps-tel-entity-picker');
        if (!btn) return;
        const id = this.#telemetry.entityId;
        const label = this.#telemetry.entityLabel;
        const et = this.#telemetry.entityType;
        if (id && label) btn.innerHTML = `${this.#esc(label)} <span class="ps-picker-id">${this.#esc(et ? `${et}:${id}` : id)}</span>`;
        else if (id) btn.innerHTML = this.#esc(id);
        else btn.innerHTML = '<em>Choose device or user…</em>';
    }

    async #openProviderPicker(onSelect) {
        let picker = this.shadowRoot.querySelector('hs-list-picker');
        if (!picker) {
            picker = document.createElement('hs-list-picker');
            this.shadowRoot.appendChild(picker);
        }
        const providers = (UserSession.get('providers') || []).slice();
        const shared = {
            getSearchText: p => [p.providername, p.displayname, p.orgname, p.orgdisplayname, p.providerid],
            renderItem: p => ({
                title: p.displayname || p.providername || p.providerid,
                subtitle: [p.orgname, p.orgdisplayname].filter(Boolean).join(' · '),
                badges: [p.providertype].filter(Boolean),
            }),
            getValue: p => p,
            emptyText: 'No providers available',
        };
        picker.open({
            title: 'Choose provider',
            tabs: [{ id: 'all', label: 'All', items: providers, ...shared }],
            onSelect: (p) => onSelect({
                providerid: p.providerid,
                label: p.displayname || p.providername || p.providerid,
            }),
        });
    }

    async #ensureEntityCache(kind) {
        if (this.#entityCache[kind]) return this.#entityCache[kind];
        if (kind === 'users') {
            // Merge homesight.users (which carries usertype + provider + deviceid) with
            // keycloak.user_profiles (which carries firstname/lastname/username) on iam_userid,
            // since neither source alone has enough to pick a user by name.
            const [users, profiles] = await Promise.all([
                this.fetch('/api/homesight/users').catch(err => {
                    Logger.debug('homesight.users fetch failed: ', err);
                    return [];
                }),
                this.fetch('/api/keycloak/user_profiles').catch(err => {
                    Logger.debug('keycloak.user_profiles fetch failed: ', err);
                    return [];
                }),
            ]);
            const byIam = new Map();
            for (const p of (Array.isArray(profiles) ? profiles : [])) {
                if (p?.iam_userid) byIam.set(p.iam_userid, p);
            }
            this.#entityCache.users = (Array.isArray(users) ? users : []).map(u => {
                const p = u?.iam_userid ? byIam.get(u.iam_userid) : null;
                return p ? {
                    ...u,
                    firstname: u.firstname || p.firstname,
                    lastname: u.lastname || p.lastname,
                    username: u.username || p.username,
                    email: u.email || p.email,
                } : u;
            });
            return this.#entityCache.users;
        }
        try {
            const rows = await this.fetch('/api/homesight/devices');
            this.#entityCache[kind] = Array.isArray(rows) ? rows : [];
        } catch (err) {
            Logger.debug(`entity fetch failed for ${kind}: `, err);
            this.#entityCache[kind] = [];
        }
        return this.#entityCache[kind];
    }

    async #openEntityPicker(onSelect) {
        let picker = this.shadowRoot.querySelector('hs-list-picker');
        if (!picker) {
            picker = document.createElement('hs-list-picker');
            this.shadowRoot.appendChild(picker);
        }
        const [devices, users] = await Promise.all([
            this.#ensureEntityCache('devices'),
            this.#ensureEntityCache('users'),
        ]);
        const deviceShared = {
            getSearchText: d => [d.deviceid, d.deviceuuid, d.modelnumber, d.displayname, d.providername, d.huma_id],
            renderItem: d => ({
                title: d.deviceid || d.deviceuuid || '(unknown device)',
                subtitle: [d.modelnumber, d.providername].filter(Boolean).join(' · '),
                badges: [d.deployment_state].filter(Boolean),
            }),
            getValue: d => ({ id: d.deviceuuid, label: d.deviceid || d.deviceuuid, entityType: 'device' }),
            emptyText: 'No devices available',
        };
        const userDisplayName = u => [u.firstname, u.lastname].filter(Boolean).join(' ');
        const userShared = {
            getSearchText: u => [u.firstname, u.lastname, u.username, u.user_id, u.email, u.huma_id, u.iam_userid, u.useruuid, u.providername, u.deviceid, u.usertype],
            renderItem: u => {
                const fullName = userDisplayName(u);
                const uname = u.username || u.user_id || u.email;
                const title = fullName || uname || u.huma_id || '(unknown user)';
                const subtitleParts = [];
                if (fullName && uname && uname !== fullName) subtitleParts.push(uname);
                if (u.providername) subtitleParts.push(u.providername);
                if (u.deviceid) subtitleParts.push(u.deviceid);
                return {
                    title,
                    subtitle: subtitleParts.join(' · '),
                    badges: [u.usertype].filter(Boolean),
                };
            },
            getValue: u => ({
                id: u.huma_id || u.useruuid,
                label: userDisplayName(u) || u.username || u.user_id || u.email || u.huma_id,
                entityType: 'user',
            }),
            emptyText: 'No users available',
        };
        picker.open({
            title: 'Choose device or user',
            tabs: [
                { id: 'devices', label: `Devices (${devices.length})`, items: devices, ...deviceShared },
                { id: 'users', label: `Users (${users.length})`, items: users, ...userShared },
            ],
            onSelect: (value) => onSelect(value),
        });
    }

    #renderTelemetryModeButtons() {
        const sd = this.shadowRoot;
        for (const btn of sd.querySelectorAll('.ps-tel-mode-btn')) {
            btn.classList.toggle('active', btn.dataset.mode === this.#telemetry.mode);
        }
        // Range chips are journal-only; hide when in state mode.
        sd.getElementById('ps-tel-range-chips').classList.toggle('hidden', this.#telemetry.mode !== 'journal');
    }

    #setTelemetryMode(mode) {
        this.#telemetry.mode = mode;
        this.#renderTelemetryModeButtons();
        this.#maybeAutoFetchTelemetry();
    }

    #renderTelemetryRangeChips() {
        const el = this.shadowRoot.getElementById('ps-tel-range-chips');
        const chips = [
            { label: 'Last 15m', ms: 15 * 60 * 1000 },
            { label: 'Last 1h', ms: 60 * 60 * 1000 },
            { label: 'Last 12h', ms: 12 * 60 * 60 * 1000 },
            { label: 'Last 24h', ms: 24 * 60 * 60 * 1000 },
        ];
        const startedAt = this.#selectedPipeline?.started_at;
        if (startedAt) chips.push({ label: 'Since start', ms: 'start' });
        el.innerHTML = chips.map(c => `<button class="btn btn-sm ps-tel-chip${c.label === this.#telemetry.rangeLabel ? ' active' : ''}" data-range="${c.ms}" data-label="${this.#esc(c.label)}">${this.#esc(c.label)}</button>`).join('');
        for (const b of el.querySelectorAll('.ps-tel-chip')) {
            b.onclick = () => {
                this.#telemetry.rangeMs = b.dataset.range === 'start' ? 'start' : Number(b.dataset.range);
                this.#telemetry.rangeLabel = b.dataset.label;
                this.#renderTelemetryRangeChips();
                this.#maybeAutoFetchTelemetry();
            };
        }
    }

    #syncTelemetryFiltersFromDom() {
        const sd = this.shadowRoot;
        this.#telemetry.providerid = sd.getElementById('ps-tel-providerid').value.trim();
        this.#telemetry.entityId = sd.getElementById('ps-tel-entity-id').value.trim();
        this.#telemetry.domain = sd.getElementById('ps-tel-domain').value.trim();
        this.#telemetry.messageType = sd.getElementById('ps-tel-mt').value.trim();
    }    async #fetchTelemetryRows() {
        this.#syncTelemetryFiltersFromDom();
        const t = this.#telemetry;
        if (!t.providerid || !t.entityId) {
            t.error = 'providerid and entity id are required';
            this.#renderTelemetryRows();
            return;
        }
        t.error = null;
        t.loading = true;
        this.#renderTelemetryRows();

        const params = new URLSearchParams();
        params.set('providerid', t.providerid);
        params.set('id', t.entityId);
        if (t.domain) params.set('domain', t.domain);
        if (t.domain && t.messageType) params.set('message_type', t.messageType);
        if (this.#selectedPipeline?.is_sandbox) {
            const ns = this.#selectedPipeline?.namespace || this.#getEffectiveNamespace();
            if (ns) params.set('namespace', ns);
        }
        if (t.mode === 'journal') {
            let since;
            if (t.rangeMs === 'start') since = this.#selectedPipeline?.started_at;
            else since = Date.now() - (t.rangeMs || 0);
            if (since) params.set('since', String(since));
            params.set('limit', '500');
            params.set('order', 'desc');
        }

        try {
            const res = await this.fetch(`${this.#baseUrl}/telemetry/${t.mode}?${params.toString()}`);
            t.rows = res?.rows || [];
        } catch (err) {
            t.error = `Fetch failed: ${err.message || err}`;
            t.rows = [];
        }
        t.loading = false;
        this.#renderTelemetryRows();
    }

    #renderTelemetryRows() {
        const t = this.#telemetry;
        const container = this.shadowRoot.getElementById('ps-tel-rows');
        const countEl = this.shadowRoot.getElementById('ps-tel-count');
        if (t.error) {
            container.innerHTML = `<div class="ps-form-result invalid">${this.#esc(t.error)}</div>`;
            countEl.textContent = '';
            return;
        }
        if (t.loading) {
            container.innerHTML = '<div class="ps-empty-state">Fetching…</div>';
            countEl.textContent = '';
            return;
        }
        if (!t.rows.length) {
            container.innerHTML = '<div class="ps-empty-state">No rows.</div>';
            countEl.textContent = '0 rows';
            return;
        }
        countEl.textContent = `${t.rows.length} row${t.rows.length === 1 ? '' : 's'}`;
        container.innerHTML = t.rows.map((r, i) => {
            const dom = this.#esc(r.domain || '');
            const mt = this.#esc(r.message_type || '');
            const ts = r.utc_timestamp_inms ? new Date(Number(r.utc_timestamp_inms)).toISOString().replace('T', ' ').slice(0, 19) : '';
            const isOpen = t.expanded.has(i);
            const payload = typeof r.payload === 'string' ? r.payload : JSON.stringify(r.payload || {}, null, 2);
            return `<div class="ps-tel-row" data-idx="${i}">
                <div class="ps-tel-row-head">
                    <span class="ps-tel-row-caret">${isOpen ? '▾' : '▸'}</span>
                    <span class="ps-tel-row-dom">${dom}</span>
                    <span class="ps-tel-row-mt">${mt}</span>
                    <span class="ps-tel-row-ts">${this.#esc(ts)}</span>
                </div>
                ${isOpen ? `<pre class="ps-tel-row-payload">${this.#esc(typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2))}</pre>` : ''}
            </div>`;
        }).join('');
        for (const row of container.querySelectorAll('.ps-tel-row')) {
            row.querySelector('.ps-tel-row-head').onclick = () => {
                const idx = Number(row.dataset.idx);
                if (t.expanded.has(idx)) t.expanded.delete(idx);
                else t.expanded.add(idx);
                this.#renderTelemetryRows();
            };
        }
    }

    #maybeAutoRefreshTelemetry() {
        const t = this.#telemetry;
        if (!t.open || !t.autoRefresh) return;
        if (t.pendingRefresh) return;
        t.pendingRefresh = setTimeout(() => {
            t.pendingRefresh = null;
            if (t.open && t.autoRefresh) this.#fetchTelemetryRows();
        }, 2000);
    }

    #renderExplorerDomains() {
        const el = this.shadowRoot.getElementById('ps-schema-explorer-domain-list');
        const domains = this.#schemaExplorer.domains;
        if (!domains.length) {
            el.innerHTML = '<div class="ps-empty-state">No schema domains available.</div>';
            return;
        }
        el.innerHTML = domains.map(d => {
            const selected = d.name === this.#schemaExplorer.selectedDomain ? ' selected' : '';
            const count = typeof d.message_type_count === 'number' ? d.message_type_count : '';
            return `<div class="ps-schema-explorer-item${selected}" data-domain="${this.#esc(d.name)}">
                <span class="ps-schema-explorer-item-name">${this.#esc(d.name)}</span>
                <span class="ps-schema-explorer-item-meta">${this.#esc(count)}</span>
            </div>`;
        }).join('');
        for (const item of el.querySelectorAll('.ps-schema-explorer-item')) {
            item.onclick = () => this.#selectExplorerDomain(item.dataset.domain);
        }
    }

    #renderExplorerCommon() {
        const wrap = this.shadowRoot.getElementById('ps-schema-explorer-common');
        const chips = this.shadowRoot.getElementById('ps-schema-explorer-common-chips');
        const props = this.#schemaExplorer.common?.properties || {};
        const names = Object.keys(props);
        if (!names.length) {
            wrap.classList.add('hidden');
            return;
        }
        wrap.classList.remove('hidden');
        chips.innerHTML = names.map(n => {
            const type = props[n]?.type || '';
            return `<span class="ps-schema-explorer-common-chip" title="${this.#esc(type)}">${this.#esc(n)}</span>`;
        }).join('');
    }

    async #selectExplorerDomain(name) {
        this.#schemaExplorer.selectedDomain = name;
        this.#schemaExplorer.selectedType = null;
        this.#schemaExplorer.detail = null;
        this.#renderExplorerDomains();
        this.#renderExplorerDetail();
        await this.#loadExplorerTypes(name);
    }

    async #loadExplorerTypes(domain) {
        this.#schemaCache.typesByDomain.delete(domain);
        const list = await this.#ensureSchemaTypes(domain);
        this.#schemaExplorer.types = list;
        this.#renderExplorerTypes();
    }

    #renderExplorerTypes() {
        const el = this.shadowRoot.getElementById('ps-schema-explorer-type-list');
        const filter = this.#schemaExplorer.filter;
        const types = this.#schemaExplorer.types.filter(t => !filter || String(t.name).toLowerCase().includes(filter));
        if (!types.length) {
            el.innerHTML = `<div class="ps-empty-state">${filter ? 'No matches.' : 'No message types.'}</div>`;
            return;
        }
        el.innerHTML = types.map(t => {
            const selected = t.name === this.#schemaExplorer.selectedType ? ' selected' : '';
            const badges = [];
            if (t.sandbox) badges.push('<span class="ps-schema-explorer-badge ps-schema-explorer-badge-overlay">overlay</span>');
            else badges.push('<span class="ps-schema-explorer-badge ps-schema-explorer-badge-prod">prod</span>');
            return `<div class="ps-schema-explorer-item${selected}" data-type="${this.#esc(t.name)}">
                <span class="ps-schema-explorer-item-name">${this.#esc(t.name)}</span>
                <span class="ps-schema-explorer-item-badges">${badges.join('')}</span>
            </div>`;
        }).join('');
        for (const item of el.querySelectorAll('.ps-schema-explorer-item')) {
            item.onclick = () => this.#selectExplorerType(item.dataset.type);
        }
    }

    async #selectExplorerType(type) {
        this.#schemaExplorer.selectedType = type;
        this.#schemaExplorer.detail = null;
        this.#renderExplorerTypes();
        this.#renderExplorerDetail('Loading…');
        try {
            const params = new URLSearchParams();
            const ns = this.#getEffectiveNamespace();
            if (ns) params.set('namespace', ns);
            const q = params.toString() ? `?${params}` : '';
            const domain = this.#schemaExplorer.selectedDomain;
            const detail = await this.fetch(`${this.#baseUrl}/schemas/${encodeURIComponent(domain)}/message-types/${encodeURIComponent(type)}${q}`);
            this.#schemaExplorer.detail = detail || null;
        } catch (err) {
            Logger.error('message-type detail fetch failed: ', err);
            this.#renderExplorerDetail(`Failed to load: ${err?.message || err}`);
            return;
        }
        this.#renderExplorerDetail();
    }

    #renderExplorerDetail(placeholder = null) {
        const actions = this.shadowRoot.getElementById('ps-schema-explorer-detail-actions');
        const titleEl = this.shadowRoot.getElementById('ps-schema-explorer-detail-title');
        const el = this.shadowRoot.getElementById('ps-schema-explorer-detail');

        // Editor takes over the detail column when active.
        if (this.#schemaEditor) {
            titleEl.textContent = this.#schemaEditor.isNew
                ? `New: ${this.#schemaEditor.domain} / (new)`
                : `Edit: ${this.#schemaEditor.domain} / ${this.#schemaEditor.messageType}`;
            actions.innerHTML = `
                <button class="btn btn-sm" id="ps-schema-editor-toggle-mode">${this.#schemaEditor.mode === 'raw' ? 'Form' : 'Raw JSON'}</button>
                <button class="btn btn-sm" id="ps-schema-editor-cancel">Cancel</button>
                <button class="btn btn-sm btn-primary" id="ps-schema-editor-save">Save</button>`;
            this.#renderExplorerEditor(el);
            actions.querySelector('#ps-schema-editor-toggle-mode').onclick = () => this.#toggleExplorerEditorMode();
            actions.querySelector('#ps-schema-editor-cancel').onclick = () => this.#closeExplorerEditor();
            actions.querySelector('#ps-schema-editor-save').onclick = () => this.#saveExplorerEditor();
            return;
        }

        titleEl.textContent = 'Details';

        if (placeholder) {
            actions.innerHTML = '';
            el.innerHTML = `<div class="ps-empty-state">${this.#esc(placeholder)}</div>`;
            return;
        }
        const d = this.#schemaExplorer.detail;
        if (!d) {
            actions.innerHTML = '';
            el.innerHTML = '<div class="ps-empty-state">Select a message type</div>';
            return;
        }
        const isSandbox = !!d.sandbox;
        actions.innerHTML = `
            <button class="btn btn-sm" id="ps-schema-explorer-copy-btn" title="Show the raw file body for copy-paste into your git branch">Copy…</button>
            <button class="btn btn-sm" id="ps-schema-explorer-edit-btn">${isSandbox ? 'Edit overlay' : 'Overlay in namespace'}</button>
            ${isSandbox ? '<button class="btn btn-sm btn-danger" id="ps-schema-explorer-delete-btn">Delete overlay</button>' : ''}`;
        const props = d.properties || {};
        const propRows = Object.entries(props).map(([name, def]) => `
            <tr>
                <td class="ps-schema-explorer-prop-name">${this.#esc(name)}</td>
                <td class="ps-schema-explorer-prop-type">${this.#esc(def?.type || '—')}</td>
                <td>${this.#esc(def?.format || '')}</td>
                <td>${this.#esc(def?.description || '')}</td>
            </tr>`).join('');
        const storage = d.storage || {};
        const storageItems = [];
        storageItems.push(`<span class="ps-schema-explorer-storage-item">journal: ${storage.journal === false ? 'off' : 'on'}</span>`);
        storageItems.push(`<span class="ps-schema-explorer-storage-item">state: ${storage.state === false ? 'off' : 'on'}</span>`);
        if (storage.ttl) storageItems.push(`<span class="ps-schema-explorer-storage-item">ttl: ${storage.ttl}s</span>`);
        const sandboxTag = isSandbox ? ` <span class="ps-schema-explorer-badge ps-schema-explorer-badge-overlay">overlay in ${this.#esc(d.namespace || this.#getEffectiveNamespace() || '')}</span>` : '';
        el.innerHTML = `
            <div class="ps-schema-explorer-detail-header">
                <span class="ps-schema-explorer-detail-name">${this.#esc(d.message_type || this.#schemaExplorer.selectedType || '')}</span>
                <span class="ps-schema-explorer-detail-version">v${this.#esc(d.version || '?')}</span>
                ${sandboxTag}
            </div>
            <div class="ps-schema-explorer-detail-desc">${this.#esc(d.description || 'No description.')}</div>
            <div class="ps-schema-explorer-detail-section">
                <div class="ps-schema-explorer-detail-section-title">Storage</div>
                <div class="ps-schema-explorer-storage">${storageItems.join('')}</div>
            </div>
            <div class="ps-schema-explorer-detail-section">
                <div class="ps-schema-explorer-detail-section-title">Properties</div>
                ${propRows ? `<table class="ps-schema-explorer-prop-table">
                    <thead><tr><th>Name</th><th>Type</th><th>Format</th><th>Description</th></tr></thead>
                    <tbody>${propRows}</tbody>
                </table>` : '<div class="ps-empty-state">No type-specific properties.</div>'}
            </div>
            <div class="ps-schema-explorer-detail-section">
                <div class="ps-schema-explorer-detail-section-title">Raw JSON</div>
                <pre class="ps-schema-explorer-raw">${this.#esc(JSON.stringify(d, null, 2))}</pre>
            </div>`;

        actions.querySelector('#ps-schema-explorer-copy-btn').onclick = () => this.#copyExplorerType();
        actions.querySelector('#ps-schema-explorer-edit-btn').onclick = () => this.#openExplorerEditor('edit');
        actions.querySelector('#ps-schema-explorer-delete-btn')?.addEventListener('click', () => this.#deleteExplorerType());
    }

    // ---- Schema editor ----

    #openExplorerEditor(mode) {
        if (!this.#getEffectiveNamespace()) {
            this.#showAlert('No active namespace. Pick or type one from the header namespace selector first.');
            return;
        }
        const domain = this.#schemaExplorer.selectedDomain;
        if (!domain) {
            this.#showAlert('Pick a domain first.');
            return;
        }
        const isNew = mode === 'new';
        const source = isNew ? null : this.#schemaExplorer.detail;
        this.#schemaEditor = {
            isNew,
            domain,
            messageType: isNew ? '' : (source?.message_type || this.#schemaExplorer.selectedType || ''),
            mode: 'form',
            saving: false,
            error: null,
            rawText: null,
            wasSandbox: !!source?.sandbox,
            draft: this.#draftFromSource(source),
        };
        this.#renderExplorerDetail();
    }

    #closeExplorerEditor() {
        this.#schemaEditor = null;
        this.#renderExplorerDetail();
    }

    #draftFromSource(source) {
        if (!source) {
            return {
                version: '0.0.1',
                description: '',
                storage: { journal: true, state: true, ttl: '' },
                properties: [],
            };
        }
        const storage = source.storage || {};
        return {
            version: source.version || '0.0.1',
            description: source.description || '',
            storage: {
                journal: storage.journal !== false,
                state: storage.state !== false,
                ttl: storage.ttl != null ? String(storage.ttl) : '',
            },
            properties: Object.entries(source.properties || {}).map(([name, def]) => ({
                name,
                type: def?.type || 'string',
                format: def?.format || '',
                description: def?.description || '',
                enumValues: Array.isArray(def?.enum) ? def.enum.join(', ') : '',
            })),
        };
    }

    #renderExplorerEditor(container) {
        const st = this.#schemaEditor;
        const nsChip = `<span class="ps-schema-explorer-badge ps-schema-explorer-badge-overlay">namespace: ${this.#esc(this.#getEffectiveNamespace())}</span>`;
        if (st.mode === 'raw') {
            const raw = st.rawText != null ? st.rawText : this.#composeRawFromDraft();
            container.innerHTML = `
                <div class="ps-schema-editor-toolbar">${nsChip}</div>
                ${st.error ? `<div class="ps-form-result invalid">${this.#esc(st.error)}</div>` : ''}
                <label class="ps-schema-editor-label">Raw JSON body (POST/PUT payload)</label>
                <textarea id="ps-schema-editor-raw" class="ps-schema-editor-raw" spellcheck="false">${this.#esc(raw)}</textarea>
                <div class="ps-form-hint">Shape must match §12.4 of the ELM API. common.json fields are auto-included and MUST NOT be redeclared here.</div>`;
            container.querySelector('#ps-schema-editor-raw').addEventListener('input', (ev) => {
                st.rawText = ev.target.value;
            });
            return;
        }

        const commonNames = Object.keys(this.#schemaExplorer.common?.properties || {});
        const commonChips = commonNames.length
            ? `<div class="ps-schema-editor-common-note">Common fields (auto-included, do not redeclare): ${commonNames.map(n => `<span class="ps-schema-explorer-common-chip">${this.#esc(n)}</span>`).join(' ')}</div>`
            : '';

        const propRows = st.draft.properties.map((p, i) => this.#renderEditorPropRow(p, i)).join('');
        container.innerHTML = `
            <div class="ps-schema-editor-toolbar">${nsChip}</div>
            ${st.error ? `<div class="ps-form-result invalid">${this.#esc(st.error)}</div>` : ''}
            <div class="ps-schema-editor-grid">
                <div class="ps-schema-editor-field">
                    <label>Message type</label>
                    <input type="text" id="ps-schema-editor-mt" class="ps-input ps-input-sm" value="${this.#esc(st.messageType)}" ${st.isNew ? '' : 'disabled'} placeholder="my_new_event" />
                </div>
                <div class="ps-schema-editor-field">
                    <label>Version</label>
                    <input type="text" id="ps-schema-editor-version" class="ps-input ps-input-sm" value="${this.#esc(st.draft.version)}" placeholder="0.0.1" />
                </div>
            </div>
            <div class="ps-schema-editor-field">
                <label>Description</label>
                <input type="text" id="ps-schema-editor-desc" class="ps-input ps-input-sm" value="${this.#esc(st.draft.description)}" placeholder="Short description" />
            </div>
            <div class="ps-schema-editor-field ps-schema-editor-storage-row">
                <label>Storage</label>
                <label class="ps-schema-editor-inline"><input type="checkbox" id="ps-schema-editor-journal" ${st.draft.storage.journal ? 'checked' : ''} /> journal</label>
                <label class="ps-schema-editor-inline"><input type="checkbox" id="ps-schema-editor-state" ${st.draft.storage.state ? 'checked' : ''} /> state</label>
                <label class="ps-schema-editor-inline">TTL (s)
                    <input type="number" id="ps-schema-editor-ttl" class="ps-input ps-input-sm" style="width:100px" value="${this.#esc(st.draft.storage.ttl)}" placeholder="(default)" min="1" />
                </label>
            </div>
            ${commonChips}
            <div class="ps-schema-editor-field">
                <div class="ps-schema-editor-props-header">
                    <label>Properties</label>
                    <div class="ps-schema-editor-quickadd">
                        <button class="btn btn-sm" data-add="string">+ string</button>
                        <button class="btn btn-sm" data-add="integer">+ integer</button>
                        <button class="btn btn-sm" data-add="number">+ number</button>
                        <button class="btn btn-sm" data-add="boolean">+ boolean</button>
                        <button class="btn btn-sm" data-add="enum">+ enum</button>
                        <button class="btn btn-sm" data-add="timestamp">+ timestamp</button>
                    </div>
                </div>
                <div id="ps-schema-editor-props" class="ps-schema-editor-props">${propRows}</div>
            </div>`;

        for (const btn of container.querySelectorAll('.ps-schema-editor-quickadd [data-add]')) {
            btn.addEventListener('click', () => this.#addEditorProp(btn.dataset.add));
        }
        this.#bindEditorPropRowEvents(container);
        // sync draft on form input
        for (const el of container.querySelectorAll('input, textarea')) {
            el.addEventListener('input', () => this.#syncDraftFromForm(container));
        }
    }

    #renderEditorPropRow(p, i) {
        const types = ['string', 'number', 'integer', 'boolean', 'array', 'object'];
        const typeOpts = types.map(t => `<option${t === p.type ? ' selected' : ''}>${t}</option>`).join('');
        return `<div class="ps-schema-editor-prop-row" data-idx="${i}">
            <input type="text" class="ps-input ps-input-sm ps-schema-editor-prop-name" value="${this.#esc(p.name)}" placeholder="field_name" />
            <select class="ps-input ps-input-sm ps-schema-editor-prop-type">${typeOpts}</select>
            <input type="text" class="ps-input ps-input-sm ps-schema-editor-prop-format" value="${this.#esc(p.format)}" placeholder="format (opt)" />
            <input type="text" class="ps-input ps-input-sm ps-schema-editor-prop-enum" value="${this.#esc(p.enumValues)}" placeholder="enum (comma-sep, opt)" />
            <input type="text" class="ps-input ps-input-sm ps-schema-editor-prop-desc" value="${this.#esc(p.description)}" placeholder="description (opt)" />
            <button class="btn-icon ps-schema-editor-prop-remove" title="Remove">✕</button>
        </div>`;
    }

    #bindEditorPropRowEvents(container) {
        for (const btn of container.querySelectorAll('.ps-schema-editor-prop-remove')) {
            btn.addEventListener('click', () => {
                const idx = Number(btn.closest('.ps-schema-editor-prop-row').dataset.idx);
                this.#schemaEditor.draft.properties.splice(idx, 1);
                this.#renderExplorerDetail();
            });
        }
    }

    #addEditorProp(kind) {
        this.#syncDraftFromForm(this.shadowRoot.getElementById('ps-schema-explorer-detail'));
        const map = {
            string: { type: 'string' },
            integer: { type: 'integer' },
            number: { type: 'number' },
            boolean: { type: 'boolean' },
            enum: { type: 'string', enumValues: 'value1, value2' },
            timestamp: { type: 'string', format: 'date-time' },
        };
        const base = map[kind] || { type: 'string' };
        this.#schemaEditor.draft.properties.push({
            name: '', type: base.type,
            format: base.format || '', description: '',
            enumValues: base.enumValues || '',
        });
        this.#renderExplorerDetail();
    }

    #syncDraftFromForm(container) {
        const st = this.#schemaEditor;
        if (!st || st.mode !== 'form') return;
        const val = (id) => container.querySelector(`#${id}`)?.value ?? '';
        const chk = (id) => !!container.querySelector(`#${id}`)?.checked;
        if (st.isNew) st.messageType = val('ps-schema-editor-mt').trim();
        st.draft.version = val('ps-schema-editor-version').trim() || '0.0.1';
        st.draft.description = val('ps-schema-editor-desc');
        st.draft.storage.journal = chk('ps-schema-editor-journal');
        st.draft.storage.state = chk('ps-schema-editor-state');
        st.draft.storage.ttl = val('ps-schema-editor-ttl').trim();
        const rows = container.querySelectorAll('.ps-schema-editor-prop-row');
        st.draft.properties = Array.from(rows).map(r => ({
            name: r.querySelector('.ps-schema-editor-prop-name').value.trim(),
            type: r.querySelector('.ps-schema-editor-prop-type').value,
            format: r.querySelector('.ps-schema-editor-prop-format').value.trim(),
            enumValues: r.querySelector('.ps-schema-editor-prop-enum').value.trim(),
            description: r.querySelector('.ps-schema-editor-prop-desc').value.trim(),
        }));
    }

    #toggleExplorerEditorMode() {
        const st = this.#schemaEditor;
        if (!st) return;
        const container = this.shadowRoot.getElementById('ps-schema-explorer-detail');
        if (st.mode === 'form') {
            this.#syncDraftFromForm(container);
            st.rawText = this.#composeRawFromDraft();
            st.mode = 'raw';
        } else {
            // parse rawText back into draft
            try {
                const body = JSON.parse(st.rawText || '{}');
                st.draft = this.#draftFromSource({ ...body, sandbox: undefined });
                if (st.isNew && body.message_type) st.messageType = body.message_type;
                st.error = null;
                st.mode = 'form';
                st.rawText = null;
            } catch (err) {
                st.error = `Raw JSON parse failed: ${err.message}. Stay in raw mode to fix.`;
            }
        }
        this.#renderExplorerDetail();
    }

    #composeRawFromDraft() {
        return JSON.stringify(this.#draftToBody(), null, 2);
    }

    #draftToBody() {
        const st = this.#schemaEditor;
        const body = {
            message_type: st.messageType || undefined,
            version: st.draft.version || '0.0.1',
            description: st.draft.description || undefined,
            storage: {},
            properties: {},
        };
        if (st.draft.storage.journal === false) body.storage.journal = false; else body.storage.journal = true;
        if (st.draft.storage.state === false) body.storage.state = false; else body.storage.state = true;
        if (st.draft.storage.ttl) body.storage.ttl = Number(st.draft.storage.ttl);
        for (const p of st.draft.properties) {
            if (!p.name) continue;
            const def = { type: p.type };
            if (p.format) def.format = p.format;
            if (p.description) def.description = p.description;
            if (p.enumValues) {
                def.enum = p.enumValues.split(',').map(s => s.trim()).filter(Boolean);
            }
            body.properties[p.name] = def;
        }
        return body;
    }

    async #saveExplorerEditor() {
        const st = this.#schemaEditor;
        if (!st) return;
        const container = this.shadowRoot.getElementById('ps-schema-explorer-detail');
        if (st.mode === 'form') this.#syncDraftFromForm(container);

        let body;
        if (st.mode === 'raw') {
            try { body = JSON.parse(st.rawText || '{}'); }
            catch (err) { st.error = `Invalid JSON: ${err.message}`; this.#renderExplorerDetail(); return; }
            if (st.isNew && body.message_type) st.messageType = body.message_type;
        } else {
            body = this.#draftToBody();
        }

        const type = (st.messageType || '').trim();
        if (!type) {
            st.error = 'message_type is required';
            this.#renderExplorerDetail();
            return;
        }

        const ns = this.#getEffectiveNamespace();
        const url = `${this.#baseUrl}/schemas/${encodeURIComponent(st.domain)}/message-types/${encodeURIComponent(type)}?namespace=${encodeURIComponent(ns)}`;
        // POST for a brand-new candidate; PUT for edit or overlay-over-prod
        const method = st.isNew && !st.wasSandbox && !this.#hasSandboxOverlayFor(st.domain, type) ? 'POST' : 'PUT';

        await this.#withLoading(async () => {
            try {
                let res;
                try {
                    res = await this.fetch(url, { method, payload: body });
                } catch (err) {
                    // POST returns 409 when a candidate or prod already exists — server hints to use PUT.
                    if (method === 'POST' && err?.status === 409) {
                        res = await this.fetch(url, { method: 'PUT', payload: body });
                    } else {
                        throw err;
                    }
                }
                const flipped = res?.flipped_pipelines || [];
                if (flipped.length) this.#setStatus(`Saved. Restarted: ${flipped.join(', ')}`);
                else this.#setStatus('Saved.');
                this.#schemaEditor = null;
                this.#schemaExplorer.selectedType = type;
                this.#schemaCache.typeDetails.delete(`${st.domain}::${type}`);
                await this.#loadExplorerTypes(st.domain);
                await this.#selectExplorerType(type);
            } catch (err) {
                const details = err?.body?.details;
                st.error = `Save failed: ${details ? (Array.isArray(details) ? details.map(d => d.message || JSON.stringify(d)).join('\n') : JSON.stringify(details)) : (err.message || err)}`;
                this.#renderExplorerDetail();
            }
        });
    }

    #hasSandboxOverlayFor(domain, type) {
        if (this.#schemaExplorer.selectedDomain !== domain) return false;
        const found = this.#schemaExplorer.types.find(t => t.name === type);
        return !!found?.sandbox;
    }

    async #deleteExplorerType() {
        const d = this.#schemaExplorer.detail;
        if (!d || !d.sandbox) return;
        const type = d.message_type || this.#schemaExplorer.selectedType;
        const ns = d.namespace || this.#getEffectiveNamespace();
        if (!await this.#confirm(`Delete sandbox overlay for "${this.#schemaExplorer.selectedDomain}/${type}" in namespace "${ns}"?`)) return;
        await this.#withLoading(async () => {
            try {
                const url = `${this.#baseUrl}/schemas/${encodeURIComponent(this.#schemaExplorer.selectedDomain)}/message-types/${encodeURIComponent(type)}?namespace=${encodeURIComponent(ns)}`;
                const res = await this.fetch(url, { method: 'DELETE' });
                const flipped = res?.flipped_pipelines || [];
                if (flipped.length) this.#setStatus(`Deleted. Restarted: ${flipped.join(', ')}`);
                else this.#setStatus('Deleted.');
                this.#schemaCache.typeDetails.delete(`${this.#schemaExplorer.selectedDomain}::${type}`);
                await this.#loadExplorerTypes(this.#schemaExplorer.selectedDomain);
                this.#schemaExplorer.selectedType = null;
                this.#schemaExplorer.detail = null;
                this.#renderExplorerDetail();
            } catch (err) {
                this.#showAlert(`Delete failed: ${err.message || err}`);
            }
        });
    }

    async #copyExplorerType() {
        const d = this.#schemaExplorer.detail;
        if (!d) return;
        const type = d.message_type || this.#schemaExplorer.selectedType;
        const domain = this.#schemaExplorer.selectedDomain;
        const ns = this.#getEffectiveNamespace();
        const params = new URLSearchParams();
        if (ns) params.set('namespace', ns);
        const q = params.toString() ? `?${params}` : '';
        try {
            const body = await this.fetch(`${this.#baseUrl}/schemas/${encodeURIComponent(domain)}/message-types/${encodeURIComponent(type)}/copy${q}`);
            const text = typeof body === 'string' ? body : JSON.stringify(body, null, 2);
            this.#showCopyModal(`schemas/${domain}/${type}.json`, text);
        } catch (err) {
            this.#showAlert(`Copy failed: ${err.message || err}`);
        }
    }

    #showCopyModal(targetPath, text) {
        const overlay = document.createElement('div');
        overlay.className = 'ps-confirm-overlay';
        overlay.innerHTML = `<div class="ps-confirm-dialog ps-alert-dialog" style="max-width:720px">
            <div class="ps-copy-path">Paste into your feature branch at <code>${this.#esc(targetPath)}</code></div>
            <pre class="ps-alert-content ps-copy-content">${this.#esc(text)}</pre>
            <div class="ps-confirm-actions">
                <button class="btn" id="ps-copy-close">Close</button>
                <button class="btn btn-primary" id="ps-copy-btn">Copy to clipboard</button>
            </div>
        </div>`;
        this.shadowRoot.appendChild(overlay);
        overlay.querySelector('#ps-copy-close').onclick = () => overlay.remove();
        overlay.querySelector('#ps-copy-btn').onclick = async () => {
            try {
                await navigator.clipboard.writeText(text);
                overlay.querySelector('#ps-copy-btn').textContent = 'Copied ✓';
            } catch (err) {
                Logger.warn('clipboard write failed: ', err);
            }
        };
    }

    // ==== Topology view (step 6) ==============================================

    async #showTopologyView() {
        this.#showView('topology');
        this.#renderNamespaceLabel();
        const nsBadge = this.shadowRoot.getElementById('ps-topology-ns');
        if (nsBadge) nsBadge.textContent = this.#getEffectiveNamespace() || '';
        await this.#loadTopology();
    }

    #leaveTopologyView() {
        this.#stopTopologyLivePoll();
        this.#stopChannelStream();
        this.#topology.selectedEdge = null;
        this.#showView('list');
    }

    async #loadTopology() {
        const params = new URLSearchParams();
        const ns = this.#getEffectiveNamespace();
        if (ns) params.set('namespace', ns);
        const q = params.toString() ? `?${params}` : '';
        await this.#withLoading(async () => {
            try {
                const [graph, live] = await Promise.all([
                    this.fetch(`${this.#baseUrl}/pipelines/topology${q}`),
                    this.fetch(`${this.#baseUrl}/pipelines/topology/live${q}`).catch(err => { Logger.debug('topology live overlay unavailable: ', err); return null; }),
                ]);
                this.#topology.graph = graph;
                this.#topology.live = live;
                this.#renderTopology();
            } catch (err) {
                Logger.error('topology fetch failed: ', err);
                this.shadowRoot.getElementById('ps-topology-canvas').innerHTML =
                    `<div class="ps-empty-state">Failed to load topology: ${this.#esc(err.message || err)}</div>`;
            }
        });
    }

    #toggleTopologyLivePoll(enabled) {
        this.#stopTopologyLivePoll();
        if (!enabled) return;
        this.#topology.livePoll = setInterval(() => this.#refreshTopologyLive(), 5000);
    }

    #stopTopologyLivePoll() {
        if (this.#topology.livePoll) {
            clearInterval(this.#topology.livePoll);
            this.#topology.livePoll = null;
        }
    }

    async #refreshTopologyLive() {
        const params = new URLSearchParams();
        const ns = this.#getEffectiveNamespace();
        if (ns) params.set('namespace', ns);
        const q = params.toString() ? `?${params}` : '';
        try {
            this.#topology.live = await this.fetch(`${this.#baseUrl}/pipelines/topology/live${q}`);
            this.#renderTopology();
        } catch (err) {
            Logger.debug('topology live refresh failed: ', err);
        }
    }

    #renderTopology() {
        const canvas = this.shadowRoot.getElementById('ps-topology-canvas');
        const graph = this.#topology.graph;
        if (!graph || !(graph.nodes || []).length) {
            canvas.innerHTML = '<div class="ps-empty-state">No pipelines defined.</div>';
            return;
        }

        const layout = this.#layoutTopology(graph);
        const svg = this.#renderTopologySvg(layout, graph, this.#topology.live);
        canvas.innerHTML = svg;

        // Wire up interactions
        for (const el of canvas.querySelectorAll('[data-node]')) {
            el.addEventListener('click', () => this.#openPipelineDetail(el.dataset.node));
        }
        for (const el of canvas.querySelectorAll('[data-edge]')) {
            el.addEventListener('click', () => this.#openChannelInspector(el.dataset.edge));
        }
    }

    #layoutTopology(graph) {
        // Column assignment via BFS from cycle-root nodes.
        const nodes = graph.nodes || [];
        const edges = graph.edges || [];
        const byName = new Map(nodes.map(n => [n.name, n]));
        const incoming = new Map(nodes.map(n => [n.name, []]));
        const outgoing = new Map(nodes.map(n => [n.name, []]));
        for (const e of edges) {
            if (!byName.has(e.from) || !byName.has(e.to)) continue;
            outgoing.get(e.from).push(e.to);
            incoming.get(e.to).push(e.from);
        }
        const col = new Map();
        const queue = [];
        for (const n of nodes) {
            if (!incoming.get(n.name).length) {
                col.set(n.name, 0);
                queue.push(n.name);
            }
        }
        while (queue.length) {
            const name = queue.shift();
            for (const child of outgoing.get(name)) {
                const proposed = col.get(name) + 1;
                if (col.get(child) === undefined || col.get(child) < proposed) {
                    col.set(child, proposed);
                    queue.push(child);
                }
            }
        }
        // Nodes not reachable from a root (dangling) → column 0
        for (const n of nodes) if (col.get(n.name) === undefined) col.set(n.name, 0);

        // Group by column, sort within column by name for stability
        const byCol = new Map();
        for (const n of nodes) {
            const c = col.get(n.name);
            if (!byCol.has(c)) byCol.set(c, []);
            byCol.get(c).push(n);
        }
        for (const arr of byCol.values()) arr.sort((a, b) => a.name.localeCompare(b.name));

        const NODE_W = 180, NODE_H = 52, HGAP = 90, VGAP = 20;
        const positions = new Map();
        let maxX = 0, maxY = 0;
        for (const [c, arr] of byCol) {
            const x = 20 + c * (NODE_W + HGAP);
            arr.forEach((n, i) => {
                const y = 20 + i * (NODE_H + VGAP);
                positions.set(n.name, { x, y, w: NODE_W, h: NODE_H });
                if (x + NODE_W > maxX) maxX = x + NODE_W;
                if (y + NODE_H > maxY) maxY = y + NODE_H;
            });
        }
        return { positions, width: maxX + 20, height: maxY + 20 };
    }

    #renderTopologySvg(layout, graph, live) {
        const { positions, width, height } = layout;
        const edges = graph.edges || [];
        const liveEdges = new Map();
        for (const c of live?.channels || []) {
            liveEdges.set(`${c.domain}::${c.message_type}`, c);
        }

        const edgePaths = edges.map(e => {
            const p1 = positions.get(e.from);
            const p2 = positions.get(e.to);
            if (!p1 || !p2) return '';
            const x1 = p1.x + p1.w, y1 = p1.y + p1.h / 2;
            const x2 = p2.x, y2 = p2.y + p2.h / 2;
            const cx = (x1 + x2) / 2;
            const path = `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`;
            const channelKey = `${e.via.domain}::${e.via.message_type}`;
            const chan = liveEdges.get(channelKey);
            const bp = chan?.backpressure ?? 0;
            const rate = chan?.publishes_per_sec ?? 0;
            let stroke = '#888';
            if (chan) {
                if (bp > 0.9) stroke = '#d62828';
                else if (bp > 0.5) stroke = '#e0a800';
                else if (rate > 0) stroke = '#2a8f3f';
                else stroke = '#aaa';
            }
            const label = `${e.via.domain} / ${e.via.message_type}${chan ? `\n${rate.toFixed(1)} evt/s${bp ? ` · bp ${(bp * 100).toFixed(0)}%` : ''}` : ''}`;
            return `<g class="ps-topo-edge" data-edge="${this.#esc(channelKey)}">
                <path d="${path}" fill="none" stroke="${stroke}" stroke-width="2" stroke-opacity="0.85" />
                <title>${this.#esc(label)}</title>
                <text x="${(x1 + x2) / 2}" y="${(y1 + y2) / 2 - 6}" text-anchor="middle" class="ps-topo-edge-label">${this.#esc(`${e.via.domain}/${e.via.message_type}`)}</text>
            </g>`;
        }).join('');

        const nodeEls = (graph.nodes || []).map(n => {
            const p = positions.get(n.name);
            if (!p) return '';
            const stateColor = n.state === 'running' ? '#2a8f3f' : n.state === 'error' ? '#d62828' : '#888';
            const triggerLabel = ELM_TRIGGER_DISPLAY_LABEL[n.trigger?.type] || n.trigger?.type || '';
            const nsBadge = n.is_sandbox ? `<text x="${p.x + p.w - 6}" y="${p.y + 14}" text-anchor="end" class="ps-topo-node-badge">sandbox</text>` : '';
            return `<g class="ps-topo-node" data-node="${this.#esc(n.name)}">
                <rect x="${p.x}" y="${p.y}" rx="8" ry="8" width="${p.w}" height="${p.h}" class="ps-topo-node-rect" />
                <rect x="${p.x}" y="${p.y}" rx="8" ry="8" width="5" height="${p.h}" fill="${stateColor}" />
                <text x="${p.x + 12}" y="${p.y + 20}" class="ps-topo-node-title">${this.#esc(n.name)}</text>
                <text x="${p.x + 12}" y="${p.y + 38}" class="ps-topo-node-sub">${this.#esc(triggerLabel)} · ${this.#esc(n.state || '')}</text>
                ${nsBadge}
                <title>${this.#esc(`${n.name}\ntrigger: ${triggerLabel}\nstate: ${n.state}\nnamespace: ${n.namespace || '(prod)'}`)}</title>
            </g>`;
        }).join('');

        return `<svg class="ps-topo-svg" width="${Math.max(width, 200)}" height="${Math.max(height, 100)}" viewBox="0 0 ${Math.max(width, 200)} ${Math.max(height, 100)}" preserveAspectRatio="xMinYMin meet">
            ${edgePaths}
            ${nodeEls}
        </svg>`;
    }

    // ---- Channel Inspector ----

    async #openChannelInspector(edgeKey) {
        const [domain, message_type] = edgeKey.split('::');
        this.#topology.selectedEdge = { domain, message_type };
        this.#topology.channelBuffer = [];
        const overlay = document.createElement('div');
        overlay.className = 'ps-confirm-overlay';
        overlay.id = 'ps-channel-inspector';
        overlay.innerHTML = `<div class="ps-confirm-dialog" style="max-width:720px; width:720px">
            <h3 style="margin-top:0">Channel: ${this.#esc(domain)} / ${this.#esc(message_type)}</h3>
            <div id="ps-channel-inspector-snapshot" class="ps-channel-snapshot">Loading…</div>
            <div class="ps-channel-tail-controls">
                <button class="btn btn-sm" id="ps-channel-tail-toggle">Start live tail</button>
                <span id="ps-channel-tail-status" class="ps-form-hint"></span>
            </div>
            <div id="ps-channel-tail-body" class="ps-channel-tail-body">
                <div class="ps-empty-state">No events yet.</div>
            </div>
            <div class="ps-confirm-actions"><button class="btn" id="ps-channel-close">Close</button></div>
        </div>`;
        this.shadowRoot.appendChild(overlay);
        overlay.querySelector('#ps-channel-close').onclick = () => this.#closeChannelInspector();

        const params = new URLSearchParams();
        const ns = this.#getEffectiveNamespace();
        if (ns) params.set('namespace', ns);
        const q = params.toString() ? `?${params}` : '';
        try {
            const res = await this.fetch(`${this.#baseUrl}/pipelines/topology/live/${encodeURIComponent(domain)}/${encodeURIComponent(message_type)}${q}`);
            const snap = res?.snapshot || {};
            overlay.querySelector('#ps-channel-inspector-snapshot').innerHTML = `
                <table class="ps-schema-explorer-prop-table">
                    <tr><th>subscribers</th><td>${this.#esc(snap.subscribers ?? '—')}</td></tr>
                    <tr><th>publishes_total</th><td>${this.#esc(snap.publishes_total ?? '—')}</td></tr>
                    <tr><th>publishes_per_sec</th><td>${this.#esc(snap.publishes_per_sec ?? '—')}</td></tr>
                    <tr><th>backpressure</th><td>${this.#esc(snap.backpressure ?? '—')}</td></tr>
                    <tr><th>last_publish_ms</th><td>${this.#esc(snap.last_publish_ms ? new Date(snap.last_publish_ms).toISOString() : '—')}</td></tr>
                </table>`;
            const events = res?.recent_events || [];
            if (events.length) {
                overlay.querySelector('#ps-channel-tail-body').innerHTML = events.map(e => this.#renderChannelEventRow(e)).join('');
            }
        } catch (err) {
            overlay.querySelector('#ps-channel-inspector-snapshot').innerHTML =
                `<div class="ps-form-result invalid">${this.#esc(err.message || err)}</div>`;
        }

        overlay.querySelector('#ps-channel-tail-toggle').onclick = () => this.#toggleChannelTail(overlay);
    }

    #renderChannelEventRow(e) {
        const ts = e.t ? new Date(e.t).toISOString().slice(11, 19) : '';
        const preview = JSON.stringify(e.event || {}).slice(0, 120);
        return `<div class="ps-tel-row">
            <div class="ps-tel-row-head">
                <span class="ps-tel-row-caret">•</span>
                <span class="ps-tel-row-dom">${this.#esc(ts)}</span>
                <span class="ps-tel-row-mt">${this.#esc(preview)}</span>
                <span></span>
            </div>
        </div>`;
    }

    async #toggleChannelTail(overlay) {
        const btn = overlay.querySelector('#ps-channel-tail-toggle');
        const statusEl = overlay.querySelector('#ps-channel-tail-status');
        if (this.#topology.channelSSE) {
            this.#stopChannelStream();
            btn.textContent = 'Start live tail';
            statusEl.textContent = '';
            return;
        }
        const { domain, message_type } = this.#topology.selectedEdge || {};
        if (!domain || !message_type) return;
        const params = new URLSearchParams();
        const ns = this.#getEffectiveNamespace();
        if (ns) params.set('namespace', ns);
        const q = params.toString() ? `?${params}` : '';
        const url = `/dashboard${this.#baseUrl}/pipelines/topology/live/${encodeURIComponent(domain)}/${encodeURIComponent(message_type)}/stream${q}`;
        const token = await Auth.getToken();
        this.#topology.channelSSE = new AbortController();
        btn.textContent = 'Stop live tail';
        statusEl.textContent = 'Connecting…';
        const body = overlay.querySelector('#ps-channel-tail-body');
        body.innerHTML = '';
        fetchEventSource(url, {
            method: 'GET',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            signal: this.#topology.channelSSE.signal,
            openWhenHidden: true,
            onopen: async () => { statusEl.textContent = 'Live'; },
            onmessage: (event) => {
                if (!event.data) return;
                try {
                    const parsed = JSON.parse(event.data);
                    const row = document.createElement('div');
                    row.innerHTML = this.#renderChannelEventRow({ t: parsed.t || Date.now(), event: parsed.event || parsed });
                    body.insertBefore(row.firstElementChild, body.firstElementChild);
                    while (body.childElementCount > 200) body.removeChild(body.lastElementChild);
                } catch (err) {
                    Logger.debug('channel SSE parse error: ', err);
                }
            },
            onerror: (err) => {
                if (this.#topology.channelSSE?.signal?.aborted) throw err;
                statusEl.textContent = 'Reconnecting…';
            },
        });
    }

    #stopChannelStream() {
        if (this.#topology.channelSSE) {
            this.#topology.channelSSE.abort();
            this.#topology.channelSSE = null;
        }
    }

    #closeChannelInspector() {
        this.#stopChannelStream();
        this.#topology.selectedEdge = null;
        const overlay = this.shadowRoot.getElementById('ps-channel-inspector');
        if (overlay) overlay.remove();
    }

    // ==== Prepare PR view (step 7) ============================================

    async #showPreparePrView() {
        this.#showView('prepare-pr');
        const nsBadge = this.shadowRoot.getElementById('ps-prepare-pr-ns');
        if (nsBadge) nsBadge.textContent = this.#getEffectiveNamespace() || '';
        await this.#loadPreparePr();
    }

    async #loadPreparePr() {
        const ns = this.#getEffectiveNamespace();
        const body = this.shadowRoot.getElementById('ps-prepare-pr-body');
        if (!ns) {
            body.innerHTML = '<div class="ps-empty-state">Pick a namespace from the header selector first.</div>';
            return;
        }

        await this.#withLoading(async () => {
            const [pipelinesRes, sandboxRes] = await Promise.all([
                this.fetch(`${this.#baseUrl}/pipelines`).catch(() => ({ pipelines: [] })),
                this.fetch(`${this.#baseUrl}/schemas/_sandbox?namespace=${encodeURIComponent(ns)}`).catch(() => ({ candidates: [] })),
            ]);

            const sandboxPipelines = (pipelinesRes?.pipelines || [])
                .filter(p => p.is_sandbox && p.namespace === ns);
            const candidates = sandboxRes?.candidates || [];

            const configs = await Promise.all(sandboxPipelines.map(p =>
                this.fetch(`${this.#baseUrl}/pipelines/${encodeURIComponent(p.name)}`)
                    .then(res => ({ name: p.name, config: res?.config || res }))
                    .catch(err => { Logger.debug('pipeline detail fetch failed: ', err); return { name: p.name, config: null }; })
            ));

            const schemaBodies = await Promise.all(candidates.map(c =>
                this.fetch(`${this.#baseUrl}/schemas/${encodeURIComponent(c.domain)}/message-types/${encodeURIComponent(c.message_type)}/copy?namespace=${encodeURIComponent(ns)}`)
                    .then(res => ({ ...c, body: res }))
                    .catch(err => { Logger.debug('schema copy fetch failed: ', err); return { ...c, body: null }; })
            ));

            const pluginRefs = new Set();
            for (const { config } of configs) {
                if (!config) continue;
                for (const step of config.pipeline || []) {
                    const ref = typeof step === 'string'
                        ? step
                        : (step?.plugin || (step?.tap?.[0] && (typeof step.tap[0] === 'string' ? step.tap[0] : step.tap[0].plugin)));
                    if (ref) pluginRefs.add(String(ref).replace(/\(.*\)$/, ''));
                }
            }

            body.innerHTML = this.#renderPreparePr({ ns, configs, schemaBodies, pluginRefs: Array.from(pluginRefs).sort() });
            for (const btn of body.querySelectorAll('.ps-pr-copy-btn')) {
                btn.addEventListener('click', () => {
                    const text = btn.dataset.content;
                    if (!text) return;
                    navigator.clipboard.writeText(text).then(() => {
                        btn.textContent = 'Copied ✓';
                        setTimeout(() => { btn.textContent = btn.dataset.label || 'Copy'; }, 1500);
                    }).catch(err => Logger.warn('clipboard write failed: ', err));
                });
            }
        });
    }

    #renderPreparePr({ ns, configs, schemaBodies, pluginRefs }) {    const pipelineCards = configs.length ? configs.map(({ name, config }) => {
            if (!config) return `<div class="ps-pr-card"><div class="ps-pr-card-title">${this.#esc(name)}</div><div class="ps-form-hint">Config unavailable.</div></div>`;
            const cleaned = { ...config };
            delete cleaned.namespace;
            delete cleaned.is_sandbox;
            delete cleaned.sandbox_state;
            delete cleaned.clone_of;
            delete cleaned.promoted;
            delete cleaned.origin_elm_version;
            delete cleaned.origin_elm_build_timestamp;
            const text = JSON.stringify(cleaned, null, 2);
            const target = `config/pipelines/${name}.json`;
            return `<div class="ps-pr-card">
                <div class="ps-pr-card-header">
                    <span class="ps-pr-card-title">${this.#esc(name)}</span>
                    <span class="ps-pr-card-target">→ <code>${this.#esc(target)}</code></span>
                    <button class="btn btn-sm ps-pr-copy-btn" data-label="Copy" data-content="${this.#esc(text)}">Copy</button>
                </div>
                <pre class="ps-pr-preview">${this.#esc(text)}</pre>
            </div>`;
        }).join('') : '<div class="ps-empty-state">No sandbox pipelines in this namespace.</div>';

        const schemaCards = schemaBodies.length ? schemaBodies.map(s => {
            if (!s.body) return `<div class="ps-pr-card"><div class="ps-pr-card-title">${this.#esc(`${s.domain}/${s.message_type}`)}</div><div class="ps-form-hint">Schema body unavailable.</div></div>`;
            const text = typeof s.body === 'string' ? s.body : JSON.stringify(s.body, null, 2);
            const target = `schemas/${s.domain}/${s.message_type}.json`;
            return `<div class="ps-pr-card">
                <div class="ps-pr-card-header">
                    <span class="ps-pr-card-title">${this.#esc(s.domain)} / ${this.#esc(s.message_type)}</span>
                    <span class="ps-pr-card-target">→ <code>${this.#esc(target)}</code></span>
                    <button class="btn btn-sm ps-pr-copy-btn" data-label="Copy" data-content="${this.#esc(text)}">Copy</button>
                </div>
                <pre class="ps-pr-preview">${this.#esc(text)}</pre>
            </div>`;
        }).join('') : '<div class="ps-empty-state">No sandbox schema candidates in this namespace.</div>';

        const pluginList = pluginRefs.length ? `
            <ul class="ps-pr-plugin-list">
                ${pluginRefs.map(r => `<li><code>${this.#esc(r)}</code></li>`).join('')}
            </ul>
            <div class="ps-form-hint">
                Your local branch already has the source for any namespace plugin overrides you uploaded via the plugin manager.
                Commit each override's <code>index.js</code> alongside the pipeline/schema changes.
            </div>` : '<div class="ps-form-hint">No plugins referenced by the pipelines above.</div>';

        return `
            <div class="ps-pr-intro">
                <div>Sandbox artifacts for namespace <code>${this.#esc(ns)}</code>. Copy each block into your feature branch at the shown path, then open a PR.</div>
            </div>
            <div class="ps-pr-section">
                <h3 class="ps-pr-section-title">1. Pipeline configs (${configs.length})</h3>
                ${pipelineCards}
            </div>
            <div class="ps-pr-section">
                <h3 class="ps-pr-section-title">2. Schemas (${schemaBodies.length})</h3>
                ${schemaCards}
            </div>
            <div class="ps-pr-section">
                <h3 class="ps-pr-section-title">3. Plugins referenced (${pluginRefs.length})</h3>
                ${pluginList}
            </div>`;
    }

    // ==== Feature Bundle import/export ========================================
    // Backend contract: docs/FEATURE_BUNDLE_EXCHANGE_DESIGN.md (elm_processor_proto), § 10.
    // The clipboard is the only bus between the IDE agent and this UI (§ 10.1) — every
    // rendered result below also carries a raw-JSON "Copy for agent" control.

    async #showBundleView() {
        this.#showView('bundle');
        const ns = this.#getEffectiveNamespace();
        const nsBadge = this.shadowRoot.getElementById('ps-bundle-ns');
        if (nsBadge) nsBadge.textContent = ns || '';
        const nsInput = this.shadowRoot.getElementById('ps-bundle-import-ns');
        if (nsInput && !nsInput.value) nsInput.value = ns || '';
        this.#switchBundleTab(this.#bundleTab);
    }

    #switchBundleTab(tab) {
        this.#bundleTab = tab;
        const sd = this.shadowRoot;
        for (const t of sd.querySelectorAll('.ps-bundle-tab')) {
            t.classList.toggle('active', t.dataset.bundleTab === tab);
        }
        for (const p of sd.querySelectorAll('.ps-bundle-tab-panel')) {
            p.classList.toggle('hidden', p.id !== `ps-bundle-tab-${tab}`);
        }
        if (tab === 'export' && !this.#bundleExportItems.length) this.#loadExportItems();
    }

    #bindBundleDropZone() {
        const textarea = this.shadowRoot.getElementById('ps-bundle-import-text');
        const zone = this.shadowRoot.getElementById('ps-bundle-drop-zone');
        if (!textarea || !zone) return;
        this.onEvent('dragover', (_, ev) => { ev.preventDefault(); zone.classList.add('drag-over'); }, zone);
        this.onEvent('dragleave', () => zone.classList.remove('drag-over'), zone);
        this.onEvent('drop', (_, ev) => {
            ev.preventDefault();
            zone.classList.remove('drag-over');
            const file = ev.dataTransfer?.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => {
                textarea.value = reader.result;
                textarea.dispatchEvent(new Event('input', { bubbles: true }));
            };
            reader.readAsText(file);
        }, zone);
    }

    // Accepts either the plain envelope or `elm.feature.bundle.packed` (gzip+base64) — auto-detected.
    async #decodeBundleText(text) {
        const trimmed = (text || '').trim();
        if (!trimmed) throw new Error('Paste a feature bundle first.');
        let parsed;
        try {
            parsed = JSON.parse(trimmed);
        } catch (err) {
            throw new Error(`Not valid JSON: ${err.message}`);
        }
        if (parsed?.kind === 'elm.feature.bundle.packed') {
            if (parsed.encoding !== 'gzip+base64') throw new Error(`Unsupported packed encoding: ${parsed.encoding}`);
            return await this.#gunzipBase64(parsed.data);
        }
        return parsed;
    }

    async #gunzipBase64(b64) {
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
        const buf = await new Response(stream).arrayBuffer();
        return JSON.parse(new TextDecoder().decode(buf));
    }

    async #copyCapabilityIndex() {
        const btn = this.shadowRoot.getElementById('ps-bundle-capabilities-btn');
        await this.#withLoading(async () => {
            try {
                const res = await this.fetch(`${this.#baseUrl}/sandbox/capabilities`);
                await navigator.clipboard.writeText(JSON.stringify(res, null, 2));
                if (btn) {
                    btn.textContent = 'Copied ✓';
                    setTimeout(() => { btn.textContent = btn.dataset.label; }, 1500);
                }
                this.#setStatus(res?.capabilities_digest ? `Capability index copied (${res.capabilities_digest})` : 'Capability index copied');
            } catch (err) {
                Logger.error('Failed to fetch capability index: ', err);
                this.#showAlert(`Failed to fetch capability index: ${err.message || err}`);
            }
        });
    }

    async #validateBundle() {
        const sd = this.shadowRoot;
        const textarea = sd.getElementById('ps-bundle-import-text');
        const nsInput = sd.getElementById('ps-bundle-import-ns');
        const resultEl = sd.getElementById('ps-bundle-plan-result');
        const applyBtn = sd.getElementById('ps-bundle-apply-btn');
        applyBtn.disabled = true;
        this.#bundleParsed = null;

        let bundle;
        try {
            bundle = await this.#decodeBundleText(textarea.value);
        } catch (err) {
            resultEl.innerHTML = this.#renderBundleError(`Could not read bundle: ${err.message}`);
            return;
        }
        const namespace = nsInput.value.trim();
        if (!namespace) {
            resultEl.innerHTML = this.#renderBundleError('Choose a target namespace first.');
            return;
        }

        // § 10.0 status code rule: a successful evaluation that answers "no" is 200 with
        // valid: false — validate never returns an error status for a bundle it understood.
        // Only 400 (unparseable payload) is an actual error here.
        await this.#withLoading(async () => {
            try {
                const res = await this.fetch(
                    `${this.#baseUrl}/sandbox/bundles/validate?namespace=${encodeURIComponent(namespace)}`,
                    { method: 'POST', payload: { bundle }, expectedStatuses: [400] },
                );
                this.#bundleParsed = bundle;
                resultEl.innerHTML = this.#renderBundlePlan(res, { stage: 'validate' });
                this.#wireBundleResultButtons(resultEl);
                const blocking = res?.valid === false || (res?.unsupported?.length || 0) > 0 || (res?.errors?.length || 0) > 0;
                applyBtn.disabled = !!blocking;
            } catch (err) {
                Logger.error('Bundle validate failed: ', err);
                let body = null;
                if (err.status === 400) {
                    try { body = await err.response.json(); } catch { /* no JSON body */ }
                }
                resultEl.innerHTML = body
                    ? this.#renderBundlePlan(body, { stage: 'validate' })
                    : this.#renderBundleError(`Validate failed: ${err.message || err}`);
                if (body) this.#wireBundleResultButtons(resultEl);
            }
        });
    }

    async #importBundle() {
        const sd = this.shadowRoot;
        const nsInput = sd.getElementById('ps-bundle-import-ns');
        const modeSelect = sd.getElementById('ps-bundle-import-mode');
        const resultEl = sd.getElementById('ps-bundle-plan-result');
        if (!this.#bundleParsed) { this.#showAlert('Validate the bundle first.'); return; }
        const namespace = nsInput.value.trim();
        const mode = modeSelect.value;
        if (!await this.#confirm(`Import into namespace "${namespace}" (${mode})? This mutates the sandbox.`)) return;

        // § 10.0: import success is 200, applied: true. A refused mutation is 422 — the only
        // error status this endpoint returns (400 for an unparseable payload can't happen here
        // since validate already parsed it).
        await this.#withLoading(async () => {
            try {
                const res = await this.fetch(
                    `${this.#baseUrl}/sandbox/bundles/import?namespace=${encodeURIComponent(namespace)}&mode=${encodeURIComponent(mode)}`,
                    { method: 'POST', payload: { bundle: this.#bundleParsed }, expectedStatuses: [422] },
                );
                resultEl.innerHTML = this.#renderBundlePlan(res, { stage: 'import' });
                this.#wireBundleResultButtons(resultEl);
                sd.getElementById('ps-bundle-apply-btn').disabled = true;
                this.#setStatus('Bundle imported.');
                await this.#fetchPipelines();
            } catch (err) {
                Logger.error('Bundle import failed: ', err);
                let body = null;
                if (err.status === 422) {
                    try { body = await err.response.json(); } catch { /* no JSON body */ }
                }
                resultEl.innerHTML = body
                    ? this.#renderBundlePlan(body, { stage: 'import' })
                    : this.#renderBundleError(`Import failed: ${err.message || err}`);
                if (body) this.#wireBundleResultButtons(resultEl);
            }
        });
    }

    #renderBundleError(message) {
        return `<div class="ps-bundle-error">${this.#esc(message)}</div>`;
    }

    // Renders the `elm.bundle.plan` envelope pinned in design doc § 10.0 — same shape for
    // validate and import, only `applied`/item `status` differ. Any other top-level key still
    // surfaces (name only) so nothing is silently dropped, and the full raw response is always
    // offered as "Copy for agent" per § 10.1.
    #renderBundlePlan(res, { stage }) {
        if (!res || typeof res !== 'object') return this.#renderBundleError('Empty response.');
        const full = JSON.stringify(res, null, 2);
        const known = new Set(['kind', 'bundle_id', 'feature', 'namespace', 'mode', 'applied', 'valid',
            'capabilities', 'items', 'conflicts', 'side_effects', 'unsupported', 'stripped', 'errors', 'warnings', 'rolled_back']);
        const sections = [];

        const table = (title, rows, renderRow) => {
            if (!rows || !rows.length) return '';
            return `<div class="ps-bundle-section">
                <div class="ps-bundle-section-title">${this.#esc(title)} (${rows.length})</div>
                <div class="ps-bundle-rows">${rows.map(renderRow).join('')}</div>
            </div>`;
        };

        if (res.valid === false || (res.errors || []).length) {
            sections.push(`<div class="ps-bundle-banner ps-bundle-banner-error">
                ${stage === 'import' ? 'Import refused' : 'Bundle invalid'} — whole bundle rejected, nothing was applied.
            </div>`);
        } else if (stage === 'import' && res.applied) {
            sections.push('<div class="ps-bundle-banner ps-bundle-banner-ok">Import applied.</div>');
        } else if (res.valid === true) {
            sections.push('<div class="ps-bundle-banner ps-bundle-banner-ok">Plan valid — review below, then Import.</div>');
        }
        if (res.bundle_id || res.feature) {
            sections.push(`<div class="ps-bundle-meta">
                ${res.bundle_id ? `bundle_id <code>${this.#esc(res.bundle_id)}</code>` : ''}
                ${res.feature ? `feature <code>${this.#esc(res.feature)}</code>` : ''}
                ${res.namespace ? `namespace <code>${this.#esc(res.namespace)}</code>` : ''}
                ${res.mode ? `mode <code>${this.#esc(res.mode)}</code>` : ''}
            </div>`);
        }

        // Every item carries a disposition (§ 3.3.1) — applied/verified/carried is what the
        // developer must never confuse, since "carried" never took effect in the sandbox.
        const dispositionLabel = { applied: 'live in sandbox', verified: 'confirmed, not created', carried: 'travelling with bundle — not active here' };
        sections.push(table('Items', res.items, r => `<div class="ps-bundle-row">
            <span class="ps-bundle-row-type">${this.#esc(r.type || '')}</span>
            <code>${this.#esc(r.logical_name || r.ref || '')}</code>
            ${r.realized_name ? `→ <code>${this.#esc(r.realized_name)}</code>` : ''}
            <span class="ps-bundle-disposition-chip ps-bundle-disposition-${this.#esc(r.disposition || '')}">${this.#esc(dispositionLabel[r.disposition] || r.disposition || '')}</span>
            ${r.status ? `<span class="ps-bundle-row-type">${this.#esc(r.status)}</span>` : ''}
            ${(r.notes || []).length ? `<div class="ps-bundle-row-reason">${r.notes.map(n => this.#esc(n)).join('; ')}</div>` : ''}
        </div>`));

        sections.push(table('Conflicts', res.conflicts, r => `<div class="ps-bundle-row ps-bundle-row-warn">
            <code>${this.#esc(r.ref || '')}</code> already exists as <code>${this.#esc(r.existing || '')}</code>
            → <strong>${this.#esc(r.resolution || '')}</strong>
        </div>`));

        sections.push(table('Side effects', res.side_effects, r => `<div class="ps-bundle-row ps-bundle-row-warn">
            <span class="ps-bundle-row-type">${this.#esc(r.type || '')}</span>
            ${(r.pipelines || []).map(p => `<code>${this.#esc(p)}</code>`).join(' ')}
        </div>`));

        sections.push(table('Unsupported — bundle refused whole', res.unsupported, r => `<div class="ps-bundle-row ps-bundle-row-error">
            <code>${this.#esc(r.ref || '')}</code>
            <span class="ps-bundle-row-code">${this.#esc(r.code || '')}</span>
            <div class="ps-bundle-row-reason">${this.#esc(r.reason || '')}</div>
        </div>`));

        sections.push(table('Stripped on import — export-only, discarded rather than dropped silently', res.stripped, r => `<div class="ps-bundle-row">
            <span class="ps-bundle-row-type">${this.#esc(r.field || '')}</span>
            <div class="ps-bundle-row-reason">${this.#esc(r.reason || '')}</div>
        </div>`));

        const drift = res.capabilities?.drift;
        sections.push(table('Drift since bundle was authored', drift, r => `<div class="ps-bundle-row ps-bundle-row-warn">
            <code>${this.#esc(r.ref || '')}</code>
            ${r.blocking ? '<span class="ps-bundle-row-code">blocking</span>' : ''}
            <div class="ps-bundle-row-reason">${this.#esc(r.change || '')}</div>
        </div>`));

        sections.push(table('Errors', res.errors, e => {
            if (typeof e === 'string') return `<div class="ps-bundle-row ps-bundle-row-error">${this.#esc(e)}</div>`;
            return `<div class="ps-bundle-row ps-bundle-row-error">
                <span class="ps-bundle-row-code">${this.#esc(e.code || '')}</span>
                ${e.path ? `<code>${this.#esc(e.path)}</code>` : ''}
                <div class="ps-bundle-row-reason">${this.#esc(e.message || '')}${e.hint ? ` — ${this.#esc(e.hint)}` : ''}</div>
            </div>`;
        }));
        sections.push(table('Warnings', res.warnings, w => `<div class="ps-bundle-row ps-bundle-row-warn">${this.#esc(typeof w === 'string' ? w : JSON.stringify(w))}</div>`));

        sections.push(table('Rolled back', res.rolled_back, r => `<div class="ps-bundle-row ps-bundle-row-warn">
            <code>${this.#esc(r.ref || '')}</code>
            <span class="ps-bundle-row-type">${this.#esc(r.compensating_action || '')}</span>
            <span class="ps-bundle-row-type">${r.ok ? 'ok' : 'failed'}</span>
        </div>`));

        const extraKeys = Object.keys(res).filter(k => !known.has(k));
        const extraHtml = extraKeys.length
            ? `<div class="ps-bundle-section"><div class="ps-bundle-section-title">Other fields: ${extraKeys.map(k => this.#esc(k)).join(', ')}</div></div>`
            : '';

        return `${sections.filter(Boolean).join('')}${extraHtml}
            <div class="ps-bundle-raw-row">
                <button class="btn btn-sm ps-bundle-copy-btn" data-label="Copy for agent" data-content="${this.#esc(full)}">Copy for agent</button>
            </div>
            <details class="ps-bundle-raw-details"><summary>Raw JSON</summary><pre class="ps-bundle-raw">${this.#esc(full)}</pre></details>`;
    }

    async #loadExportItems() {
        const ns = this.#getEffectiveNamespace();
        const sd = this.shadowRoot;
        const container = sd.getElementById('ps-bundle-export-items');
        const featureSelect = sd.getElementById('ps-bundle-export-feature');
        if (!ns) {
            container.innerHTML = '<div class="ps-empty-state">Pick a namespace from the header selector first.</div>';
            return;
        }

        await this.#withLoading(async () => {
            const [pipelinesRes, sandboxRes, featuresRes] = await Promise.all([
                this.fetch(`${this.#baseUrl}/pipelines`).catch(() => ({ pipelines: [] })),
                this.fetch(`${this.#baseUrl}/schemas/_sandbox?namespace=${encodeURIComponent(ns)}`).catch(() => ({ candidates: [] })),
                // § 10.0: { features: [{feature, counts: {type: n}, bundle_ids[], updated_at}], unassigned: {counts} }.
                // Item-level `feature` stamping on the pipeline/schema listings is additive —
                // falls back to "unassigned" until those responses carry it.
                this.fetch(`${this.#baseUrl}/sandbox/features?namespace=${encodeURIComponent(ns)}`).catch(() => null),
            ]);

            const pipelineItems = (pipelinesRes?.pipelines || [])
                .filter(p => p.is_sandbox && p.namespace === ns)
                .map(p => ({ type: 'pipeline', name: p.name, feature: p.feature || null }));
            const schemaItems = (sandboxRes?.candidates || [])
                .map(c => ({ type: 'schema', name: `${c.domain}/${c.message_type}`, feature: c.feature || null }));

            this.#bundleExportItems = [...pipelineItems, ...schemaItems];
            this.#bundleExportSelected = new Set(this.#bundleExportItems.map(i => `${i.type}:${i.name}`));

            const sumCounts = counts => Object.values(counts || {}).reduce((a, b) => a + b, 0);
            const counts = new Map();
            for (const i of this.#bundleExportItems) {
                const label = i.feature || 'unassigned';
                counts.set(label, (counts.get(label) || 0) + 1);
            }
            for (const f of featuresRes?.features || []) counts.set(f.feature, sumCounts(f.counts));
            if (featuresRes?.unassigned) counts.set('unassigned', sumCounts(featuresRes.unassigned.counts));
            this.#bundleExportFeatures = Array.from(counts.entries()).map(([feature, count]) => ({ feature, count }));

            featureSelect.innerHTML = `<option value="__all__">All items (${this.#bundleExportItems.length})</option>`
                + this.#bundleExportFeatures.map(f => `<option value="${this.#esc(f.feature)}">${this.#esc(f.feature)} (${f.count})</option>`).join('');
            this.#renderExportItems();
        });
    }

    #renderExportItems() {
        const sd = this.shadowRoot;
        const container = sd.getElementById('ps-bundle-export-items');
        const chosenFeature = sd.getElementById('ps-bundle-export-feature')?.value || '__all__';
        const items = this.#bundleExportItems.filter(i => chosenFeature === '__all__' || (i.feature || 'unassigned') === chosenFeature);

        if (!items.length) {
            container.innerHTML = '<div class="ps-empty-state">No sandbox artifacts match this filter.</div>';
            return;
        }
        container.innerHTML = items.map(i => {
            const key = `${i.type}:${i.name}`;
            const checked = this.#bundleExportSelected.has(key);
            return `<label class="ps-bundle-export-item-row">
                <input type="checkbox" class="ps-bundle-export-cb" data-key="${this.#esc(key)}" ${checked ? 'checked' : ''} />
                <span class="ps-bundle-row-type">${this.#esc(i.type)}</span>
                <code>${this.#esc(i.name)}</code>
                <span class="ps-bundle-feature-chip">${this.#esc(i.feature || 'unassigned')}</span>
            </label>`;
        }).join('');
        for (const cb of container.querySelectorAll('.ps-bundle-export-cb')) {
            cb.addEventListener('change', () => {
                if (cb.checked) this.#bundleExportSelected.add(cb.dataset.key);
                else this.#bundleExportSelected.delete(cb.dataset.key);
            });
        }
    }

    // `feature` is grouping metadata, not entity content, so setting it is not the in-place
    // mutation the architecture otherwise forbids (§ 10.0) — safe to call at any time.
    async #assignFeatureLabel() {
        const sd = this.shadowRoot;
        const ns = this.#getEffectiveNamespace();
        const input = sd.getElementById('ps-bundle-assign-feature-input');
        const feature = input.value.trim() || null;
        const items = Array.from(this.#bundleExportSelected).map(key => {
            const [type, ...rest] = key.split(':');
            return { type, name: rest.join(':') };
        });
        if (!items.length) { this.#showAlert('Check at least one item first.'); return; }

        await this.#withLoading(async () => {
            try {
                const res = await this.fetch(`${this.#baseUrl}/sandbox/features/assign`, {
                    method: 'POST',
                    payload: { namespace: ns, feature, items },
                });
                this.#setStatus(`Assigned feature label to ${res?.updated ?? items.length} item(s).`);
                input.value = '';
                await this.#loadExportItems();
            } catch (err) {
                Logger.error('Feature label assign failed: ', err);
                this.#showAlert(`Assign failed: ${err.message || err}`);
            }
        });
    }

    async #buildExport() {
        const sd = this.shadowRoot;
        const ns = this.#getEffectiveNamespace();
        const policy = sd.getElementById('ps-bundle-export-policy').value;
        const resultEl = sd.getElementById('ps-bundle-export-result');
        const items = Array.from(this.#bundleExportSelected).map(key => {
            const [type, ...rest] = key.split(':');
            return { type, name: rest.join(':') };
        });
        if (!items.length) { this.#showAlert('Select at least one item to export.'); return; }

        // `items` overrides `feature`/`bundle_id` selectors when present (§ 10.0) — sent anyway
        // as informational context when a specific feature filter is active.
        const payload = { namespace: ns, policy, items };
        const featureFilter = sd.getElementById('ps-bundle-export-feature')?.value;
        if (featureFilter && featureFilter !== '__all__' && featureFilter !== 'unassigned') payload.feature = featureFilter;

        await this.#withLoading(async () => {
            try {
                const res = await this.fetch(`${this.#baseUrl}/sandbox/bundles/export`, {
                    method: 'POST',
                    payload,
                    expectedStatuses: [422],
                });
                resultEl.innerHTML = this.#renderExportResult(res, { refused: false });
                this.#wireBundleResultButtons(resultEl);
            } catch (err) {
                Logger.error('Bundle export failed: ', err);
                let body = null;
                if (err.status === 422) {
                    try { body = await err.response.json(); } catch { /* no JSON body */ }
                }
                resultEl.innerHTML = body
                    ? this.#renderExportResult(body, { refused: true })
                    : this.#renderBundleError(`Export failed: ${err.message || err}`);
                if (body) this.#wireBundleResultButtons(resultEl);
            }
        });
    }

    // `candidate` refusal is a workflow step, not an error state (§ 4) — name it plainly and
    // point at the fix (run tests, or switch to `draft`), never just a red banner.
    #renderExportResult(res, { refused }) {
        if (!res || typeof res !== 'object') return this.#renderBundleError('Empty response.');
        const full = JSON.stringify(res, null, 2);

        if (refused) {
            const reasonLabel = {
                never_exercised: 'never run',
                assertions_failed: 'assertions failing',
                evidence_stale: 'evidence stale — artifact changed since the last passing run',
            };
            const rows = (res.blocking || []).map(b => `<div class="ps-bundle-row ps-bundle-row-error">
                <span class="ps-bundle-row-type">${this.#esc(b.type || '')}</span>
                <code>${this.#esc(b.name || '')}</code>
                <span class="ps-bundle-row-code">${this.#esc(reasonLabel[b.reason] || b.reason || '')}</span>
                ${b.detail ? `<div class="ps-bundle-row-reason">${this.#esc(b.detail)}</div>` : ''}
                ${b.type === 'pipeline' ? `<button class="btn btn-sm ps-bundle-run-test-btn" data-pipeline="${this.#esc(b.name)}">Run tests…</button>` : ''}
            </div>`).join('');
            return `<div class="ps-bundle-banner ps-bundle-banner-error">
                    Export refused (<code>${this.#esc(res.code || 'EVIDENCE_REQUIRED')}</code>) — run the tests below,
                    or switch to <code>draft</code> if this is deliberately unfinished work.
                </div>
                <div class="ps-bundle-rows">${rows}</div>
                <div class="ps-bundle-raw-row">
                    <button class="btn btn-sm ps-bundle-copy-btn" data-label="Copy for agent" data-content="${this.#esc(full)}">Copy for agent</button>
                </div>
                <details class="ps-bundle-raw-details" open><summary>Raw JSON</summary><pre class="ps-bundle-raw">${this.#esc(full)}</pre></details>`;
        }

        const bundle = res.bundle || {};
        const bundleText = JSON.stringify(bundle, null, 2);
        const filename = `${bundle.feature || bundle.bundle_id || 'export'}.feature.json`;
        const driftHtml = res.drift && Object.keys(res.drift).length
            ? `<div class="ps-bundle-section">
                <div class="ps-bundle-section-title">Drift vs. imported bundle</div>
                <pre class="ps-bundle-raw">${this.#esc(JSON.stringify(res.drift, null, 2))}</pre>
            </div>` : '';
        const warningsHtml = (res.warnings || []).length
            ? `<div class="ps-bundle-section">
                <div class="ps-bundle-section-title">Warnings (${res.warnings.length})</div>
                <div class="ps-bundle-rows">${res.warnings.map(w => `<div class="ps-bundle-row ps-bundle-row-warn">${this.#esc(typeof w === 'string' ? w : JSON.stringify(w))}</div>`).join('')}</div>
            </div>` : '';

        return `<div class="ps-bundle-banner ps-bundle-banner-ok">Export built.</div>
            ${driftHtml}${warningsHtml}
            <div class="ps-bundle-raw-row">
                <button class="btn btn-sm ps-bundle-copy-btn" data-label="Copy" data-content="${this.#esc(bundleText)}">Copy</button>
                <button class="btn btn-sm ps-bundle-download-btn" data-filename="${this.#esc(filename)}" data-content="${this.#esc(bundleText)}">Download</button>
            </div>
            <div class="ps-bundle-raw-row">
                <button class="btn btn-sm ps-bundle-copy-btn" data-label="Copy for agent" data-content="${this.#esc(full)}">Copy for agent</button>
            </div>
            <details class="ps-bundle-raw-details"><summary>Raw JSON (full response)</summary><pre class="ps-bundle-raw">${this.#esc(full)}</pre></details>`;
    }

    #wireBundleResultButtons(container) {
        for (const btn of container.querySelectorAll('.ps-bundle-copy-btn')) {
            btn.addEventListener('click', () => {
                const text = btn.dataset.content;
                if (!text) return;
                navigator.clipboard.writeText(text).then(() => {
                    const label = btn.dataset.label || 'Copy';
                    btn.textContent = 'Copied ✓';
                    setTimeout(() => { btn.textContent = label; }, 1500);
                }).catch(err => Logger.warn('clipboard write failed: ', err));
            });
        }
        for (const btn of container.querySelectorAll('.ps-bundle-download-btn')) {
            btn.addEventListener('click', () => {
                const blob = new Blob([btn.dataset.content || ''], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = btn.dataset.filename || 'bundle.feature.json';
                a.click();
                URL.revokeObjectURL(url);
            });
        }
        for (const btn of container.querySelectorAll('.ps-bundle-run-test-btn')) {
            btn.addEventListener('click', () => this.#openPipelineDetail(btn.dataset.pipeline, 'test'));
        }
    }

    // ==== Clone-with-downstream (step 8) ======================================

    async #openCloneModal(sourceName, namespace) {
        // Look up downstream candidates via topology (production graph — clones happen from prod).
        let graph = null;
        try {
            graph = await this.fetch(`${this.#baseUrl}/pipelines/topology`);
        } catch (err) {
            Logger.debug('topology unavailable during clone: ', err);
        }
        const descendants = graph ? this.#topologyDescendants(graph, sourceName) : [];

        const suffix = namespace ? `-${namespace.replace(/[^A-Za-z0-9._-]+/g, '_')}` : '-sandbox';
        const overlay = document.createElement('div');
        overlay.className = 'ps-confirm-overlay';
        overlay.innerHTML = `<div class="ps-confirm-dialog" style="max-width:640px; width:640px">
            <h3 style="margin-top:0">Clone pipeline</h3>
            <div class="ps-form-hint">Namespace: <code>${this.#esc(namespace)}</code></div>
            <div class="ps-form-row">
                <label>Source</label>
                <div class="ps-form-hint" style="margin-left:8px"><code>${this.#esc(sourceName)}</code></div>
            </div>
            <div class="ps-form-row">
                <label for="ps-clone-name">Clone name</label>
                <input type="text" id="ps-clone-name" class="ps-input ps-input-sm" value="${this.#esc(sourceName + suffix)}" />
            </div>
            ${descendants.length ? `<div style="margin-top:12px">
                <div class="ps-form-hint" style="margin-bottom:6px">Also clone downstream pipelines into the same namespace? Selected pipelines are stopped in production and started in your sandbox.</div>
                <div class="ps-clone-downstream-list">
                    ${descendants.map(name => `<label class="ps-clone-downstream-row">
                        <input type="checkbox" class="ps-clone-downstream-cb" data-name="${this.#esc(name)}" />
                        <code>${this.#esc(name)}</code> →
                        <input type="text" class="ps-input ps-input-sm ps-clone-downstream-name" value="${this.#esc(name + suffix)}" data-source="${this.#esc(name)}" />
                    </label>`).join('')}
                </div>
            </div>` : ''}
            <div class="ps-confirm-actions" style="margin-top:12px">
                <button class="btn" id="ps-clone-cancel">Cancel</button>
                <button class="btn btn-primary" id="ps-clone-ok">Clone</button>
            </div>
        </div>`;
        this.shadowRoot.appendChild(overlay);
        overlay.querySelector('#ps-clone-cancel').onclick = () => overlay.remove();
        overlay.querySelector('#ps-clone-ok').onclick = async () => {
            const cloneName = overlay.querySelector('#ps-clone-name').value.trim();
            if (!cloneName) return;
            const downstream = [];
            for (const cb of overlay.querySelectorAll('.ps-clone-downstream-cb')) {
                if (!cb.checked) continue;
                const nameInput = overlay.querySelector(`.ps-clone-downstream-name[data-source="${CSS.escape(cb.dataset.name)}"]`);
                downstream.push({ source: cb.dataset.name, clone: nameInput?.value?.trim() || '' });
            }
            overlay.remove();
            await this.#executeCloneChain(sourceName, cloneName, namespace, downstream);
        };
    }

    #topologyDescendants(graph, sourceName) {
        const outgoing = new Map();
        for (const e of graph.edges || []) {
            if (!outgoing.has(e.from)) outgoing.set(e.from, []);
            outgoing.get(e.from).push(e.to);
        }
        const seen = new Set();
        const stack = [...(outgoing.get(sourceName) || [])];
        while (stack.length) {
            const n = stack.pop();
            if (seen.has(n)) continue;
            seen.add(n);
            for (const c of outgoing.get(n) || []) stack.push(c);
        }
        return Array.from(seen);
    }

    async #executeCloneChain(sourceName, cloneName, namespace, downstream) {
        await this.#withLoading(async () => {
            const failures = [];
            const doClone = async (src, dst) => {
                try {
                    await this.fetch(`${this.#baseUrl}/pipelines/${encodeURIComponent(src)}/clone`, {
                        method: 'POST',
                        payload: { clone_name: dst, namespace },
                        expectedStatuses: [409],
                    });
                } catch (err) {
                    Logger.error(`Clone ${src} → ${dst} failed: `, err);
                    const body = await this.#errorBody(err);
                    failures.push(body?.code === 'PIPELINE_INCOMPLETE'
                        ? `${src}: incomplete — its artifacts did not restore, so it cannot be cloned`
                        : `${src}: ${err.message || err}`);
                }
            };
            await doClone(sourceName, cloneName);
            for (const d of downstream) {
                if (!d.clone) continue;
                await doClone(d.source, d.clone);
            }
            await this.#fetchPipelines();
            if (failures.length) {
                this.#showAlert(`Some clones failed:\n${failures.join('\n')}`);
            } else {
                this.#setStatus(`Cloned ${1 + downstream.length} pipeline${downstream.length ? 's' : ''} into ${namespace}`);
            }
        });
    }
}

customElements.define('hs-pipeline-sandbox', HSPipelineSandbox);
